export type Block =
  | { type: "p"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "quote"; text: string; attribution?: string }
  | { type: "img"; src: string; alt: string; caption?: string; size?: "small" | "wide"; width?: number; height?: number; zoomHref?: string }
  | { type: "img-pair"; images: { src: string; alt: string; caption?: string }[] }
  | { type: "specs"; groups: { title: string; items: [string, string][] }[] }
  | { type: "placeholder"; label: string }
  | { type: "rich"; parts: (string | { text: string; href: string })[] }
  | { type: "release-timeline" }
  | { type: "release-highlights" };

export interface CaseStudySection {
  label: string;
  heading?: string;
  blocks: Block[];
  bg?: string;
  /** Anchor id, so the section can be linked to directly. */
  id?: string;
  /** Blocks span the full content width under the label and heading (for wide figures like a timeline). */
  wide?: boolean;
}

export interface CaseStudy {
  slug: string;
  title: string;
  subtitle: string;
  client: string;
  industry: string;
  tags: string[];
  /** Optional fine-grained keywords for cross-link matching (see refresh-related workflow). */
  topics?: string[];
  summary: string;
  featuredImage?: { src: string; alt: string; position?: string };
  featuredImagePair?: {
    before: { src: string; alt: string };
    after: { src: string; alt: string };
  };
  sections: CaseStudySection[];
  results: string[];
  seoTitle?: string;
  seoDescription?: string;
  ogImage?: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
  closingImage?: { src: string; alt: string };
  pressCitation?: { label: string; href: string };
  resultsHeading?: string;
  resultsLabel?: string;
  resultsNote?: string;
  /** Optional technical-notes sections rendered after the numbers band, anchored at #technical-notes. */
  appendix?: CaseStudySection[];
  appendixHeading?: string; // collapsed band title above the technical notes
  appendixBlurb?: string; // one line under it
}

export const caseStudiesData: CaseStudy[] = [
  {
    slug: "putting-past-work-to-work",
    title: "Putting Past Work to Work",
    subtitle: "Turning years of client experience into shared knowledge our Team and AI tools can use.",
    client: "Macktez (internal)",
    industry: "Technology & Software",
    tags: [
      "AI",
      "Cloud & Infrastructure"
    ],
    topics: [
      "ai-agents",
      "knowledge-management",
      "institutional-knowledge",
      "claude",
      "jev",
      "document-verification",
      "helpdesk"
    ],
    summary: "How Macktez turned years of scattered client history, in tickets, documents, time entries and conversations, into checked references its Team and support agent start from.",
    seoTitle: "Putting Past Work to Work: Turning Client History Into Checked Knowledge | Macktez",
    seoDescription: "How Macktez turned years of scattered client history, in tickets, documents, time entries and conversations, into checked references its Team and support agent start from.",
    canonicalPath: "/case-studies/putting-past-work-to-work",
    featuredImage: {
      src: "/assets/putting-past-work-to-work-hero-v2.png",
      position: "top",
      alt: "The full harvest diagram: one row per source, from what we started with through what code kept and what Jev judged to what Opus read, a panel of Jev in numbers at the right, and the five Opus stages below."
    },
    sections: [
      {
        label: "Overview",
        blocks: [
          {
            type: "p",
            text: "Every client relationship teaches us something. We learn how an organization works, why its systems are configured a particular way, which problems recur, and what has helped before. Much of that experience is recorded somewhere: in tickets, project documents, time entries, and conversations. Bringing it together usually falls to whoever handles the next problem."
          },
          {
            type: "rich",
            parts: [
              "We built a process to do that work systematically. It gathers relevant records, connects related information, and produces client references checked against their sources. Our support agent ",
              {
                text: "reads those references before investigating a ticket",
                href: "/case-studies/ai-assisted-ticket-flow"
              },
              ", and Team Members can inspect, correct, and add to them."
            ]
          },
          {
            type: "p",
            text: "The goal is to shorten the path to resolving a problem and make the experience gained along the way useful again. Work completed for a client should give the next person a better place to start."
          }
        ]
      },
      {
        label: "The Team",
        heading: "Experience the whole Team can use",
        blocks: [
          {
            type: "p",
            text: "A technician who has worked with a client for years brings context that takes time to acquire. They know which unusual arrangements are deliberate, what has already been tried, and which questions remain unresolved. That understanding shapes where they look and what they recommend."
          },
          {
            type: "p",
            text: "Making more of that context available helps colleagues take on unfamiliar work. It also gives experienced Team Members a way to contribute beyond the tickets they handle themselves. A useful explanation or correction can inform later investigations, instead of depending on someone remembering to ask the same person again."
          },
          {
            type: "p",
            text: "The references are designed to support that exchange. They bring relevant history together in a form people can review and our AI tools can use. Experienced colleagues continue to supply the judgment and context that the records do not contain."
          }
        ]
      },
      {
        label: "The Harvest",
        heading: "Bringing the records together",
        blocks: [
          {
            type: "p",
            text: "We began with a year of helpdesk tickets, documents, time entries, approved recommendations, and Team conversations. From roughly 226,000 items, we produced 174 reference documents for 46 clients."
          },
          {
            type: "p",
            text: "The useful information was often spread across several sources. A ticket might describe the problem, a time entry record the work, and a later conversation explain what finally resolved it. Reading those records together makes it possible to preserve the lesson."
          },
          {
            type: "p",
            text: "Different parts of that work call for different tools. Scripts remove obvious clutter and duplicates. A small model helps identify relevant material. A more capable model brings the selected evidence together and drafts the references. This keeps the process practical to repeat while reserving the more demanding work for the tool suited to it."
          },
          {
            type: "img",
            src: "/assets/putting-past-work-to-work-simple-v5.png",
            size: "wide",
            width: 3120,
            height: 798,
            alt: "Four boxes in a row: past records, select useful evidence, build and check references, use them in future work. Scripts clear the clutter and a small model picks what would teach the next person. A return arrow shows the weekly update adding what changed, from new tickets, Slack, calendars, and Zoom calls.",
            caption: "From past records to references the next investigation starts from."
          },
          {
            type: "rich",
            parts: [
              "Once assembled, the same client history can support many investigations. ",
              {
                text: "See the technical design",
                href: "#technical-notes"
              },
              " for the source-by-source numbers and what the work cost."
            ]
          }
        ]
      },
      {
        label: "Checking",
        heading: "Keeping the evidence attached",
        blocks: [
          {
            type: "p",
            text: "A shared reference needs to be open to scrutiny. Each factual statement carries evidence from its source, and a separate review checks whether that evidence supports what was written. Unsupported details are corrected or removed."
          },
          {
            type: "p",
            text: "One correction came up again and again: a fix that had worked was recorded as if it explained the problem. Restarting a device can restore service without telling you why it failed. The next technician needs to know both things: what worked last time, and that the cause is still open."
          },
          {
            type: "p",
            text: "We preserve that distinction throughout the references. Documented facts, previous remedies, and working theories each have a place, so a useful possibility does not quietly become an accepted explanation."
          }
        ]
      },
      {
        label: "Harvest to Habit",
        heading: "Making knowledge part of the work",
        blocks: [
          {
            type: "p",
            text: "The initial harvest establishes a starting point. Keeping it useful depends on what happens afterward."
          },
          {
            type: "p",
            text: "Investigations now read the client’s reference and record new findings as they work. Team Members can correct the record, with their contributions dated and attributed. That gives knowledge a regular path from an individual investigation into something later work can use."
          },
          {
            type: "p",
            text: "A weekly update process gathers recent activity and proposes changes to the references. Those changes are checked against their evidence before being incorporated, which makes maintaining this knowledge part of normal operations rather than a project."
          },
          {
            type: "p",
            text: "The references are in daily use. Investigations record what they find, Team Members correct what they know, and the weekly update folds in what changed."
          }
        ]
      },
      {
        label: "For Clients",
        heading: "Putting your own experience to work",
        blocks: [
          {
            type: "p",
            text: "Many organizations have a similar opportunity. Years of completed work contain decisions, exceptions, and lessons that could help the next project or request. Making those records useful can give people more room to apply their expertise."
          },
          {
            type: "p",
            text: "Some knowledge can be recovered from existing material. Other knowledge still needs to come from the people who understand the business. A useful system gives them a place to contribute it and a reason to keep improving it: their experience becomes available to more of the work."
          },
          {
            type: "p",
            text: "The same approach can begin with one part of your business: a recurring request, a project handoff, or a body of client knowledge. We help identify the useful records, establish how they should be checked, and put them where the next task can benefit."
          }
        ]
      }
    ],
    resultsLabel: "Three Facts",
    resultsHeading: "The short version.",
    resultsNote: "Every figure on this page comes from our own logs and records, except where it is marked as an estimate.",
    results: [
      "About 226,000 records became 174 reference documents for 46 clients",
      "15,745 lines checked against their sources; 2,405 corrected or removed",
      "Facts, past remedies and working theories kept apart, each traced to its source"
    ],
    appendixHeading: "How the harvest was built",
    appendixBlurb: "The source-by-source numbers, how each small-model judgement was tested, the four writing passes, what the checks corrected, and the full diagram.",
    appendix: [
      {
        label: "Technical Notes",
        heading: "How the harvest was built",
        blocks: [
          {
            type: "p",
            text: "For readers who want the detail behind the story above. Every figure here comes from our own logs and records, except where it is marked as an estimate."
          },
          {
            type: "img",
            src: "/assets/putting-past-work-to-work-diagram-v3.png",
            zoomHref: "/assets/putting-past-work-to-work-diagram-v3.svg",
            size: "wide",
            width: 3600,
            height: 2166,
            alt: "The harvest in full: one row per source showing what we started with, what the scripts kept, which small-model pass judged it and how that pass was checked, and what Claude read; a panel of the small model in numbers; the five Claude stages; and two findings on model choice and context size.",
            caption: "The full harvest, source by source."
          },
          {
            type: "p",
            text: "The harvest ran as a sequence of narrowing steps. Scripts first removed what could never teach anyone anything: pictures, fonts, installers, automated alerts and duplicates. That left about 63,000 documents, 4,000 resolved tickets, 6,500 time entries, 101 approved recommendations and 457 conversations across 191 client folders. Jev, a small model from TypeSafe that answers narrow questions with a confidence score, then read each remaining item and answered a few questions about it: is this document useful, would this ticket teach the next person something, which ticket does this time entry belong to, which client is this conversation about. Its answers narrowed the set to 5,092 items for the 46 clients in our two largest service classes."
          },
          {
            type: "table",
            head: [
              "Source",
              "We started with",
              "Claude read"
            ],
            rows: [
              [
                "Helpdesk tickets",
                "57,094 from a year",
                "1,269"
              ],
              [
                "Drive files",
                "155,356 in client folders",
                "663"
              ],
              [
                "Time entries",
                "10,359",
                "2,883"
              ],
              [
                "Approved recommendations",
                "182",
                "101"
              ],
              [
                "Team chat",
                "457 conversations, 2,734 replies",
                "167 threads"
              ]
            ]
          },
          {
            type: "p",
            text: "Before any of Jev’s answers were allowed to decide anything, each kind of judgement was tested against examples with known answers. Time entries that already named their ticket showed whether it could link the ones that didn’t: right 76 of 80 times when the true ticket was among the candidates. Conversations that linked a ticket showed whether it could tell which client was being discussed: right 84 of 86 times. One score that looked useful, a rating of how likely a document was to mislead, turned out not to match Claude’s judgement and had been filtering out current network details, so it now only lowers a document’s rank."
          },
          {
            type: "p",
            text: "Why not have Claude read everything? The text Jev judged came to about 140 million tokens by our logs. Read once by Claude at list price, that is about $560, against about $6 billed for Jev (estimated). More to the point, Claude did this work as an agent, re-reading everything it held on every turn: the harvest took about 3.3 billion input tokens to read, write and check 5,092 items, so 226,000 was never a realistic number. Jev’s largest pass judged 63,478 documents in 13 minutes; Claude’s work on a fraction of the items took two days. The harvest ran inside the same one-month premium Claude plan that covered the ticket-flow build, and its own extra cost was about $6 of Jev."
          }
        ]
      },
      {
        label: "Writing and Checking",
        heading: "From evidence to checked references",
        blocks: [
          {
            type: "p",
            text: "Claude did the writing in four passes, each handled by many short-lived agents working on one bundle or one document at a time. Readers took a bundle of related items, a ticket with its time entries, the documents about it and the chat around it, and wrote down every fact with a verbatim quote and the ids of its sources: 8,607 facts in all. Writers took one client’s facts and produced one document per client and skill, 174 documents, the longest under 35,000 characters. Checkers then read every document against its quotes and against an evidence pack holding every source it cited: 15,745 lines, of which 2,351 were corrected and 54 removed. Finally an audit per client reconciled its documents with each other, fixing 192 contradictions and trimming 810 repeated passages to pointers."
          },
          {
            type: "p",
            text: "The corrections were mostly specifics a source did not support (902), statements a later source contradicted (391), the wrong person or device named (367), facts that had gone out of date (186), and fixes written up as proven causes (174). An earlier test, run before every fact had to carry its quote, found problems on 35% of the lines it checked, which is why the quote became a requirement."
          },
          {
            type: "p",
            text: "Checking is where the context window mattered. A checker holds the whole document, the facts it came from and every source it cites at once, because the errors it exists to catch are contradictions between them. 114 of the 245 checkers needed more than 200,000 tokens of context, so that step ran with Claude’s largest window. Checking was also where we tested a cheaper model, with the same three drafts checked by Claude Opus and by Claude Sonnet 5.5. Sonnet took more turns and about 1.6 times the tokens, flagged two to three times as many lines, and on the hardest document missed 12 of the 27 corrections Opus made. Both models charge the same for re-reading cached context, which is most of an agent’s usage, so Sonnet would have cost more here, not less. Opus’s corrections were the reference and the disputed lines were not independently judged, so this is a finding about this work, not a rule about the models."
          }
        ]
      },
      {
        label: "In Short",
        heading: "The harvest, in short",
        blocks: [
          {
            type: "p",
            text: "Scripts cleared about 226,000 records down to what could teach anyone anything. A small classifier screened the rest to 5,092 items across tickets, documents, time entries, recommendations, and chat, for about $6. Claude read those items in bundles, wrote down 8,607 facts with their quotes, produced 174 reference documents for 46 clients, checked 15,745 lines against their sources and corrected or removed 2,405, and reconciled each client’s documents with one another. A weekly update now folds in what changed, and Team Members correct what the records could never know."
          },
          {
            type: "rich",
            parts: [
              "Your organization’s history is sitting in the same kinds of places: a ticketing system, shared drives, time records, chat. We can run this process on your records, under your permissions, and leave you with references your people and your AI tools can use. ",
              { text: "Tell us where the knowledge lives today", href: "/contact" },
              ", or start with ",
              { text: "how we assess the work", href: "/services/ai-enablement" },
              "."
            ]
          }
        ]
      }
    ]
  },
  {
    slug: "ai-assisted-ticket-flow",
    title: "AI Assisted Ticket Flow",
    subtitle: "Translating the Team’s accumulated knowledge into better and faster outcomes.",
    client: "Macktez (internal)",
    industry: "Technology & Software",
    tags: [
      "AI",
      "Security",
      "Cloud & Infrastructure"
    ],
    topics: [
      "ai-agents",
      "helpdesk",
      "ticket-triage",
      "mcp",
      "claude",
      "jev",
      "automation",
      "human-in-the-loop",
      "freshservice"
    ],
    summary: "How Macktez prepares support investigations across network, support, service-request and security tickets, combining current evidence with what the Team already knows about each client, and keeps what it learns for next time.",
    seoTitle: "AI Assisted Ticket Flow: Translating the Team's Knowledge Into Better and Faster Outcomes | Macktez",
    seoDescription: "How Macktez prepares support investigations across network, support, service-request and security tickets, combining current evidence with what the Team already knows about each client, and keeps what it learns for next time.",
    canonicalPath: "/case-studies/ai-assisted-ticket-flow",
    featuredImage: {
      src: "/assets/ai-assisted-ticket-flow-hero-v3.png",
      position: "top",
      alt: "The top of the full AI Assisted Ticket Flow diagram: the support toolkit’s three plugins, where tickets come from, the client platforms, the Macktez MCP servers at the center, and the first stages where code and Jev sort tickets every 30 minutes before Claude is involved."
    },
    sections: [
      {
        label: "Overview",
        blocks: [
          {
            type: "p",
            text: "Getting a support ticket to resolution takes more than understanding the reported problem. A technician needs to know the client’s environment, what has happened before, what has already been tried, and which next steps make sense. Gathering that context takes time, even when the company already holds the answers."
          },
          {
            type: "p",
            text: "We built our AI-assisted ticket flow around two goals: shorten the path to completing a ticket, and bring our accumulated experience into every investigation. It prepares investigations across network issues, general support, service requests, and security, combining current evidence with what our Team already knows about the client. A Team Member reviews the findings, decides what to do, and works with the client."
          },
          {
            type: "p",
            text: "The knowledge has somewhere to go, too. Findings and corrections become part of a shared client record that later investigations can use."
          },
          {
            type: "quote",
            text: "Every investigation should benefit from what the company has already learned."
          }
        ]
      },
      {
        label: "Breadth and Depth",
        heading: "More kinds of work, with more context",
        blocks: [
          {
            type: "p",
            text: "We began with network investigations and expanded into support, service requests, and security, backed by a richer record of each client. More of the helpdesk tickets now arrive with useful preparation: a Wi-Fi complaint, a laptop that won’t sign in, a request to add someone to a shared mailbox, and a suspicious sign-in alert each get an investigation shaped for that kind of work."
          },
          {
            type: "p",
            text: "Each investigation brings together more than the ticket. It reads the current evidence from that client’s systems, the procedure our Team follows for that kind of request, the client’s known quirks, previous incidents at that site, and what earlier investigations found. A technician opens the ticket with the context gathered and a working theory on the table, instead of beginning from a blank page and a dozen logins. The report leads with that theory and the next step for a person to take, then shows its evidence."
          }
        ]
      },
      {
        label: "Knowledge",
        heading: "What we learn becomes available next time",
        blocks: [
          {
            type: "p",
            text: "The relationship runs in a loop. Investigations use what the company already knows about a client. The work produces new findings. People correct and extend them. Later investigations begin with that accumulated record."
          },
          {
            type: "rich",
            parts: [
              "Each client has a shared record, built first from a year of our own tickets, documents, time entries and conversations (that work has ",
              {
                text: "its own case study",
                href: "/case-studies/putting-past-work-to-work"
              },
              ") and added to by every investigation since. When a Team Member replies in the report’s thread or corrects a finding, the correction lands on that client’s record, credited and dated, and in how the next ticket of that kind is handled. A reply of “too shallow” tunes how deep the next investigation of that kind goes, and a request to skip a kind of ticket for a client becomes a routing rule the same day."
            ]
          },
          {
            type: "p",
            text: "Two kinds of knowledge are in play, and only one of them can be assembled from records. Processing a year of tickets and documents gathers what was written down. What a technician knows but never wrote down, the closet that runs hot, the contact who approves changes, the fix that only looks like a fix, still has to come from the Team, as corrections and additions to the record. The system gives technicians more room for judgement, client relationships and the difficult decisions, and in return it depends on their judgement to stay right."
          },
          {
            type: "quote",
            text: "The record gathers what was written down. The Team adds what it knows."
          }
        ]
      },
      {
        label: "Building on What Exists",
        heading: "Built on work we had already done",
        blocks: [
          {
            type: "rich",
            parts: [
              "Expanding from network tickets to the rest of the helpdesk took about a week, because much of what it needed already existed. The connections to every platform we manage, built first for our Team and then for ",
              {
                text: "support.agent",
                href: "/case-studies/support-agent"
              },
              ", are the same ones ",
              {
                text: "Macktez Monitor",
                href: "/case-studies/macktez-monitor"
              },
              " collects through. The network and security investigation steps were already written down as skills in our support toolkit, and our standard operating procedures were already in Drive. What we built new was the routing, the methodology for support and service-request tickets, and the client records that give every investigation what we already know."
            ]
          }
        ]
      },
      {
        label: "The Right Tool",
        heading: "The right tool for each step",
        blocks: [
          {
            type: "p",
            text: "Preparing an investigation for every ticket that needs one, and only those, has to be affordable and dependable, or it stops. So each judgement goes to the simplest tool that can make it reliably. Ordinary scripts handle the known rules. A small classifier model answers the narrow questions: what kind of ticket this is, whether a person wrote it, how urgent it is. Rules and the classifier determine which tickets need investigation. A more capable model gathers and interprets the evidence, read-only and on that client’s systems only, and a Team Member decides what happens next."
          },
          {
            type: "img",
            src: "/assets/ai-assisted-ticket-flow-simple-v2.png",
            size: "wide",
            width: 3120,
            height: 798,
            alt: "Five boxes in a row: a ticket arrives, known rules settle the routine, a small model classifies the rest, Claude investigates what needs it, and a Team Member decides. Labels mark the tickets handled by rules and the classifier and the few investigated by Claude, and a return arrow shows corrections becoming rules.",
            caption: "The flow in one line: a ticket arrives, rules and the classifier route it, Claude investigates what needs it, a Team Member decides, and corrections become rules."
          },
          {
            type: "rich",
            parts: [
              "Each step gets only the information and access it needs. ",
              {
                text: "See the technical design",
                href: "#technical-notes"
              },
              " for every step, what a report contains, the measurements behind it, and the full diagram."
            ]
          }
        ]
      },
      {
        label: "Measuring",
        heading: "What we measure",
        blocks: [
          {
            type: "p",
            text: "The goal is a shorter path to resolution and investigations the Team can depend on, so we measure both. We track whether tickets close sooner and how much work is repeated, against the weeks before the change. Before any change to the flow goes live, we replay a week of real tickets through it and compare the new answers with the old ones. And we test the client records the same way: the same tickets investigated with and without them, judged for errors, missed next steps, and how useful the technician found the starting point."
          }
        ]
      },
      {
        label: "For Clients",
        heading: "Where to start",
        blocks: [
          {
            type: "p",
            text: "Most organizations have spent years developing their people’s expertise, and most of it is reachable only by asking the person who has it. We start with the work your team already understands. Together, we decide what can be prepared automatically, what requires a person’s judgement, and what each completed task should contribute to the next."
          },
          {
            type: "rich",
            parts: [
              "That’s the work we do with clients: connecting AI to your systems through ",
              {
                text: "scoped connectors",
                href: "/services/mcp-tooling"
              },
              ", giving each agent ",
              {
                text: "only the access it needs",
                href: "/services/agent-governance"
              },
              ", and ",
              {
                text: "logging every action",
                href: "/services/audit-accountability"
              },
              " so it can be reviewed. ",
              {
                text: "Putting Past Work to Work",
                href: "/case-studies/putting-past-work-to-work"
              },
              " covers how we built the client records the investigations start from."
            ]
          }
        ]
      }
    ],
    results: [],
    appendixHeading: "The technical design",
    appendixBlurb: "Every step of the flow, what a report contains, where it started, the replayed-week measurements, and the full diagram.",
    appendix: [
      {
        label: "Technical Notes",
        heading: "The technical design",
        blocks: [
          {
            type: "p",
            text: "For readers who want the detail behind the story above: every step of the flow, where it started, and the measurements behind the numbers. Every figure here comes from our own logs and records, except where it is marked as an estimate."
          },
          {
            type: "img",
            src: "/assets/ai-assisted-ticket-flow-diagram-v3.png",
            zoomHref: "/assets/ai-assisted-ticket-flow-diagram-v3.svg",
            size: "wide",
            width: 3600,
            height: 4290,
            alt: "The AI Assisted Ticket Flow in full: the support toolkit’s three plugins, the ticket flow with every step numbered, the Macktez MCP servers as the hub, client platforms, where findings land, how the pipeline teaches itself, the weekly knowledge synthesis, what each tier costs, and a legend of every step.",
            caption: "The full ticket flow, every step numbered."
          },
          {
            type: "p",
            text: "The flow runs every 30 minutes, and most of what it does involves no model at all. Scripts collect new and changed tickets through our existing connections, pair each alert with its recovery, group a burst of alerts from one site into one item, and drop whatever the helpdesk’s own automations have already closed. Only what is left goes to the classifier. Jev reads each remaining ticket once and answers several questions at the same time, each from a fixed list of answers: what kind of ticket this is, whether it is actionable, whether a person wrote it, how urgent it is, and whether it reports a compromise. Jev picks from answers we define in advance instead of writing text, so it can still pick wrong, but a narrow answer is easy to check and easy to route. That costs about 3,200 tokens and 160 milliseconds a ticket."
          },
          {
            type: "p",
            text: "A confident answer sends the ticket straight to its queue. An unsure one goes to Claude, which classifies it from the text alone, with no tools attached, for about 7,100 tokens a call. A possible compromise always goes to the security queue, whatever else Jev said. For each queued ticket, one Claude call then runs the matching investigation, read-only and on that client’s platforms only, starting from the client’s reference. An investigation runs between about one and 2.7 million tokens, median by skill, most of it re-reading context it already holds. Scripts trim the finished report to its budget, post it as a note on the ticket, and hand it to Slack for a Team Member."
          }
        ]
      },
      {
        label: "The Report",
        heading: "What a technician receives, and what comes back",
        blocks: [
          {
            type: "p",
            text: "Every report has the same shape, led by the decision and backed by the evidence: a summary with a stated confidence, recommended next steps for a person to take, the root-cause analysis, then the assumptions, the tools called, the evidence, the history at that site, and what remains unknown. A typical report on a slow-Wi-Fi complaint, simplified, reads like this:"
          },
          {
            type: "ul",
            items: [
              "Reported: Wi-Fi is slow in the second-floor conference room, since this morning.",
              "History: the same room was reported twice in the past year. Both times a neighboring network on the same channel was the cause, and a channel change resolved it.",
              "Network evidence: the room’s access point is up and busy, its channel is crowded by a network that appeared this week, and the switch port and uplink show no errors.",
              "Device evidence: through device management, the requester’s laptop shows a strong signal to the right access point on the 5 GHz band, current Wi-Fi drivers, and no VPN or bandwidth-heavy process running, so the laptop isn’t the cause.",
              "Confirmation: a short packet capture on the access point shows retransmissions and airtime lost to the neighboring network, which matches the theory.",
              "Suggested next step: move the access point to a quieter channel and confirm with the client. A draft note to the requester is attached for a Team Member to send."
            ]
          },
          {
            type: "p",
            text: "No single source would settle that ticket. The access point says the channel is crowded, the laptop says the client is healthy, and the capture shows the interference is real. Connecting every platform we manage is what lets one investigation read all three and arrive with a theory that has already been tested."
          },
          {
            type: "p",
            text: "The report is the start of the ticket, not the end of it. The technician still confirms the theory, decides, makes the change, and talks to the client. What comes back shapes the flow. When two client records said a connector could not read a client’s directory sign-in events, a Team Member knew it could; a check returned 1,031 events for one day, and the records now carry that correction, credited and dated. Repeated decisions become reusable rules, and continued checks catch any rule that stops working."
          }
        ]
      },
      {
        label: "Where It Started",
        heading: "The first agent, and what it cost",
        blocks: [
          {
            type: "p",
            text: "The first agent woke every 30 minutes during business hours, read the helpdesk, picked out network tickets by their subjects, and ran a full investigation with Claude. It read the client’s documentation, checked their network platforms, and wrote a note on the ticket and a summary in Slack. The notes were useful, and the Team came to rely on them."
          },
          {
            type: "p",
            text: "Then we measured it. In the week we studied, only 66 of 331 runs found a ticket to work on. The other 80% spent a full investigation’s worth of effort confirming an empty queue, because the cost was set by the clock rather than by the work. It also covered network tickets only. The current flow fixed both problems at once: scripts and the classifier decide whether there is anything to investigate, so a quiet day costs almost nothing, and the same machinery routes support, service-request and security tickets as readily as network ones."
          }
        ]
      },
      {
        label: "Measurements",
        heading: "What the replayed week showed",
        blocks: [
          {
            type: "p",
            text: "Before the new flow went live, we replayed a week of real tickets through it and compared its decisions with the old agent’s. The table shows where each ticket was settled."
          },
          {
            type: "table",
            head: [
              "Stage",
              "In",
              "Settled there",
              "Passed on"
            ],
            rows: [
              [
                "Monitoring rules",
                "95 alerts and backup repeats",
                "85",
                "10 network items"
              ],
              [
                "Helpdesk automations",
                "899 other tickets",
                "408 closed on arrival",
                "491"
              ],
              [
                "Jev",
                "491",
                "321 noise",
                "81 to investigation queues, 89 to Claude to classify"
              ],
              [
                "Claude",
                "179 queue items",
                "",
                "89 classification calls, 90 investigations"
              ]
            ]
          },
          {
            type: "p",
            text: "In that week, 994 tickets led to 179 calls to Claude. On classification, Jev’s answers agreed with Claude’s 99.3% of the time and dropped one real ticket as noise; the keyword rules it replaced agreed 98.1% of the time and dropped five. That figure is agreement with Claude rather than independently measured accuracy, and the same Claude labels guided our choice among Jev variants, so we treat it as evidence that the classifier makes the same calls, not proof that those calls are right. Urgency is handled accordingly. Jev’s escalation answer, with our rules on top, caught 11 of the 18 cases Claude marked urgent, so urgency is a flag that brings a ticket forward, not a gate that decides anything, and a person still judges it."
          },
          {
            type: "p",
            text: "Measuring also changed the design. When Claude classified a ticket with its usual tools and connectors attached, each call cost about 185,000 tokens. Giving it only the ticket text cut that to about 6,900 with the same answers. We would not have guessed that without measuring, and it is the kind of finding that makes a flow affordable enough to keep running."
          },
          {
            type: "p",
            text: "Model charges are not the whole cost, so it is worth being precise about them. Building the flow ran on one month of a premium Claude plan, about $125, plus about $6 of Jev. It now runs on the standard plan, about $25 a month paid to Anthropic plus a few cents of Jev, at the prices when we built it. None of that counts the Team’s time to design, build and supervise it, which is the larger investment and the one that made the rest work."
          }
        ]
      },
      {
        label: "In Short",
        heading: "The technical design, in short",
        blocks: [
          {
            type: "p",
            text: "Scripts collect and filter tickets through the same connections our Team uses every day. A small classifier answers the narrow routing questions for a fraction of a cent each. Claude investigates only the tickets that need judgement, read-only, on that client’s systems, starting from what we already know about the client. Every finding lands with a Team Member, and the Team’s corrections become rules and records the next ticket starts from. In the replayed week, 994 tickets led to 179 calls to Claude, the classifier agreed with Claude 99.3% of the time, and the flow now runs for about $25 a month in model charges."
          },
          {
            type: "rich",
            parts: [
              "If your organization has a queue like this, a steady stream of requests where a few need real judgement and the rest follow patterns your people already know, we can build the same thing on your systems: the connections, the routing, the knowledge, and the review. ",
              { text: "Tell us about the queue", href: "/contact" },
              ", or start with ",
              { text: "how we put AI to work", href: "/services/ai" },
              "."
            ]
          }
        ]
      }
    ]
  },
  {
    slug: "macktez-monitor",
    title: "Macktez Monitor: Building on Our AI Foundations",
    subtitle:
      "How we turned the connections behind our AI tools into a monitoring platform — and brought its history and insights back to those tools.",
    client: "Macktez (internal)",
    industry: "Technology & Software",
    tags: ["AI", "Identity", "Security", "Cloud & Infrastructure"],
    topics: ["monitoring", "mcp", "model-context-protocol", "observability", "network-topology", "backups", "automation", "read-only", "claude", "ai-agents", "reporting"],
    summary:
      "A growing monitoring platform that gives our team and AI tools shared access to system health, historical context, and insights that individual platforms cannot provide on their own.",
    seoTitle: "Macktez Monitor: Building on Our AI Foundations | Macktez",
    seoDescription:
      "How Macktez turned the connections behind its AI tools into a monitoring platform, and brought its history and insights back to those tools.",
    canonicalPath: "/case-studies/macktez-monitor",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Macktez Monitor: Building on Our AI Foundations",
      "description":
        "How Macktez turned the connections behind its AI tools into a monitoring platform, and brought its history and insights back to those tools.",
      "url": "https://macktez.com/case-studies/macktez-monitor",
      "author": { "@type": "Organization", "name": "Macktez", "url": "https://macktez.com" },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "In-house infrastructure monitoring built on Model Context Protocol servers",
      },
    },
    featuredImage: {
      src: "/assets/macktez-monitor-topology.png",
      position: "top",
      alt: "Monitor’s network view for one client office. A topology map shows the internet at the top, two carrier circuits marked active and standby, a pair of firewalls feeding a switch stack, and five wireless access points below it, each marked healthy. A side panel shows the switch’s ports as a grid colored by VLAN, with one port selected and the access point on it named.",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "Macktez (internal)"],
                  ["Problem", "No single dashboard showed everything we wanted to know. Vendor consoles each exposed part of the picture, with limited history. Understanding a client’s environment meant gathering information from several systems by hand."],
                  ["Approach", "Reuse our existing MCP connections to collect data on a recurring schedule, retain its history, and combine it into a more complete picture."],
                  ["Outcome", "A growing monitoring platform that gives our team and AI tools shared access to system health, historical context, and insights that individual platforms cannot provide on their own."],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Idea",
        heading: "Existing infrastructure, new capabilities",
        blocks: [
          {
            type: "rich",
            parts: [
              "We built our custom Model Context Protocol (MCP) connections so our team could use AI tools to work across the platforms we manage. That shared foundation subsequently enabled ",
              { text: "support.agent", href: "/case-studies/support-agent" },
              ", our autonomous digital employee.",
            ],
          },
          {
            type: "p",
            text:
              "It also created an opportunity. The connections could serve ordinary software as readily as a language model. We could use them to gather information continuously, without building another set of vendor integrations.",
          },
          {
            type: "p",
            text:
              "Using AI-assisted development, we built Macktez Monitor around that idea. A scheduled script queries our vendor platforms through MCP, while collectors inside client networks gather readings from local systems. Reports arrive in a central storage repository on AWS S3, where they trigger processing by the Monitor application.",
          },
          {
            type: "p",
            text:
              "Deterministic rules interpret the readings and store the results. No LLM is involved in collection or health assessment.",
          },
          {
            type: "img",
            src: "/assets/macktez-monitor-diagram-v2.png",
            zoomHref: "/assets/macktez-monitor-diagram-v2.svg",
            width: 4160,
            height: 5240,
            alt:
              "The Macktez Monitor systems diagram. In the upper left, the vendor platforms we read, each connected to the shared gateway at mcp.macktez.com at the center, with the access control beside it. On the right, the collection path: the roster and its safeguards, the fleet collector, and the report store on Amazon S3, with the two collectors inside a client network feeding the same store. Below, the Monitor application: readings received and normalized, judged by deterministic rules, written to a Postgres database, and read back by Team Members through the website and by Claude and support.agent through the Monitor interface. Along the bottom, the rules for network evidence, backups, servers and directories, and the six states.",
            caption:
              "Every box is a system that exists and every arrow is a real path between two of them, numbered in the order a reading travels: from the vendor platforms through the shared gateway to the collector, into the report store, through the rules into the database, and out to the people and assistants who read it.",
          },
        ],
      },
      {
        label: "What It Adds",
        heading: "A more complete view across systems and time",
        blocks: [
          {
            type: "p",
            text: "Bringing the data together makes new capabilities possible.",
          },
          {
            type: "p",
            text:
              "A live network topology map, for example, draws on information from firewalls, switches, access points, and device management platforms. Assembling that view previously required logging into several systems and reconciling their answers. Monitor now renders it automatically, showing connections across equipment from different vendors and making gaps in the available information visible.",
          },
          {
            type: "img",
            src: "/assets/macktez-monitor-topology.png",
            zoomHref: "/assets/macktez-monitor-topology.png",
            size: "wide",
            width: 2000,
            height: 1123,
            alt: "Monitor’s topology view for one office. The internet sits at the top, two carrier circuits are marked active and standby, a pair of firewalls feeds a switch stack, and the wireless access points hang below it. A side panel shows the switch’s ports as a grid colored by VLAN and outlined by link speed, with one port selected and the device on it named.",
            caption: "One site, mapped automatically by combining readings from firewalls, switches, access points, and network probes. The panel on the right shows individual switch ports and their connections.",
          },
          {
            type: "p",
            text:
              "History adds another dimension. We can see when a device disappeared, whether a condition is recurring, or how long it has been since a backup completed successfully. A successful reading also has an age: when fresh information stops arriving, Monitor shows that uncertainty instead of leaving yesterday’s reassurance on screen.",
          },
          {
            type: "p",
            text:
              "Together, those capabilities give our team a stronger starting point for troubleshooting and planning.",
          },
        ],
      },
      {
        label: "Closing the Loop",
        heading: "Better information feeds back into our AI tools",
        blocks: [
          {
            type: "p",
            text:
              "Monitor exposes its accumulated history and insights through its own MCP tools. Our team, support.agent, and our investigation workflows can use that context alongside live vendor readings.",
          },
          {
            type: "p",
            text:
              "An investigation can begin with what changed, what has persisted, and what else was happening at the site, then query individual systems for more detail.",
          },
          {
            type: "p",
            text:
              "That closes the loop: infrastructure built for AI enables a new operational tool, and that tool gives AI-assisted work better information.",
          },
        ],
      },
      {
        label: "Built to Last",
        heading: "Built to improve and expand",
        blocks: [
          {
            type: "p",
            text:
              "Ongoing performance work keeps Monitor lean as its coverage grows. We measure changes, reduce unnecessary processing and database writes, and refine how much detail we retain over time.",
          },
          {
            type: "p",
            text:
              "That discipline keeps operating costs low and creates room for more systems and more frequent readings. Each addition to our shared connectivity becomes another opportunity to extend the platform.",
          },
          {
            type: "p",
            text:
              "AI-assisted coding helps us act on those opportunities: developing features, improving existing workflows, and testing whether an idea delivers enough practical value to keep.",
          },
        ],
      },
      {
        label: "For Clients",
        heading: "Find new solutions to familiar challenges",
        blocks: [
          {
            type: "p",
            text:
              "Monitor began with a familiar frustration and infrastructure we already had. Recognizing how those pieces could work together created something more useful than either alone.",
          },
          {
            type: "p",
            text:
              "We help clients find similar opportunities in their own businesses: understanding where work gets stuck, identifying where AI and custom software can help, and building solutions that fit the way their teams operate.",
          },
        ],
      },
    ],
    results: [],
  },
  {
    slug: "2x4-30-year-partnership",
    title: "Partnership and Growth: How Macktez and 2x4 Built a 30-Year Relationship",
    subtitle:
      "That is what a thirty-year partnership looks like: the technology recedes, and the work moves forward.",
    client: "2x4",
    industry: "Design & Advertising",
    tags: ["Networking", "Cloud & Infrastructure", "Identity", "Buildout"],
    topics: [
      "design-studio",
      "graphic-design",
      "jumpcloud",
      "identity-management",
      "networking",
      "juniper-mist",
      "storage",
      "synology",
      "managed-services",
      "long-term-partnership",
      "essentials",
    ],
    summary:
      "In 1996, 2x4's first file server was a repurposed Macintosh Performa 6110 and a pile of external hard drives. Thirty years later, it's 196 terabytes of managed, redundant storage, a JumpCloud-based identity and security platform, and a Cisco and Juniper Mist network — documented from the ground up rather than held together from memory. This is the story of how a design studio and a technology partner moved through three eras of trust: reacting, planning, and managing.",
    seoTitle: "Partnership and Growth: How Macktez and 2x4 Built a 30-Year Relationship | Macktez",
    seoDescription:
      "Thirty years of Macktez and 2x4: from a jury-rigged Macintosh Performa in 1996 to a fully managed enterprise infrastructure today, told through three eras of trust — reacting, planning, and managing.",
    canonicalPath: "/case-studies/2x4-30-year-partnership",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Partnership and Growth: How Macktez and 2x4 Built a 30-Year Relationship",
      "description":
        "Thirty years of Macktez and 2x4: from a jury-rigged Macintosh Performa in 1996 to a fully managed enterprise infrastructure today, told through three eras of trust — reacting, planning, and managing.",
      "url": "https://macktez.com/case-studies/2x4-30-year-partnership",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Thirty-year managed IT partnership between Macktez and design studio 2x4",
      },
    },
    featuredImage: {
      src: "/assets/2x4-30-year-partnership.jpg",
      alt: "A graphic reading “2 × 4 + Macktez × 30” on a light blue gradient background, marking thirty years of the Macktez and 2x4 partnership.",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "2x4 — a NYC design studio whose work includes identity systems for Prada and competition work with Rem Koolhaas and Richard Gluckman"],
                  [
                    "Starting point",
                    "A studio running on jury-rigged, aging hardware — the first file server was a repurposed 1990s Macintosh Performa 6110 with a pile of external hard drives — with technology treated as a cost to minimize, not an investment.",
                  ],
                  [
                    "Approach",
                    "Three decades of engagement moving through three eras, in Macktez CEO Noah Landow's own words: Reacting (“Something broke, can you give us a hand?”), Planning (“We're big enough to have a regular conversation”), and Managing (“Let's not wait for things to break”).",
                  ],
                  [
                    "Outcome",
                    "A fully enterprise infrastructure: 196 terabytes of managed, redundant storage, a JumpCloud-based identity and security platform covering roughly 60 staff members, a Cisco and Juniper Mist network, and infrastructure documented from the ground up rather than held together from memory.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "1994–1996",
        heading: "It began with a conversation.",
        blocks: [
          {
            type: "p",
            text: "The relationship between Macktez and 2x4 did not begin with a contract. It began with a conversation.",
          },
          {
            type: "p",
            text: "In the mid-1990s, Michael Rock was a faculty member in Yale's Graphic Design department, where he agreed to be an independent study adviser to an undergrad named Noah Landow on a project about interactive computer technology in education. (Delivered on a Syquest cartridge and a box of cards in a salvaged card catalog drawer.)",
          },
          {
            type: "p",
            text: "In 1994 2x4 was formed when Michael, Susan Sellers, and Georgie Stout created a new studio.",
          },
          {
            type: "p",
            text: "After moving to New York City, Noah freelanced as a designer and provided support for 2x4, and other studios. By 1996 these varied efforts had coalesced into Macktez, which Noah founded with Michael Horst as a loose collective of technical designers and design-savvy technologists.",
          },
        ],
      },
      {
        label: "The Performa 6110",
        heading: "The right tool for the moment.",
        blocks: [
          {
            type: "p",
            text: "The first “file server” at 2x4 was a repurposed Macintosh Performa 6110, a flat, pizza-box-style computer from what Noah calls “the dark days of Apple history,” slow, taupe-colored plastic, with a pile of external hard drives. It was jury rigged but the right tool for the moment. 2x4 was small, ambitious, and savvily operating on a budget. The goal was not to make a perfect tech system but one that was good enough to get work done.",
          },
          {
            type: "p",
            text: "Thirty years later, that Performa and its external hard drives evolved into a fully enterprise infrastructure: 196 terabytes of managed, redundant storage, a cloud-based identity and security platform covering roughly 60 staff members, a network running on Cisco and Juniper Mist, and infrastructure documented from the ground up rather than held together from memory.",
          },
          {
            type: "p",
            text: "Getting from one jury-rigged server to today's infrastructure forged three distinct eras of engagement, technology, and trust. Noah, now CEO of Macktez, describes those eras in three plain sentences:",
          },
          {
            type: "ul",
            items: [
              "Reacting: “Something broke, can you give us a hand?”",
              "Planning: “We're big enough to have a regular conversation.”",
              "Managing: “Let's not wait for things to break.”",
            ],
          },
          {
            type: "p",
            text: "Those three sentences contain thirty years of growth — for 2x4, for Macktez, and for what a partnership between a design studio and a technology firm can look like when it endures long enough.",
          },
        ],
      },
      {
        label: "1996–2013",
        heading: "Reacting",
        blocks: [
          {
            type: "quote",
            text: "Something broke, can you give us a hand?",
          },
          {
            type: "p",
            text: "In its early years, 2x4 was a studio that operated like many creative firms: it hired extraordinary talent, kept overhead low, and treated technology as a necessary cost rather than a strategic investment. The studio would buy one new computer every six to twelve months instead of refreshing the whole fleet, which meant most machines were five or six years old; extremely old for that era. When something broke, Noah would come in and fix it. When a hard drive filled up, Noah would figure out how to free up more space. The approach was, in his own words, “How do we jury-rig this stuff? How can we find a cost-effective way to solve this problem quickly and creatively?”",
          },
          {
            type: "p",
            text: "It was the right model for the studio's size and stage. 2x4 was doing high-profile work, including identity systems for Prada, a competition entry with Rem Koolhaas for MoMA, a massive Woodstock revival project that involved thousands of full-bleed photographs and files literally too large to print. Macktez's job was to keep those projects moving. Sometimes that meant defragmenting a hard drive. Sometimes it meant calling a DSL provider three times in a week because service was unreliable. Sometimes it meant running Norton Utilities on a machine that should have been replaced two years ago and finding enough headroom to finish a deadline.",
          },
          {
            type: "p",
            text: "By 2000, the relationship had grown enough that Macktez was managing a full studio relocation for 2x4, coordinating cabling contractors, supervising the installation of servers and network hardware, running an eight-hour appointment with Bell Atlantic to bring up DSL in the new space, and handling every vendor call in between. That year, Macktez logged 150 hours working on 2x4 projects. Four years later, the number was 700 hours. In 2007 it was 1,100 hours. In 2008, the year Macktez was most deeply embedded in the operational life of the studio, it peaked at 1,300 hours.",
          },
          {
            type: "p",
            text: "The work of those early years shows up vividly in the records. Noah and a rotating team of Macktez technicians — Matthew Vladimir, Dallas Roberts, Jordan Kilpatrick, Matthew Gray, Joseph Aulisi — were fielding daily requests from studio staff, managing DAT tape backups, troubleshooting workstations, ordering supplies, handling email hosting, tracking software licenses, and visiting on-site multiple times a week. 2x4's day-to-day technology contact was Irwin Chen, who communicated constantly with Noah about server space, DSL outages, and new equipment needs. Server disk space was already a recurring concern as early as October 2000, when a technician's note recorded “constant issues with disk space” and recommended adding SCSI storage to the RAID set. That note, written 25 years ago, reads like a preview of everything that came after.",
          },
          {
            type: "p",
            text: "2x4 maintained deep academic connections throughout this era to RISD, to Yale, and to design and architecture schools across the country. The studio had the feel of what Noah calls “a dynamic post-graduate program.” Junior designers would arrive for two or three years and then leave to start their own studios. The work was consistently ambitious, and Macktez was close enough to the work to understand it. For the Prada identity system, Macktez helped the design team build Microsoft Word and Excel templates that translated 2x4's graphic language into tools Prada's own staff could use. For the Dallas Fort Worth competition with architect Richard Gluckman, Noah participated directly as a designer, not just as IT support. The line between technology partner and studio collaborator was never entirely fixed.",
          },
          {
            type: "p",
            text: "Starting in 2008, the dynamic shifted with a clearer emphasis from 2x4 on containing technology costs.",
          },
          {
            type: "p",
            text: "An internal report drafted by Macktez in 2010 captured the change in engagement and strategy. “The requirement to maintain a bare minimum number of hours per week,” it reads, “has meant a focus on only core maintenance and reacting to emergencies.” Long-term planning had come “to a stop.” Macktez had been “excluded from internal technology planning discussions.” Management had made a deliberate decision to reduce Macktez's role to the minimum necessary to keep things running. Macktez honored it. They kept the servers up, managed the backups, responded to emergencies, and waited.",
          },
          {
            type: "p",
            text: "By 2013, Noah and his colleague Jay Ivan wrote a candid technology report addressed to Michael Rock and 2x4's CFO Doug Freedman. Carefully and professionally, it addressed the risks of continuing to defer technology investment. The studio had been getting by on aging infrastructure for years, limiting its ability to take advantage of advancements in hardware and software; designers had “grown more resourceful in handling tech support needs” that competed with their project work; and the storage situation had reached a genuine limit. The studio's file exchange server was a Mac mini handling more work than it was designed for. The active projects Drobo was more than 80% full and filling up fast. The FileMaker server was running on a PowerMac G5 that was years past its useful life.",
          },
          {
            type: "p",
            text: "The period of constraint was necessary from 2x4's business perspective, but deferred costs had created urgent technology challenges. The 2013 report was an invitation to rebuild.",
          },
        ],
      },
      {
        label: "2013–2020",
        heading: "Planning",
        blocks: [
          {
            type: "quote",
            text: "We're big enough to have a regular conversation.",
          },
          {
            type: "p",
            text: "The 2013 strategy report became the blueprint for a systematic rebuilding of 2x4's technology infrastructure. Over the next seven years, Macktez and 2x4 worked through a backlog of deferred decisions and layered in new capabilities the studio had never had before; not just as one-off fixes, but as part of a coherent, documented plan.",
          },
          {
            type: "p",
            text: "The first wave came in 2014. Macktez replaced the failing Drobo archive system with a Synology network-attached storage unit, moved the archives off the main file server for the first time, and defined a formal data archiving process. Those moves mark the beginning of 2x4's modern storage architecture. Macktez also introduced cloud-based workstation backups for senior staff, the first time 2x4 had any cloud services engagement. The FileMaker server was upgraded to a new Mac mini and new iMacs were purchased for design staff. A dedicated After Effects workstation arrived, signaling that 2x4's creative work was expanding into motion and animation.",
          },
          {
            type: "p",
            text: "In 2015, Macktez recommended replacing 2x4's aging conference room setup with a video conferencing solution from Bluejeans, running over a dedicated DSL line with Quality of Service configuration to prioritize video traffic. It was a significant investment, one that let 2x4 engage with clients around the world, but Macktez also evaluated hardware systems costing four to six times as much and ruled them out as more than 2x4 needed. That kind of recommendation — new infrastructure to meet a new business need, with cost and performance evaluated together — reflects the growing trust of this era. 2x4 was now asking Macktez to think with them, not just react for them.",
          },
          {
            type: "p",
            text: "Then came the 16th floor.",
          },
          {
            type: "p",
            text: "In 2016 and 2017, 2x4 expanded into a new floor at their Manhattan office, a significant physical and operational undertaking. Macktez planned the cabling ($34,500 worth of Cat6 runs), designed the network architecture (new FortiGate firewall, Ruckus wireless access points, fiber circuit upgrade), and executed the physical move over a single weekend in May 2017. The full Macktez team was on-site: Nate Smith led the networking, Scott Battaglia handled the phone system, Stefan Brown managed cabling, and Noah coordinated the whole effort. On Monday morning, the studio opened on a new floor with a fully operational network, a new Cisco 52-port switch, a Peplink load balancer for connection redundancy, and a Grandstream PBX for phones. Executing the weekend expansion provided a clear, prominent showcase of the results Macktez could achieve when working with 2x4's complete confidence.",
          },
          {
            type: "p",
            text: "In July 2017, following the move, Macktez introduced a formal managed services arrangement for 2x4's architecture team, standardizing their Rhino and Revit workstations, managing network licensing through McNeel Zoo, and establishing a $400-per-month retainer for ongoing support. It was modest in scope. In retrospect, it was the beginning of the model that would define the relationship a decade later.",
          },
          {
            type: "p",
            text: "The Era 2 arc follows a consistent thread: Macktez and 2x4 moving, project by project, from a reactive posture to a proactive one. Storage grew from a nearly full 10-terabyte Drobo in 2013 to a Synology system with 52 terabytes of usable capacity by 2019, with a documented path to 140 terabytes. Conferencing moved from ad hoc Skype calls to Bluejeans in 2015 to Google Meet via Chromebox in 2018. A power infrastructure hardening project in 2019 added dual uninterrupted power supplies and a managed power distribution unit for remote monitoring to the network and server rack. By 2019, Macktez was not just fixing what broke. They were monitoring and managing the infrastructure so that less would break in the first place.",
          },
          {
            type: "p",
            text: "When the COVID-19 pandemic arrived in early 2020, and 2x4's staff went home, Macktez sent a proactive check-in: assessments of home working conditions, recommendations for remote setups, a goal review. No one had asked for it. That is what a proactive technology partner does. It is also, in miniature, a preview of what the relationship would become in the years that followed.",
          },
        ],
      },
      {
        label: "2020–2026",
        heading: "Managing",
        blocks: [
          {
            type: "quote",
            text: "Let's not wait for things to break.",
          },
          {
            type: "p",
            text: "After Covid restrictions eased, work slowly returned to the office. Macktez reorganized its management tools and workflows to prioritize remote-first support, which was very different from the regular in-person support visits that consultants and technicians had made to 2x4 for years. But the work that had started in 2013 to maximize stability at 2x4 made that transition easier. Years of documented, consistent work had built the trust, knowledge, and institutional understanding needed to build the next era of engagement.",
          },
          {
            type: "p",
            text: "In the summer of 2023, Macktez proposed, and 2x4 approved, a comprehensive rebuild of the studio's physical network infrastructure. Before any equipment was ordered, Macktez conducted an Ethernet tracing audit: two technicians, Ray Brown and Jairo Cuenca, tested and certified all 168 Ethernet cable runs throughout the office, created annotated floor plans, and documented every pass and fail. The audit was not billable work in the traditional sense. It was due diligence; knowing exactly what existed before deciding what to replace.",
          },
          {
            type: "p",
            text: "From there, the projects ran in sequence. The wireless network came first: seven Juniper Mist enterprise access points, each connected by new Cat6A cabling capable of supporting multigigabit speeds, deployed across the office with pre- and post-installation wireless coverage surveys to verify performance. The Juniper Mist platform manages the access points from the cloud and provides AI-driven analytics for diagnosing issues before users notice them. Macktez CTO Reilly Scull and network engineer Luis Rodriguez designed the solution; the installation team verified every square foot of coverage before signing off.",
          },
          {
            type: "p",
            text: "The network upgrade followed. That project — new equipment, full configuration, on-site installation, comprehensive testing, and complete documentation — closed out in November 2023 with a formal handoff including rack diagrams, network diagrams, and configuration records. The documentation is not incidental. It is part of the deliverable. 2x4 now owns a complete picture of their own infrastructure.",
          },
          {
            type: "p",
            text: "Macktez spent months researching and designing a new file sharing solution. In Covid's wake, Macktez had migrated dozens of clients' on-prem server storage to the cloud, and initially planned to recommend a similar solution to 2x4. But the amount of data to migrate and the folder depth of the storage made 2x4's needs more complex. In the end, a hybrid Synology/Dropbox system was designed. On prem storage was consolidated into a single Synology RS4021xs+ server, the studio's Dropbox environment was cleaned up and prepped for more regular, secure use, and a migration tool, Movebot transferred data without disrupting active workflows.",
          },
          {
            type: "p",
            text: "In November 2025, Macktez launched its Essentials managed services platform at 2x4. Essentials uses JumpCloud to create a centralized identity directory: every staff member has a single password that works across Google Workspace, Dropbox, the VPN, the file server, and their computer login. Multi-factor authentication, enforced through the JumpCloud Protect app, is required for all users for all federated services. Every device in the studio is enrolled in endpoint management: antivirus, drive encryption, screen lock policy, and remote wipe capability if a machine is lost or stolen. Onboarding and offboarding workflows are now systematic: when a new employee joins 2x4, access is provisioned from a single platform. When someone leaves, it is revoked from the same place.",
          },
          {
            type: "p",
            text: "The addition of JumpCloud MSP tools at 2x4 marked a milestone for 2x4 and Macktez. It's a recognition that remote-first technology support is more efficient, effective, and secure than the weekly in-person visits that characterized the first 25 years of this relationship. These tools also create more opportunities for iterative policy changes in the coming years, as technology itself is changing at as great a pace as ever.",
          },
        ],
      },
      {
        label: "2026",
        heading: "What Thirty Years Looks Like",
        blocks: [
          {
            type: "p",
            text: "Thirty years is a long time for any company, especially where technology is concerned. The relationship between Macktez and 2x4 has outlasted dozens of storage platforms, operating systems, service providers, and software solutions. What remains is the relationship itself, along with the trust and knowledge accumulated over decades.",
          },
          {
            type: "p",
            text: "Noah's three-era framework — reacting to planning to managing — describes something real about how technology partnerships mature when they are allowed to. An ad hoc “reacting” era may be inevitable for start-ups. It is where organizations learn to walk and where trust is first built. The semi-proactive “planning” era is a period of dynamic transition when systems (and trust) are tested. The enterprise “managing” era is what becomes possible when a client and a technology partner have been through enough together to move from just fixing problems to preventing them.",
          },
          {
            type: "p",
            text: "2x4 is a world-class design studio. Its work appears in some of the most recognized museums, cities, and organizations around the world. Macktez's job over thirty years has been to make sure the technology never gets in the way of that work. In the early years, that meant jury-rigging aging hardware and keeping servers alive past their useful life. Today it means managing a security platform, a storage infrastructure, and a network environment that operate well enough that the designers at 2x4 do not need to think about them at all.",
          },
          {
            type: "p",
            text: "That is what a thirty-year partnership looks like: the technology recedes, and the work moves forward.",
          },
        ],
      },
    ],
    resultsHeading: "Thirty years, three eras.",
    results: [
      "196 terabytes of managed, redundant storage — up from a nearly full 10-terabyte Drobo in 2013",
      "A JumpCloud-based identity and security platform covering roughly 60 staff members, with MFA enforced across every federated service",
      "A Cisco and Juniper Mist network, with wireless coverage verified by pre- and post-installation surveys",
      "Every device enrolled in endpoint management: antivirus, drive encryption, screen lock policy, and remote wipe",
      "Systematic onboarding and offboarding, provisioned and revoked from a single platform",
      "Infrastructure documented from the ground up — rack diagrams, network diagrams, and configuration records — rather than held together from memory",
    ],
  },

  {
    slug: "support-agent",
    title: "Meet support.agent: Our First Fully Autonomous Digital Employee",
    subtitle:
      "How custom MCP servers connected every system we manage — and what changed for our Team and our clients once an agent could use them",
    client: "Macktez (internal)",
    industry: "Technology & Software",
    tags: ["AI", "Identity", "Security", "Cloud & Infrastructure"],
    topics: [
      "mcp",
      "model-context-protocol",
      "claude",
      "ai-agents",
      "support-agent",
      "oidc",
      "jumpcloud",
      "audit",
      "monitoring",
      "network-investigation",
      "read-only",
      "integration",
      "automation",
    ],
    summary:
      "Every 30 minutes, support.agent reads our ticket queue, picks out the work it can help with — a network problem to investigate, a security event to investigate — checks every relevant platform and our own history, and writes up what it found before a person has opened the ticket. It has its own identity, its own permissions, and its own audit trail — a digital employee doing real work. This is the story of what made that possible: custom Model Context Protocol (MCP) servers that let an AI talk to every system we manage, and what it means for the clients on the other end of the ticket.",
    seoTitle: "Meet support.agent: Our First Autonomous Digital Employee | Macktez",
    seoDescription:
      "How Macktez built support.agent, an autonomous AI agent that triages tickets and investigates network problems and security events across every platform we manage — powered by custom Model Context Protocol (MCP) servers — and what it means for clients.",
    canonicalPath: "/case-studies/support-agent",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Meet support.agent — Our First Fully Autonomous Digital Employee, Built on Custom MCP Servers",
      "description":
        "How Macktez built support.agent, an autonomous agent with its own identity that triages and investigates support tickets across every platform it manages, and the custom Model Context Protocol (MCP) servers that connect an AI to those systems safely.",
      "url": "https://macktez.com/case-studies/support-agent",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Autonomous AI agents built on custom Model Context Protocol (MCP) servers",
      },
    },
    featuredImage: {
      src: "/assets/support-agent-mcp-hero-v4.png",
      position: "top",
      alt: "The Macktez MCP platform diagram: the collector, the support toolkit, support.agent and its Jev classifier, and the Team that call it; the sign-in gate; and every connection on mcp.macktez.com, now including Zoom, grouped by how it signs in to its vendor.",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "Macktez (internal)"],
                  [
                    "Problem",
                    "Most support work is the same loop: go to several platforms, look at what each one says, decide what is going on, then act. Every step was a person clicking through consoles, and none of that clicking taught anyone anything.",
                  ],
                  [
                    "Approach",
                    "Write a custom Model Context Protocol (MCP) server for every platform we manage so an AI can reach all of them, then give an agent its own identity, its own permissions, and the ticket queue.",
                  ],
                  [
                    "Outcome",
                    "support.agent, an agent with its own identity that works the ticket queue around the clock — network and security investigations, and a growing list of other work. Clients get faster answers grounded in more evidence, and the Team spends its time on judgment instead of tab-switching.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The End State",
        heading: "A colleague you will never meet",
        blocks: [
          {
            type: "p",
            text: "Every 30 minutes, support.agent signs in and reads the ticket queue. It picks out the tickets that look like network problems — the Wi-Fi is slow, a printer disappeared, the office lost internet for a minute — and goes to work. It pulls the current state from the wireless controller, the firewall, the switches, and device management. It checks what our monitoring remembers about that network over the past few days. It looks up which client, which site, and which hardware is involved. Then it writes what it found onto the ticket, posts a summary in Slack, and files its notes in Google Drive.",
          },
          {
            type: "p",
            text: "By the time a Team Member opens that ticket, the evidence is gathered, the timeline is laid out, and there is a working theory on the table. What used to be the first twenty minutes of every network ticket is already done.",
          },
          {
            type: "p",
            text: "Network problems were only where we started. support.agent also runs security investigations. When a sign-in looks wrong, or the endpoint-security platform raises an alert, it pulls the sign-in history, the alert detail, what the device was doing at the time, and who the person is, then lays the sequence out in one place. Assembling that picture by hand is exactly the kind of work that is urgent, tedious, and easy to do incompletely at the end of a long day.",
          },
          {
            type: "p",
            text: "The notes are the part that compounds. Every investigation leaves a durable record for that client — how their network normally behaves, which alerts turned out to be noise, what the problem was last time and what resolved it — and later runs read those notes before they start. The second investigation at a site begins with what the first one found. The agent gets better at a given client the longer it works there, and nobody has to write that knowledge down by hand. Under the old way, the twenty minutes a person spent gathering evidence vanished the moment they closed the ticket.",
          },
          {
            type: "p",
            text: "support.agent is not a chatbot bolted onto the help desk. It is our first fully autonomous digital employee. It has its own identity in our directory, the same way a new hire would. It can only see the systems its role allows. Everything it does is logged under its own name. And it works on its own schedule, without anyone asking it to.",
          },
          {
            type: "p",
            text: "It is not a replacement for anyone, and it never changes anything on its own. It investigates on a schedule and produces internal findings; a Team Member decides and carries out changes. support.agent works in service of a person: every finding lands in front of someone who checks it, decides, and acts. That human in the loop is how we do quality control, and it is not optional. Getting more of this to run without a person watching means treating quality control and security as the whole job, not an afterthought.",
          },
          {
            type: "p",
            text: "Still, this is the thing all the excitement about AI has actually been about: not a better search box, but software that shows up, does real work across real systems, and hands a person the result. The rest of this story is how we got here, working backwards from the problem.",
          },
        ],
      },
      {
        label: "The Problem",
        heading: "Look, decide, act — across dozens of systems",
        blocks: [
          {
            type: "p",
            text: "Nearly every support task has the same shape. Go to several platforms. Look at what each one says. Decide what is going on. Act. A ticket that says “the Wi-Fi is slow in the conference room” touches five systems before anyone can say what is wrong, and every one of them has its own login, its own screen, and its own way of describing the same office.",
          },
          {
            type: "p",
            text: "For as long as this industry has existed, a person did all of that by hand. Sign in here, find the right page, copy out a number, sign in there, compare, repeat. It is slow. It depends on the individual knowing where everything lives. And it produces nothing of lasting value: clicking around a dozen windows to collect logs does not teach anyone anything. It is minutiae, and nobody was learning from it.",
          },
          {
            type: "p",
            text: "The thinking part — what does this pattern mean, what should we do about it — was never the bottleneck. The gathering was. We wanted to hand the gathering, and the first pass at investigation, to a computer.",
          },
        ],
      },
      {
        label: "Why Now",
        heading: "Computers could not talk to all these systems — until now",
        blocks: [
          {
            type: "p",
            text: "The reason nobody automated this years ago is that it was genuinely hard. Every platform has its own way for software to talk to it: different sign-in, different vocabulary, different shape of answer. Writing a program that spoke to all of them, and could reason about a fuzzy ticket in plain language, was out of reach.",
          },
          {
            type: "p",
            text: "Two things changed. Large language models supplied the reasoning: an AI can now read “the Wi-Fi is slow” and know what to check. And the Model Context Protocol (MCP) supplied the connection. MCP is a common language that lets an AI use outside tools instead of only answering from memory. A custom MCP server is a wrapper around one platform — a Rosetta Stone that translates that vendor’s particular dialect into the one language the AI understands, carries the actual requests, and returns the answers in a consistent form.",
          },
          {
            type: "p",
            text: "Few of the platforms we manage offered one, so we wrote our own for each and host them together at mcp.macktez.com. Even where a vendor does offer an MCP server, it often exposes only part of the platform, gives us little say over who can use which tools, and keeps no record of who did. Building our own meant we could cover every tool we need, decide permissions ourselves, and keep our own log. Once every system spoke the same language, an AI could reach all of them — and so could an agent.",
          },
        ],
      },
      {
        label: "How It Came Together",
        heading: "People first, then agents, then a dashboard",
        blocks: [
          {
            type: "p",
            text: "The first payoff was for the Team. A Team Member opens Claude, asks about a client’s network in ordinary words, and the assistant gathers the evidence from every platform — including MackInfo, our system of record for clients, jobs, and billing — and answers with the sources attached. Each Team Member connects the platforms their role calls for, behind the same sign-in they already use for everything else.",
          },
          {
            type: "p",
            text: "The connections do not care whether a person or a program is asking. So the steps a Team Member follows on a network ticket could be written down as skills, the start of what we now call the support toolkit, and handed to an agent with its own identity. That agent is support.agent.",
          },
          {
            type: "p",
            text: "The list of work it takes on keeps growing. Adding a workflow now means writing down what a Team Member does by hand and checking that the agent can reach the systems involved — which, by this point, it usually can. Network investigations came first, security investigations followed, and the queue of candidates is longer than the list of what it does today.",
          },
          {
            type: "rich",
            parts: [
              "How the queue is sorted today, and how each investigation starts from what we already know about the client, is the subject of ",
              { text: "AI Assisted Ticket Flow", href: "/case-studies/ai-assisted-ticket-flow" },
              ".",
            ],
          },
          {
            type: "rich",
            parts: [
              "The same connections then gave us ",
              { text: "monitor.macktez.com", href: "/case-studies/macktez-monitor" },
              ". Every 5 minutes a simple script — no AI involved — asks each platform how things stand and saves a snapshot. The dashboard checks those snapshots against our rules for every client at once: is a device offline, is a link degrading, has a configuration changed, did last night’s backups finish. It keeps the history too, which is where support.agent learns what “normal” looks like for a network before deciding what is unusual.",
            ],
          },
          {
            type: "img",
            src: "/assets/support-agent-mcp-topology-v3.png",
            zoomHref: "/assets/support-agent-mcp-topology-v3.svg",
            width: 3600,
            height: 3764,
            alt: "Macktez MCP platform diagram: one host at mcp.macktez.com with a connection per platform, Zoom among them, the sign-in gate above them, the client systems they reach below, and three cycles drawn as numbered legs: monitoring every 5 minutes, the ticket flow every 30 minutes with its Jev classifier outside the host, and the investigations the support toolkit runs across the platforms.",
            caption: "The full picture: every connection, how each one signs in to its vendor, and the three cycles that run on them: monitoring, the ticket flow, and the investigations the support toolkit runs. The legend and wire tables explain every leg.",
          },
        ],
      },
      {
        label: "Working Alongside the Team",
        heading: "A colleague in Slack and on Zoom",
        blocks: [
          {
            type: "p",
            text: "support.agent doesn’t only work the queue. It’s a member of our Slack, and the Team talks to it the way they’d talk to a colleague. Ask it a question in a direct message and it answers from the same platforms and client history it uses on tickets. Reply in the thread under one of its reports and it picks that investigation up where it left off, instead of starting over. Tell it a finding missed the mark, or went deeper than the ticket needed, and that feedback shapes how it works the next one.",
          },
          {
            type: "p",
            text: "It connects to Slack the way the Slack app on a laptop does, by opening its own connection out to Slack. (Slack calls this Socket Mode.) Nothing on its machine sits open to the internet waiting for messages to arrive.",
          },
          {
            type: "p",
            text: "It joins our Zoom calls, too. When a Team Member on its roster is in a Macktez meeting, support.agent joins and appears in the participant list as Support Agent, and anyone can ask it to join from Slack. Its presence is the consent: everyone on the call can see it, and the host can remove it at any time. Afterward it can read the transcript of a meeting it was present for, and only those meetings, and every read is logged.",
          },
          {
            type: "rich",
            parts: [
              "That’s how calls become knowledge. Each week, support.agent reads what changed across tickets, Slack, calendars, and meeting transcripts, and proposes updates to each client’s reference documents, checked against their sources before anything is published. A decision made on a call can be in that client’s notes the following week without anyone writing it up. How those references were built in the first place is the subject of ",
              { text: "Putting Past Work to Work", href: "/case-studies/putting-past-work-to-work" },
              ".",
            ],
          },
          {
            type: "p",
            text: "None of this widens what it’s allowed to do. In Slack and on Zoom it has the same read-only reach it has everywhere else: it can draft a reply, and a person decides whether to send it.",
          },
        ],
      },
      {
        label: "Guardrails",
        heading: "What a digital employee is allowed to do",
        blocks: [
          {
            type: "p",
            text: "Giving software its own identity and access to every system is only a good idea if it is governed at least as well as a person would be. A few principles do most of the work.",
          },
          {
            type: "p",
            text: "support.agent only investigates. It never changes a setting, restarts a device, or replies to a client. Those decisions stay with a person, who now makes them with the evidence already in hand. The connections that exist to answer questions are built so they cannot change anything — a tool that alters a system simply cannot be added to them — and where changes are possible, reaching them takes a second, separate permission.",
          },
          {
            type: "p",
            text: "It uses exactly the same sign-in, permissions, and logging as any Team Member, because it uses exactly the same connections. There is no looser path for software. Where a vendor only offers shared keys, we use more than one: people with read-only access work through a key that can only read, and the separate key that can make changes is used behind the scenes only for those with elevated permissions.",
          },
          {
            type: "img",
            src: "/assets/support-agent-tool-call.png",
            zoomHref: "/assets/support-agent-tool-call.svg",
            size: "wide",
            width: 3600,
            height: 2084,
            alt: "Sequence diagram with five lanes: a Team Member in Claude or support.agent, the MCP endpoint at mcp.macktez.com, JumpCloud, a vendor platform, and the audit log on Amazon S3. Nine numbered steps run top to bottom: sign-in through JumpCloud, identity confirmed with group memberships, a tool call, the endpoint’s group check on every call, the vendor API call with the key that matches the caller’s permission, the data returned, the audit event written, the result returned to Claude, and a finding handed to a Team Member who decides.",
            caption: "One tool call, start to finish: the same sign-in, group check, vendor key, and audit event whether a person or support.agent is asking. The two gold boxes are where a decision is made rather than data moved.",
          },
          {
            type: "p",
            text: "We keep our own record of who did what. Some vendors let our connection sign in as the individual and log the person themselves. Many others cannot tell who is using a shared key, so we log every request through every connection against the Team Member or agent who made it. We can answer who did what on every platform we manage, including the ones that were never built to tell us.",
          },
          {
            type: "p",
            text: "A record is only useful if someone reads it, so we also built tripwires. When something high-risk happens, an alert posts within moments in a Slack channel the Team watches. That makes them a governance tool and a security tool at once. They show us when a person or an agent steps outside how they normally work, and those are the same signals an attacker would set off first.",
          },
          {
            type: "ul",
            items: [
              "Someone tries a connection or a tool they aren’t authorized for. They’re refused, and we hear about it.",
              "An agent does something outside its normal pattern.",
              "Sign-ins or activity that don’t fit the person or agent behind them, such as from an unexpected place or at an unexpected pace.",
            ],
          },
          {
            type: "p",
            text: "Alerts say what happened and who was involved, and leave out the raw details, so the channel is safe for people and support.agent alike to read.",
          },
        ],
      },
      {
        label: "For Clients",
        heading: "Faster answers, grounded in more evidence",
        blocks: [
          {
            type: "p",
            text: "The point of all of this is what happens on the other end of the ticket. When a client reports a problem, the first response now comes with the evidence already gathered from every system involved, not from whichever console someone happened to open first. The answer is faster, because the gathering took minutes instead of a chunk of someone’s afternoon. And it is better, because it is grounded in more data than a person would realistically pull by hand for a routine ticket.",
          },
          {
            type: "p",
            text: "The Team’s time moves to where it matters: judgment, conversation with the client, and fixing the thing. The minutiae nobody was learning from is gone. And because the same connections serve people, agents, and dashboards alike, the next workflow we hand to a digital colleague — a report, a scheduled check, a client-facing status page — will cost a fraction of what the first one did.",
          },
          {
            type: "rich",
            parts: [
              "support.agent grew out of the broader rollout described in ",
              { text: "Operationalizing AI Across Every Department", href: "/case-studies/operationalizing-ai" },
              ", and we built it the way we built this website — with Claude Code, on the same identity backbone (see ",
              { text: "Vibe Coding, Done Right", href: "/case-studies/vibe-coding-done-right" },
              ").",
            ],
          },
        ],
      },
    ],
    resultsHeading: "What we built.",
    results: [
      "support.agent: an agent with its own identity, working the ticket queue every 30 minutes — network and security investigations, and a growing list of workflows — and handing a person the evidence, without ever changing a client system",
      "A custom MCP server for every platform we manage, so an AI — or an agent — can reach all of them in one language, hosted together at mcp.macktez.com",
      "One interface for the Team: any Team Member can ask a question that spans every system, from Claude, in plain language",
      "Durable per-client notes from every investigation, read back on later runs so each one starts from what the last one found",
      "monitor.macktez.com: network, server, and backup health for every client, snapshotted every 5 minutes, with no AI in the loop",
      "The same sign-in, permissions, and logging for people and agents alike, with each platform its own door and roles deciding which doors open",
      "Question-only connections that cannot be given the ability to change anything, separate keys for read-only and elevated access, and a separate permission wherever changes are possible",
      "Our own record of who did what on every platform, including the ones whose vendors cannot tell",
      "Tripwires that post high-risk activity by people or agents to Slack within moments",
      "support.agent in Slack and on Zoom: a colleague the Team can message, that resumes an investigation from a thread reply, joins Macktez calls in plain view, and folds what was said into each client’s references every week",
      "Faster, better-grounded first responses for clients, and Team time moved from clicking to judgment",
    ],
  },

  {
    slug: "operationalizing-ai",
    title: "Operationalizing AI Across Every Department",
    subtitle:
      "How Macktez deployed Claude at every level of the business — with the identity, governance, and architecture to do it safely",
    client: "Macktez (internal)",
    industry: "Technology & Software",
    tags: ["AI", "Identity", "Security"],
    summary:
      "How Macktez moved beyond AI as a chat assistant to a governed operational layer — AI agents provisioned as managed JumpCloud identities with least-privilege access, custom OAuth-backed MCP servers, and audit governance enforced at every endpoint, automating and augmenting work across every department.",
    seoTitle: "Operationalizing AI Across Every Department | Macktez",
    seoDescription:
      "How Macktez deployed Claude across the business — AI agents provisioned as managed JumpCloud identities with least-privilege access, custom OAuth-backed MCP servers, and audit governance enforced at every endpoint — operationalizing AI at every level and in every department.",
    canonicalPath: "/case-studies/operationalizing-ai",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Operationalizing AI Across Every Department — Governed Agentic AI at Macktez",
      "description":
        "How Macktez deployed Claude as a governed operational layer across the business — AI agents as managed JumpCloud identities with least-privilege RBAC, custom OAuth-backed MCP servers via JumpCloud OIDC, and audit governance enforced at each endpoint by group membership.",
      "url": "https://macktez.com/case-studies/operationalizing-ai",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Agentic AI operations with managed identity and governance",
      },
    },
    featuredImage: {
      src: "/assets/operationalizing-ai-v2.jpg",
      alt: "A human conductor leading an orchestra of humanoid robots — orchestrating AI agents across the business.",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "Macktez (internal)"],
                  [
                    "Problem",
                    "Move beyond using AI as a chat assistant to deploying it as a genuine operational layer across the business — able to act inside real systems, automate real work, and do it all under the same identity and governance controls that protect everything else.",
                  ],
                  [
                    "Approach",
                    "A structured Claude rollout built on managed identity, least-privilege role-based access, custom MCP servers, and audit governance enforced at every endpoint.",
                  ],
                  [
                    "Outcome",
                    "AI agents that operate across the business's systems through natural language — authenticated, permissioned, and audited like any other user — automating work and augmenting the team across every department.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Opportunity",
        heading: "Beyond the chat window",
        blocks: [
          {
            type: "p",
            text: "Most organizations' experience with AI stops at the chat window: a person types a question, gets an answer, and copies the result somewhere useful. That's valuable, but it's a fraction of what's possible. The larger opportunity is to let AI act — to read from and write to the actual systems a business runs on, and to automate or assist with the work that today requires a human to log in, click through, and carry data from one place to another.",
          },
          {
            type: "p",
            text: "The obstacle isn't the AI's capability. It's everything around it: identity, permissions, security, and governance. An agent that can act inside your business systems is only safe if it's subject to the same controls as a human employee — authenticated through your identity provider, scoped to only what it should access, and audited so every action is attributable. Without that scaffolding, agentic AI is a liability. With it, it's a genuine operational capability.",
          },
          {
            type: "p",
            text: "We built the scaffolding first, then rolled out the agents. This is how.",
          },
        ],
      },
      {
        label: "Identity",
        heading: "Agents are users too",
        blocks: [
          {
            type: "p",
            text: "The foundation of the entire rollout is a simple principle: an AI agent is treated as a managed identity, exactly like a person.",
          },
          {
            type: "p",
            text: "Each agent is a JumpCloud user. It has its own identity, its own role-based access controls at Google, Microsoft, and the other platforms it touches, and its own scoped permissions — granted and revoked centrally, the same way we manage human staff. Every agent is provisioned according to the principle of least privilege: it gets access to exactly what it needs to do its job and nothing more. An agent responsible for documentation has access to the documentation systems and nothing else. An agent that handles invoicing is permissioned for the accounting platform, not HR records. Because these are real managed identities, every action an agent takes is attributable to that identity, and access can be revoked instantly if anything looks wrong.",
          },
          {
            type: "p",
            text: "This is the difference between \"we gave the AI an API key\" and \"we onboarded the AI like an employee.\" The second is governable. The first is a breach waiting to happen.",
          },
        ],
      },
      {
        label: "Governance",
        heading: "Every action audited",
        blocks: [
          {
            type: "p",
            text: "There is no gateway in front of the agents’ access to business systems, and none is needed. Every MCP endpoint checks the caller’s JumpCloud group membership itself and refuses by default: no group, no access. Alongside that gate, the MCP fleet writes its own audit trail of every tool call and every authentication event, recording who called what and when.",
          },
          {
            type: "p",
            text: "Governance is enforced at each endpoint rather than centralized at a chokepoint, so there is no single path that everything has to squeeze through and no single point whose failure opens everything. AI activity across the business isn’t a black box — it’s a reviewable audit trail. For any organization that has to answer questions about who, or what, accessed a system and when, this is the control that makes agentic AI defensible rather than reckless.",
          },
        ],
      },
      {
        label: "Architecture",
        heading: "Custom MCPs as the connective tissue",
        blocks: [
          {
            type: "p",
            text: "What lets the agents actually do things is a set of custom MCP servers — the standardized way modern AI models connect to external tools and systems.",
          },
          {
            type: "p",
            text: "We host these at mcp.macktez.com, one endpoint per system, and every Team Member adds each endpoint to their own Claude as its own connector. Signing in goes through JumpCloud using OIDC, so an agent connects with revocable, identity-bound authorization rather than a static credential buried in a config file. The result is that the business becomes addressable in natural language: a monitoring dashboard, a business record, or a network controller can be asked — in plain language — to check, retrieve, or analyze, under a governed identity.",
          },
          {
            type: "rich",
            parts: [
              "How the platform is built, how access to it is gated at each endpoint, and why the same layer turned out to run a monitoring program with no AI in it is the subject of ",
              { text: "its own case study", href: "/case-studies/support-agent" },
              ".",
            ],
          },
        ],
      },
      {
        label: "In Practice",
        heading: "What the agents do, across every department",
        blocks: [
          {
            type: "p",
            text: "With identity, governance, and connectivity in place, the agents take on work across the business, all of it on the same governed foundation:",
          },
          {
            type: "ul",
            items: [
              "Time tracking — capturing and recording time against work without manual entry.",
              "Documentation — generating and maintaining technical and operational documentation that historically went stale because no one had time to keep it current.",
              "Network analysis and troubleshooting — querying network monitoring systems in natural language, surfacing issues, and assisting with investigation.",
              "Monitoring — watching systems and surfacing what matters to a human operator, augmenting the team rather than replacing the human in the loop.",
              "Sales and marketing — supporting outreach, content, and pipeline work.",
              "Invoicing and accounting — moving financial data through the systems that handle it, reducing manual reconciliation and entry.",
              "HR and compliance — assisting with the recurring administrative and compliance work that consumes staff time.",
            ],
          },
          {
            type: "p",
            text: "In each case the pattern is the same: work that used to require a human to log in and operate a system can now be initiated in natural language and carried out — or accelerated — by an agent operating under its own permissioned, audited identity.",
          },
        ],
      },
      {
        label: "The Results So Far",
        heading: "What the foundation has produced since May",
        blocks: [
          {
            type: "p",
            text: "The foundation paid for itself quickly. In the months since we put Claude to work on it (the timeline below has the dates), the same identities, connections, and working practices have carried one piece of production work after another, each starting from what the one before it had left behind:",
          },
          // The list of connections and launches, and the case-study links, come from
          // src/app/lib/ai-release-timeline.ts. Add a launch there (with a `summary`) and it appears here.
          { type: "release-highlights" },
          {
            type: "p",
            text: "None of it would have come together this fast without the identity, permission, and audit layer described above, and none of it needed a second one. That is the point of building the foundation first: the second project is cheaper than the first, and each one after that is cheaper still.",
          },
        ],
      },
      {
        label: "Release Timeline",
        id: "release-timeline",
        heading: "How it came online, day by day",
        wide: true,
        blocks: [
          {
            type: "p",
            text: "None of this arrived at once. Each connection came online when the one before it had proved the pattern, the foundation under them grew as we needed it, and the systems we built on top followed. This timeline starts on the first day of our Claude rollout, and we’ll keep adding to it.",
          },
          { type: "release-timeline" },
        ],
      },
      {
        label: "The Throughline",
        heading: "One foundation, many kinds of work",
        blocks: [
          {
            type: "p",
            text: "On the surface those systems have little in common: a marketing website, a monitoring dashboard, an agent that reads a ticket queue, a process that reads a year of records. What they share is everything underneath. Each one signs in through the same directory, reaches systems through the same connections under the same permissions, and writes to the same audit trail. That's why none of them needed an architecture of its own, and why the next one won't either.",
          },
          {
            type: "rich",
            parts: [
              "AI, deployed properly, isn't a single tool. It's a capability: once the identity, governance, and connective layer exists, it extends from how you build a website to how you run a back office. For most of our clients that layer is the one we already run as their managed service provider, which is where we start when a client asks ",
              { text: "what AI could do for them", href: "/services/ai" },
              ".",
            ],
          },
        ],
      },
    ],
    resultsHeading: "What we built.",
    results: [
      "AI agents provisioned as managed JumpCloud identities, with least-privilege RBAC across Google, Microsoft, and other platforms",
      "Access gated at every MCP endpoint by JumpCloud group membership, fail-closed, with the fleet writing its own audit trail of every tool call and authentication event",
      "Custom MCP servers hosted at mcp.macktez.com, one per tool/system",
      "OAuth-backed MCP authentication via JumpCloud OIDC — identity-bound, revocable authorization, no static credentials",
      "Natural-language operation across business systems through the MCP layer",
      "Agentic automation and augmentation across time tracking, documentation, network troubleshooting, monitoring, sales and marketing, invoicing and accounting, and HR/compliance",
      "The Macktez website itself, built with Claude Code on the same identity backbone",
      "One production system after another since May 2026, each built on the connections and practices the one before it left behind",
    ],
  },

  {
    slug: "vibe-coding-done-right",
    title: "Vibe Coding, Done Right",
    subtitle:
      "How macktez.com was designed, built, and is maintained using AI-assisted development — and what it takes to do it well",
    client: "Macktez (this website)",
    industry: "Technology & Software",
    tags: ["AI", "Cloud & Infrastructure", "Identity"],
    summary:
      "How Macktez designed, built, and maintains macktez.com with AI-assisted development — a fully custom Next.js and Tailwind site on Vercel, with GitHub, JumpCloud SSO, and professional infrastructure underneath — and what it actually takes to vibe code a production website well.",
    seoTitle: "Vibe Coding, Done Right | Macktez",
    seoDescription:
      "How Macktez designed, built, and maintains macktez.com using AI-assisted development — a custom Next.js site on Vercel, with GitHub, JumpCloud SSO, and professional infrastructure — and what it actually takes to vibe code a production website well.",
    canonicalPath: "/case-studies/vibe-coding-done-right",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Vibe Coding, Done Right — Building macktez.com with AI-Assisted Development",
      "description":
        "How Macktez designed, built, and maintains its own website with AI-assisted development — a fully custom Next.js and Tailwind site on Vercel, with GitHub, JumpCloud SSO, automated TLS, and professional virtualized infrastructure underneath.",
      "url": "https://macktez.com/case-studies/vibe-coding-done-right",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "WebSite",
        "name": "macktez.com",
        "url": "https://macktez.com",
        "description":
          "A fully custom Next.js and Tailwind marketing site built with AI-assisted development and maintained by non-developers through a chat interface.",
      },
    },
    featuredImage: {
      src: "/assets/vibe-coding-macktez.png",
      alt: "An AI-assisted development session building macktez.com — code edits alongside a live terminal build running in Claude Code.",
      position: "bottom",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "Macktez (this website)"],
                  [
                    "Problem",
                    "Build a custom, production-grade marketing site for a technical consulting firm — distinctive design, structured SEO, a growing case study library, and an ongoing content pipeline — without hiring a web developer.",
                  ],
                  [
                    "Approach",
                    "AI-assisted development using Claude Code, on a purpose-built virtualized dev environment, with professional infrastructure and identity management underneath.",
                  ],
                  [
                    "Outcome",
                    "A fully custom Next.js and Tailwind site, live on Vercel, maintained by non-developers through a chat interface. Estimated cost to build the same site at agency or senior freelance rates: $25,000–$40,000 for the initial build alone.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Premise",
        heading: "What \"vibe coding\" actually means",
        blocks: [
          {
            type: "p",
            text: "Vibe coding is a recent term for a style of software development where, instead of writing code by hand, you describe what you want in plain language and an AI model writes the code for you. You review, you refine, you describe the next change — and the work happens in a conversation rather than a text editor.",
          },
          {
            type: "p",
            text: "It's a genuinely new way to build software, and it's powerful. But there's a catch that doesn't get said often enough: doing it well still requires knowing what you're doing. You either need to be a developer, or you need solid technical fundamentals — enough to set up the infrastructure correctly, to recognize when the AI has made a wrong turn, and to make the architectural decisions that the model can execute but shouldn't be left to decide on its own. Vibe coding lowers the labor of writing code. It doesn't remove the need to understand systems.",
          },
          {
            type: "p",
            text: "This site is a case study in that distinction — because the visible part, a website built by describing it, sits on top of a foundation of decisions that required real technical knowledge to get right.",
          },
        ],
      },
      {
        label: "The Foundation",
        heading: "The infrastructure underneath the conversation",
        blocks: [
          {
            type: "p",
            text: "Before a single line of the site was written, there was infrastructure to stand up — and this is where the fundamentals earn their keep.",
          },
          {
            type: "p",
            text: "The development environment is a virtual machine, provisioned on our own VMware infrastructure. Spinning it up meant making decisions about resource allocation, networking, and operating system — the kind of routine virtualization work that's invisible when it's done right and a constant obstacle when it isn't.",
          },
          {
            type: "p",
            text: "Source control runs through GitHub. Setting that up properly meant creating the repository, configuring SSH and deploy keys so the dev environment could authenticate to GitHub securely without passwords living in plaintext, and establishing a git workflow — commits, branches, and a clean history — so that every change is tracked and reversible.",
          },
          {
            type: "p",
            text: "Hosting runs on Vercel, linked directly to the GitHub repository. This is a continuous deployment pipeline: when a change is committed and pushed, Vercel automatically builds and deploys the updated site. There's no manual upload step, no FTP, no \"publishing\" — the act of saving the code is the act of deploying it. Changes are reviewed first on a private development copy of the site, behind single sign-on, and pushed to production once they look right.",
          },
          {
            type: "p",
            text: "DNS was configured to point the domain at Vercel's infrastructure, and TLS certificates — the encryption that puts the padlock in the browser and serves the site over HTTPS — are handled through Let's Encrypt and Certbot, issuing and automatically renewing certificates so the site is always served securely.",
          },
          {
            type: "p",
            text: "None of this is exotic. All of it is the kind of thing that, done wrong, produces a site that's insecure, can't deploy, or simply doesn't work. The AI can write code beautifully. It can't decide for you how your deployment pipeline should be structured, or notice that your certificate auto-renewal is misconfigured. That's the part that takes fundamentals.",
          },
        ],
      },
      {
        label: "Identity",
        heading: "Built so the whole team can run it",
        blocks: [
          {
            type: "p",
            text: "One decision turned this from a personal project into a team asset: connecting the development platform to JumpCloud for single sign-on.",
          },
          {
            type: "p",
            text: "JumpCloud is our identity provider, and wiring the site's management environment into it means access is governed by the same identity system as everything else we run. Team members log in through SSO — no separate credentials, no shared passwords, access granted and revoked centrally. Practically, this is what allows non-technical team members to manage the site and add content. They don't need to be developers. They don't need to touch the infrastructure. They log in through an interface they already have access to and work through a chat interface to make changes.",
          },
          {
            type: "rich",
            parts: [
              "That same identity backbone reaches well beyond this site — it's how we provision and govern AI agents across the entire business, a rollout we detail in ",
              { text: "Operationalizing AI Across Every Department", href: "/case-studies/operationalizing-ai" },
              ".",
            ],
          },
        ],
      },
      {
        label: "The Payoff",
        heading: "What it actually changes",
        blocks: [
          {
            type: "p",
            text: "The headline result is speed, but the specific kind of speed matters.",
          },
          {
            type: "p",
            text: "Complex edits that would have taken a developer hours — or taken days to schedule, brief, and wait on — now take minutes. A concrete example: as new case studies and content roll out, every related page should cross-link to it. Done by hand, that's tedious, error-prone work that tends to get deferred. Through a conversation, it's a single instruction, executed across the whole site in one pass. Content can be added the moment it's ready instead of waiting for developer availability.",
          },
          {
            type: "p",
            text: "And because the management layer is behind SSO and driven by chat, the people adding content aren't gated on a developer at all. The site is live. The team is using it. New content goes up when it's written, not when someone has time to hand-code it.",
          },
        ],
      },
      {
        label: "The Economics",
        heading: "What this would have cost",
        blocks: [
          {
            type: "p",
            text: "A site like this one — custom design system, a dozen-plus case studies, more than a dozen industry pages, structured data and SEO throughout, multi-level navigation, and continuous iteration — is not a template brochure site. Priced conventionally, current market rates put a custom Next.js build of this scope at $25,000–$40,000 with an agency or senior freelancer for the initial build, with senior developers billing $100–$200 per hour. Ongoing iterative work — the constant stream of edits, new pages, and cross-linking — would add several thousand dollars a year on top, at those same rates.",
          },
          {
            type: "p",
            text: "The point isn't that the site was free to build. Doing it well took real expertise and real time. The point is that the same expertise, paired with AI-assisted development, produced an agency-grade result at a fraction of the cost — and left us with something an agency engagement wouldn't: a site our own team can run, every day, without calling anyone.",
          },
        ],
      },
    ],
    resultsHeading: "What we did.",
    results: [
      "Provisioned a dedicated development VM on our own VMware infrastructure",
      "Created and configured a GitHub repository with SSH and deploy keys for secure authentication",
      "Established a clean git workflow with full version history",
      "Linked GitHub to Vercel for continuous deployment — commit to deploy, automatically",
      "Configured DNS and automated TLS via Let's Encrypt and Certbot",
      "Integrated the management environment with JumpCloud SSO, so non-technical team members can manage the site securely",
      "Used Claude Code to build the entire site through conversation",
      "Built a fully custom Next.js + Tailwind site, live on Vercel, maintained through a chat interface",
    ],
  },

  {
    slug: "redundancy-by-design",
    title: "Zero Downtime Since Deployment — Enterprise Infrastructure for a Leading NYC Architecture Firm",
    subtitle:
      "A ground-up, zero-compromise infrastructure built with redundancy as a first-order constraint — network, compute, storage, wireless, and access control, with no single point of failure tolerated anywhere in the stack.",
    client: "A leading NYC architecture firm",
    industry: "Architecture & Design",
    tags: ["Networking", "Cloud & Infrastructure", "Security", "Identity"],
    summary:
      "Ground-up redundant infrastructure for a 100-person architecture firm — Cisco switching, Ruckus Wi-Fi 6, VMware cluster, dual-controller SAN, PDK access control, and 10 Gbps WAN. Zero downtime since deployment.",
    featuredImage: {
      src: "/assets/PI_Network_Topology.png",
      alt: "Network topology diagram — Pembrooke & Ives full-stack redundant infrastructure",
    },
    seoTitle:
      "Zero Downtime Since Deployment — Enterprise Infrastructure for a Leading NYC Architecture Firm | Macktez",
    ogImage: "https://macktez.com/assets/PI_Network_Topology.png",
    seoDescription:
      "Macktez designed a ground-up, zero-compromise infrastructure for a 100-person NYC architecture firm — fully redundant networking, enterprise SAN, VMware cluster, Ruckus Wi-Fi 6, PDK access control with Entra provisioning, and a 10 Gbps WAN. Zero infrastructure downtime since deployment.",
    canonicalPath: "/case-studies/redundancy-by-design",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Zero Downtime Since Deployment — Enterprise Infrastructure for a Leading NYC Architecture Firm",
      description:
        "Macktez designed a ground-up, zero-compromise infrastructure for a 100-person NYC architecture firm — fully redundant networking, enterprise SAN, VMware cluster, Ruckus Wi-Fi 6, PDK access control with Entra provisioning, and a 10 Gbps WAN. Zero infrastructure downtime since deployment.",
      author: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      about: {
        "@type": "Organization",
        name: "A leading NYC architecture firm",
      },
      keywords:
        "architecture firm infrastructure, Cisco Catalyst 9300, Cisco Catalyst 9500, SonicWall NSA 4700, Ruckus Wi-Fi 6, VMware ESXi, Dell PowerEdge, Synology UC3200, PDK access control, Entra ID, disaster recovery, high availability",
    },
    sections: [
      {
        label: "The Challenge",
        heading: "Legacy infrastructure built for a smaller firm",
        blocks: [
          {
            type: "p",
            text: "When a premier New York City architecture firm scaled past 100 employees and relocated to a new headquarters, their legacy bare-metal Windows Server environment could no longer support the high-concurrency, high-throughput demands of their design workflow. Staff worked directly off shared storage rather than copying files locally — a workflow pattern that ruthlessly exposes any latency or bandwidth weakness in the underlying infrastructure. Large-format CAD and BIM assets, real-time multi-user collaboration, and uninterrupted application availability were non-negotiable requirements.",
          },
          {
            type: "p",
            text: "Macktez was engaged to architect a ground-up, zero-compromise infrastructure covering wired networking, wireless, compute, storage, and access control. Redundancy was a first-order design constraint at every layer: redundant power, controllers, chassis, and paths throughout. The goal wasn't incremental improvement — it was an environment that could absorb any single component failure without interruption.",
          },
        ],
      },
      {
        label: "Network Architecture",
        heading: "A fully redundant switching fabric",
        blocks: [
          { type: "h3", text: "Access Layer" },
          {
            type: "p",
            text: "Six Cisco Catalyst 9300 switches form the access layer as a single logical stack, providing nearly 300 ports across the office floor. The top stack member — a multi-gigabit variant — is dedicated exclusively to wireless access point uplinks, supporting 5 Gbps per AP uplink with headroom for future 10 Gbps capacity. The stack was specified with 8-port 10GbE expansion modules to provide significant uplink headroom beyond what the licensing tier requires.",
          },
          {
            type: "p",
            text: "Beyond data-plane stacking (each inter-switch link operates at 480 Gbps, dual cables per switch providing 960 Gbps of intra-stack capacity), Cisco StackPower connects the power supplies of all stacked switches into a single logical power domain. A switch member remains fully operational even if both of its local PSUs fail simultaneously, provided the aggregate pool has sufficient capacity from neighboring members. Every switch carries dual PSUs on independent 30-amp UPS circuits — defense-in-depth at the power layer.",
          },
          { type: "h3", text: "Core Layer" },
          {
            type: "p",
            text: "Dual Cisco Catalyst 9500 switches operate via StackWise Virtual — a unified control plane in which both chassis are simultaneously active and authoritative. Neither chassis can enter a split-brain state. From the perspective of any connected device, the two physical switches are a single logical entity.",
          },
          {
            type: "p",
            text: "This enables true cross-chassis EtherChannel: all 12 access-to-core uplinks function as a single bundle, providing 120 Gbps of aggregate bandwidth between layers with simultaneous throughput and link-level redundancy — and no spanning tree blocking. Both core switches carry redundant PSUs on independent circuits.",
          },
          { type: "h3", text: "Firewall & WAN" },
          {
            type: "p",
            text: "A SonicWall NSA 4700 pair operates in active/passive HA, selected specifically for its ability to sustain full deep-packet inspection at 10 Gbps line rate. The site runs a 10 Gbps primary WAN circuit with a 1 Gbps backup — the 4700 was sized to ensure the firewall is never the throughput ceiling at either circuit speed. Downstream connectivity from each firewall to the core uses four bonded 10GbE links (40 Gbps per firewall). Failover is automatic and stateful, typically converging within 2–3 seconds. Both units carry redundant power on independent circuits.",
          },
          { type: "h3", text: "Thermal Management" },
          {
            type: "p",
            text: "The server room presented a challenge common in built-out office environments: extending dedicated precision cooling would have required tens of thousands of dollars in additional ductwork and chiller capacity — not viable given the space geometry. Macktez specified and installed a dedicated HVAC unit within the server room with a hot-aisle/cold-aisle design: cold supply air directed at rack front faces, hot exhaust captured and ducted into an adjacent room. Stable operating temperatures have been maintained since deployment.",
          },
        ],
      },
      {
        label: "Wireless",
        heading: "Enterprise Wi-Fi 6 in a hostile RF environment",
        blocks: [
          {
            type: "p",
            text: "The firm's open floor plan — glass partitions, reflective surfaces, dense client populations in continuous motion — presents a genuinely hostile RF environment. Macktez over-specified radio capacity relative to theoretical client density so that practical performance remained top-tier even as conditions diverged from lab ideals.",
          },
          {
            type: "p",
            text: "A tiered Ruckus Wi-Fi 6 deployment was specified by per-zone density:",
          },
          {
            type: "ul",
            items: [
              "Ruckus R850 (8×8:8 MU-MIMO) in high-density open-plan design zones and primary desk clusters — the 8-stream radio capacity is critical for managing co-channel interference where many clients are simultaneously active in a small area",
              "Ruckus R650 / R610 in conference rooms and corridor coverage zones",
            ],
          },
          {
            type: "p",
            text: "All APs were cabled with dual Cat6a cables supporting 10GbE, ensuring neither cabling nor hardware becomes a bottleneck on a 5–10 year refresh cycle.",
          },
          {
            type: "p",
            text: "Rather than purchasing physical SmartZone controller appliances (~$10,000 per unit at time of deployment), Macktez virtualized redundant SmartZone instances as VMs on the Dell/VMware cluster — eliminating $20,000 in dedicated controller hardware without sacrificing any management or roaming capability. VM-level HA and vMotion portability are added benefits.",
          },
          {
            type: "p",
            text: "The controller-managed roaming model is central to wireless performance in this environment. Without a controller, roaming decisions are left to client devices — leading to the sticky client problem, where a laptop holds a degraded AP signal rather than handing off to a stronger one. SmartZone continuously monitors per-client RSSI and SNR across the AP fabric and issues directed disassociations, seamlessly transitioning clients to the optimal AP during active video calls or large file transfers. This orchestration is invisible to the user.",
          },
        ],
      },
      {
        label: "Storage",
        heading: "Dual-controller SAN with 80 Gbps aggregate bandwidth",
        blocks: [
          { type: "h3", text: "Primary SAN — Synology UC3200" },
          {
            type: "p",
            text: "Selected specifically for its dual active-active iSCSI controllers. Each controller has its own processor, RAM, and dedicated network interfaces. Path failover on controller failure occurs at the hardware level with nanosecond-range latency — no controller election, no convergence delay, no VM I/O timeout. Both controllers serve I/O continuously.",
          },
          {
            type: "p",
            text: "Disk configuration: 8 × 16TB SAS HDD with 4 × 1.92TB SAS SSD read/write cache tier. The cache tier handles small-block, high-IOPS workloads; spinning disk handles large sequential transfers. The tiering engine dynamically promotes and demotes data based on access patterns.",
          },
          {
            type: "p",
            text: "iSCSI connectivity: 8 discrete 10GbE interfaces, each with a unique IP address, all running Jumbo Frames (MTU 9000) — 80 Gbps of raw iSCSI bandwidth. Each ESXi host uses 3 dedicated 10GbE NICs for iSCSI, providing 9 host-side paths in aggregate, closely matched to the 8 appliance-side paths. The network fabric was deliberately over-provisioned relative to the storage appliance's maximum sustainable I/O throughput — the disk array is the ceiling, not the network. That is by design.",
          },
          {
            type: "p",
            text: "Three precision tuning optimizations were applied: ALUA MPIO for intelligent path selection that avoids unnecessary inter-controller traffic; Round-Robin IOPS threshold reduced from 1,000 to 1 (distributing I/O across all 8 active paths simultaneously rather than allowing a single path to carry disproportionate load); and EtherChannel hashing algorithms coordinated across Cisco switch and VMware vSwitch layers to ensure balanced traffic distribution across all physical links.",
          },
          { type: "h3", text: "Secondary SAN — Synology SA3200D" },
          {
            type: "p",
            text: "Active/passive dual-controller unit serving as backup target and extended services platform. The SA3200D was chosen over a second UC3200 for its support of native cloud replication to AWS S3, DNS services, and additional data management capabilities required by the firm's workflow. The trade-off — a few seconds of failover latency versus nanoseconds on the primary — is acceptable on the backup tier where brief interruptions are tolerable.",
          },
        ],
      },
      {
        label: "Compute",
        heading: "VMware cluster on Dell PowerEdge",
        blocks: [
          {
            type: "p",
            text: "Three Dell PowerEdge R650 servers form the compute cluster, each configured with 128GB RAM (half-filled slots reserved for future expansion) and 6 × 10GbE SFP+ NICs: 3 dedicated to iSCSI storage traffic, 3 bonded via EtherChannel for VM data traffic, vMotion, and management.",
          },
          {
            type: "p",
            text: "The cluster runs VMware ESXi 7 under Essentials Plus licensing — a one-time perpetual purchase of approximately $7,000 covering up to three hosts. This licensing tier includes vMotion and DRS, both central to the cluster's operational model.",
          },
          {
            type: "p",
            text: "vMotion enables live migration of running VMs between hosts with zero downtime — firmware updates, hardware replacements, and maintenance can be performed during business hours by migrating workloads off the target host first.",
          },
          {
            type: "p",
            text: "DRS monitors real-time CPU and RAM utilization and automatically rebalances workloads across hosts. The cluster is sized to sustain full workload capacity with any single host offline.",
          },
          {
            type: "p",
            text: "Anti-affinity rules prevent redundant service pairs from co-locating on the same host. Without them, DRS might place both domain controllers on the same physical chassis — defeating the redundancy entirely. Anti-affinity rules are defined for all critical service pairs, including domain controllers and the SmartZone controller pair.",
          },
          {
            type: "p",
            text: "The cluster supports Active Directory, file sharing, wireless controllers, cloud sync agents, a Network Video Recorder for the office camera system, print servers, and additional line-of-business VMs. All VM storage is centralized on the shared SAN — replicated and backed up, not siloed on individual host disks.",
          },
          {
            type: "p",
            text: "For context: hyperconverged platforms capable of meeting these throughput and redundancy requirements would have carried a price tag exceeding $100,000 per node.",
          },
          {
            type: "img",
            src: "/assets/pi-rack-v2.jpg",
            width: 898,
            height: 1600,
            alt: "Server rack at Pembrooke & Ives — Dell PowerEdge cluster, Cisco switching, and SAN storage",
          },
        ],
      },
      {
        label: "Access Control",
        heading: "Cloud-managed access control, provisioned through Entra",
        blocks: [
          {
            type: "p",
            text: "Macktez deployed PDK as the firm's physical access control platform, integrated directly with Microsoft Entra ID (Azure AD) as the identity source of truth.",
          },
          {
            type: "p",
            text: "The integration eliminates a manual provisioning step that is easy to miss and costly to overlook. When a new employee is added to Entra, PDK automatically creates their access control profile and sends them an invite to enroll their smartphone as a credential. No fob to issue, no IT ticket to file, no separate admin console to manage.",
          },
          {
            type: "p",
            text: "Staff use their phones at door readers via NFC and Bluetooth Low Energy — a faster and more reliable experience than physical credentials, and one that scales gracefully as the organization grows. Offboarding is equally automatic: removing a user from Entra revokes their building access without any additional action.",
          },
        ],
      },
    ],
    results: [
      "Zero infrastructure downtime since deployment",
      "5–7 Gbps real-world internal file transfer throughput — on par with local NVMe, delivered over shared SAN",
      "10 Gbps primary WAN with 1 Gbps backup — SonicWall NSA 4700 HA pair sustains full DPI at 10 Gbps line rate",
      "All 12 access-to-core uplinks active simultaneously — no spanning tree blocking",
      "80 Gbps aggregate iSCSI bandwidth from primary SAN to VMware cluster",
      "SmartZone virtualization eliminated $20,000 in dedicated controller hardware",
      "Full VMware cluster deployed under $7,000 in licensing costs",
      "PDK access control provisioned automatically through Entra — phones as credentials, zero manual issuance",
    ],
  },

  {
    slug: "collaborative-systems-for-audio",
    title: "Collaborative Systems for Audio",
    canonicalPath: "/case-studies/collaborative-systems-for-audio",
    subtitle:
      "Nearly two decades of infrastructure partnership with a leading NYC audio engineering firm",
    client: "Sonic Union",
    industry: "Audio Production",
    tags: ["Cloud & Infrastructure", "Identity", "Networking"],
    topics: [
      "Wasabi",
      "Morro Data",
      "Suite Studios",
      "JumpCloud",
      "SonicWall",
      "Synology",
      "Dell",
      "cloud storage",
      "ISP failover",
      "site-to-site VPN",
      "network monitoring",
      "audio post-production",
    ],
    summary:
      "Nearly two decades of infrastructure partnership with Sonic Union, a leading NYC audio post-production house — from onsite support before Sonic Union had internal IT, through two-studio high availability and a pandemic pivot to the cloud, to a co-managed, cloud-native production platform today.",
    seoTitle: "Collaborative Systems for Audio — Sonic Union | Macktez",
    seoDescription:
      "How Macktez has partnered with Sonic Union for nearly two decades — two-studio high availability with ISP failover, a pandemic pivot to Wasabi and JumpCloud, and a move onto a cloud-native file-streaming platform protecting 100TB+ of production data.",
    featuredImage: {
      src: "/assets/sonic-union-studio.jpg",
      alt: "Sonic Union audio post-production studio in New York City",
    },
    sections: [
      {
        label: "Background",
        heading: "Nearly two decades of technology that grew with the business",
        blocks: [
          {
            type: "p",
            text: "Sonic Union is a leading NYC audio post-production house, collaborating with major creative agencies and global brands out of two Manhattan studio locations — its original home at Union Square, and a second studio near Bryant Park added as the business grew. Macktez's relationship with Sonic Union dates back to 2009, beginning with hands-on onsite support before it had any dedicated internal IT staff. As Sonic Union eventually built its own technical team, Macktez's role evolved from doing the work directly to advising, architecting, and co-managing alongside them.",
          },
          {
            type: "p",
            text: "What's kept the partnership running for nearly twenty years isn't any single piece of technology — it's a habit of re-evaluating what Sonic Union actually needs as the business itself changes, rather than defending past decisions. Every major shift in this case study — a second studio, a pandemic, an industry-wide move to cloud-native production — prompted a real conversation about whether the existing infrastructure still fit the business, not just a technology refresh for its own sake.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Two studios, growing data, and a decade of accumulated technical debt",
        blocks: [
          {
            type: "p",
            text: "Sonic Union's original network was built for a single studio and a workflow centered entirely on local, on-premises file access — standard for audio post-production, where every project involves large, uncompressed audio files that need to move fast between edit bays. As the business grew, that original infrastructure aged out and was rebuilt after years of service. When Sonic Union opened its second studio near Bryant Park, the two locations needed to function as one operation — sharing files, sharing internet connectivity, and staying online even when one site had a problem.",
          },
          {
            type: "p",
            text: "Then the pandemic hit. A workflow built entirely around people being in the same room as the files stopped working the moment everyone had to work from home. Large audio files that used to move over the local network now needed to move — securely and quickly — to wherever an editor happened to be.",
          },
          {
            type: "p",
            text: "Underneath all of it, years of network growth without a corresponding investment in cleanup had left both studios' server rooms and cabling in a state that made troubleshooting slower and riskier than it needed to be.",
          },
          {
            type: "quote",
            text: "Macktez helped us understand hybrid cloud solutions — blob storage, deduplication, backup schemes. They had the expertise to implement it too.",
            attribution: "Sonic Union's co-founder",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Infrastructure that kept pace with how the business actually worked",
        blocks: [
          {
            type: "p",
            text: "Macktez's work with Sonic Union has never been a single project — it's been a continuous relationship that rebuilt Sonic Union's infrastructure in layers, each one driven by a real shift in how the business operated:",
          },
          {
            type: "ul",
            items: [
              "Replaced Sonic Union's original single-studio network after years of service, and rebuilt it again as its needs continued to grow",
              "When Sonic Union opened a second studio near Bryant Park, connected both locations with site-to-site VPN, then upgraded that connection to direct fiber once real-time collaboration between studios became part of daily production rather than an occasional need",
              "Configured dynamic routing so each studio's internet connection automatically serves as a live failover for the other — recognizing that once two studios depended on each other operationally, neither could afford to go down alone",
              "When the pandemic forced a shift to remote production overnight, deployed Wasabi cloud object storage so editors and engineers could access large production files from anywhere, and recommended and deployed JumpCloud as a cloud-based identity layer for a suddenly distributed team",
              "Built a new storage backbone using Morro Data CloudNAS technology bridging on-premises and cloud-cached storage, backed by new Dell and Synology server hardware sized to support the growing live dataset — now over 100TB — with both on-site and cloud backup redundancy",
              "Years later, as cloud-native production tools matured industry-wide, guided Sonic Union's move off Morro Data and onto Suite Studios' cloud-native file-streaming platform — and found ways to repurpose their existing on-premises hardware into the new architecture rather than writing it off",
              "Executed multiple full network overhauls over the years — replacing generations of SonicWall firewalls and switching hardware as both the business and the threat landscape changed — and untangled years of accumulated cabling neglect with dedicated rack cleanup, patch panel work, and full cable testing and certification",
              "Rolled out real-time network monitoring integrated with Macktez's Claude-based tooling, giving both Sonic Union and Macktez live visibility into network health and performance for proactive tuning rather than reactive troubleshooting",
            ],
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "High-availability infrastructure across two studio locations, with automatic ISP failover keeping both sites online through outages at either one",
      "Real-time file accessibility for a hybrid, remote-capable production team, now running on a modern cloud-native platform rather than legacy on-prem caching",
      "A high-availability storage architecture protecting more than 100TB of production data, combining cloud-native streaming with on-site and cloud-replicated backups",
      "A fully cabled, tested, and documented network and server room — replacing years of undocumented growth",
      "Live, AI-assisted network monitoring and reporting for ongoing tuning",
      "An unusually long-running partnership — nearly two decades — that evolved from hands-on onsite support into a trusted advisory role, with technology decisions consistently driven by where Sonic Union's business was headed next",
    ],
  },

  {
    slug: "little-island",
    title: "Little Island — Built from the Waterline Up",
    subtitle:
      "From a startup office in Chelsea to opening-day infrastructure for 1.7 million visitors",
    client: "Little Island",
    industry: "Parks & Recreation",
    tags: ["Networking", "Cloud & Infrastructure", "Identity"],
    summary:
      "Ground-up IT infrastructure for NYC's most ambitious new public park — network architecture, identity, Dante audio, Genetec, emergency systems, and 1.7M visitors in year one.",
    featuredImage: {
      src: "/assets/little-island-park-aerial.jpg",
      alt: "Little Island park, Hudson River, New York City",
    },
    seoTitle: "Little Island — Built from the Waterline Up | Macktez",
    seoDescription:
      "Macktez designed and deployed the full IT infrastructure for Little Island — ground-up network architecture, identity, emergency systems, and deep systems integration across a $260M+ public park build, delivered on time for 1.7 million visitors.",
    canonicalPath: "/case-studies/little-island",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Little Island — Built from the Waterline Up",
      "description":
        "Macktez designed and deployed the full IT infrastructure for Little Island, including ground-up network architecture, JumpCloud identity, Genetec access control, Dante audio networking, and emergency systems coordination with FDNY, NYPD, and the Hudson River Park Trust.",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "about": { "@type": "Organization", "name": "Little Island" },
      "keywords":
        "Little Island, network infrastructure, JumpCloud, Genetec, Dante audio, parks technology, NYC, emergency communications",
    },
    sections: [
      {
        label: "How It Started",
        heading: "A modest engagement at the edge of Manhattan",
        blocks: [
          {
            type: "p",
            text: "Little Island was one of New York's most anticipated civic projects — a $260M+ gift from Barry Diller and Diane von Furstenberg to the city, constructed on 132 concrete tulip-shaped piers off the Hudson shore in Chelsea. When the organization behind the park needed basic office support — subscription cleanup, workstation recommendations, occasional help desk — Macktez showed up. What followed was two years of deep technical involvement in one of the most complex ground-up IT builds in the city.",
          },
          {
            type: "p",
            text: "Macktez was engaged to set up a small office for the park's startup team near the construction site. A few questions about connecting that office across West Side Highway to the park led to a request to review the Wi-Fi specification the general contractor had provided. That review surfaced something significant: the underlying network had not been designed.",
          },
        ],
      },
      {
        label: "The Advisory Role",
        heading: "Working inside a $260M design/build",
        blocks: [
          {
            type: "p",
            text: "By 2019, Macktez was embedded as the technical advisor to the full project team — working alongside general contractor Hunter Roberts, Standard Architects, and IAC executive Jason Stewart, who served as Barry Diller's personal representative and project executive throughout the build.",
          },
          {
            type: "p",
            text: "Arup, the engineering firm that had developed the initial IT plans, had since departed the project. Their work included solid Wi-Fi coverage planning but left critical gaps: no network topology, no specified switching equipment, no environmental hardening for an outdoor marine installation, and — critically — no power redundancy. For a public space of this profile, that last omission wasn't a design preference. It was a public safety deficiency.",
          },
          {
            type: "p",
            text: "Macktez reviewed the full architectural and engineering plan set, identified every system with a network dependency, and rebuilt the IT infrastructure design from scratch. That review extended well beyond networking: fire alarm integration, security camera alignment, conduit routing for future cable pulls, and close coordination with MEP contractors on power and environmental controls. When problems were found — a fire alarm system that wasn't connected to the network, security cameras that weren't aligned correctly — Macktez escalated them, even when scope disputes made that uncomfortable.",
          },
          {
            type: "quote",
            text: "He identified the problems and the solutions.",
            attribution: "Jason Stewart, Project Executive, Little Island",
          },
          {
            type: "p",
            text: "Park Operations Manager Kathryn Lewis, who oversaw the park's technical operational readiness for opening, described the dependency simply: \"It turns out the internet is connected to everything.\"",
          },
          {
            type: "img",
            src: "/assets/little-island-network-install.jpg",
            width: 1600,
            height: 893,
            alt: "Network installation in progress at Little Island",
          },
        ],
      },
      {
        label: "What Was Connected",
        heading: "Every operational system ran through the network",
        blocks: [
          {
            type: "p",
            text: "Little Island is not a typical office. It operates 365 days a year with 24/7 staffing, two indoor/outdoor performance venues, public programming, food and beverage operations, and large-scale events. Nearly every operational system on the site had a network dependency — and Macktez was responsible for making sure each one worked.",
          },
          {
            type: "ul",
            items: [
              "Building management and MEP systems — environmental monitoring and controls across the site",
              "Theatrical lighting and production audio — including Dante-based audio networking across both performance venues",
              "Genetec — access control and IP camera infrastructure for the entire park",
              "Digital signage — public-facing displays and wayfinding",
              "Ticketing and point-of-sale — for events, programming, and food and beverage operations",
              "Radios and two-way communications — for operations and emergency coordination",
              "Irrigation controls — for the park's extensive horticultural systems",
              "Financial and HR platforms — integrated through the identity layer",
            ],
          },
          {
            type: "p",
            text: "Every system was tied together through a unified identity infrastructure via JumpCloud, providing centralized access control, auditing, and single sign-on across dozens of SAML and OIDC-integrated applications.",
          },
        ],
      },
      {
        label: "Emergency Systems",
        heading: "Designed for the worst day",
        blocks: [
          {
            type: "p",
            text: "For a public space on the Hudson River with large venue capacity, emergency infrastructure wasn't an afterthought — it was a design constraint baked in from the start. Macktez coordinated directly with FDNY, NYPD, and the Hudson River Park Trust to align Little Island's emergency communications infrastructure with external response procedures.",
          },
          {
            type: "p",
            text: "Working with the park's electrical team, Macktez identified excess conduit already installed in the site and designed a redundant power architecture with substantial battery backup capacity — enough to sustain emergency communications through a complete commercial power failure. Every aspect of the outdoor installation was engineered for the specific demands of a waterfront public space: waterproofing, temperature extremes, humidity, and marine exposure.",
          },
          {
            type: "p",
            text: "Getting connectivity to the site was its own challenge. After a year-long effort to secure Verizon service came up empty, Macktez leveraged a longstanding relationship with Pilot Fiber to arrange dedicated internet service for the park — a custom arrangement that required direct relationship capital and would not have been available through a standard procurement process.",
          },
          {
            type: "img",
            src: "/assets/little-island-equipment-install.jpg",
            width: 1600,
            height: 1161,
            alt: "Equipment installation detail at Little Island",
          },
        ],
      },
      {
        label: "Identity & Access",
        heading: "From 7 employees to 200+ in under a year",
        blocks: [
          {
            type: "p",
            text: "When Macktez first engaged with Little Island, the organization had fewer than ten people. By opening day, the park had grown to more than 200 employees and contractors spanning operations, programming, food and beverage, and technical roles — all of whom needed to be onboarded, credentialed, and managed at scale.",
          },
          {
            type: "p",
            text: "Macktez designed the identity infrastructure to absorb that growth. JumpCloud was deployed as the organizational directory — a single source of truth for every employee, with integrations to every business application and secure onboarding and offboarding for a team growing faster than most organizations can manage. Nearly 100 portable workstations were deployed with remote onboarding capability, so new hires could be brought online without hands-on setup, regardless of where they started.",
          },
        ],
      },
    ],
    results: [
      "10G fully redundant network with 40G backbone — live for opening day, May 21, 2021",
      "Cisco Catalyst 9000 Series switching for wireless systems throughout the venue",
      "Cisco Industrial Ethernet Series for exposed outdoor and marine-environment locations",
      "JumpCloud identity infrastructure — SAML and OIDC integrations across dozens of applications",
      "Genetec access control and IP camera system",
      "Dante audio networking for two performance venues",
      "Emergency power architecture coordinated with FDNY, NYPD, and Hudson River Park Trust",
      "Pilot Fiber dedicated connectivity — negotiated custom arrangement",
      "~100 workstations deployed with secure remote onboarding",
      "Full systems integration: BMS, MEP, theatrical, signage, ticketing, POS, irrigation, radios, financial, and HR",
      "1.7M+ visitors in the park's first year",
    ],
  },

  {
    slug: "makerbot-overhaul",
    title: "Untangling the Spaghetti Monster",
    subtitle:
      "A ground-up infrastructure overhaul for a leader in industrial 3D printing — embedded for months, rebuilt over a single holiday",
    client: "MakerBot",
    industry: "Product Design & Manufacturing",
    tags: ["Networking", "Cloud & Infrastructure"],
    summary:
      "Months embedded inside MakerBot learning their infrastructure before overhauling it over a single holiday closure — 40 VMs rebuilt, 400% throughput increase, complete redundancy, and offsite backup replication, all executed without disrupting production operations.",
    seoTitle: "Untangling the Spaghetti Monster — Infrastructure Overhaul | Macktez",
    seoDescription:
      "How Macktez spent months embedded inside MakerBot learning their infrastructure before overhauling it over a single holiday closure — 40 VMs rebuilt, 400% throughput increase, complete redundancy, and offsite backup replication, all executed without disrupting production operations.",
    canonicalPath: "/case-studies/makerbot-overhaul",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline":
        "Untangling the Spaghetti Monster — A Ground-Up Infrastructure Overhaul for MakerBot",
      "description":
        "How Macktez spent months embedded inside MakerBot learning their infrastructure before overhauling it over a single holiday closure — 40 VMs rebuilt, 400% throughput increase, complete redundancy, and offsite backup replication.",
      "url": "https://macktez.com/case-studies/makerbot-overhaul",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
    },
    featuredImage: {
      src: "/assets/mkb-v2.jpg",
      alt: "Server room after infrastructure overhaul",
    },
    featuredImagePair: {
      before: {
        src: "/assets/manufacturing-before.jpg",
        alt: "MakerBot server room before infrastructure overhaul",
      },
      after: {
        src: "/assets/manufacturing-after.jpg",
        alt: "MakerBot server room after infrastructure overhaul",
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "MakerBot — a leader in industrial 3D printing and additive manufacturing",
                  ],
                  [
                    "Problem",
                    "A production environment running 40 virtual machines across a dozen departments — on undocumented infrastructure that nobody fully understood — that had grown too complex and too critical to fix without first spending months learning exactly how it worked.",
                  ],
                  [
                    "Services",
                    "Infrastructure assessment and full documentation · embedded on-site consulting · VMware and storage architecture · server procurement and build · network remediation · backup strategy with offsite replication · cable management · vendor coordination (VMware, Dell, Cisco, APC)",
                  ],
                  [
                    "Platforms",
                    "VMware ESXi · Cisco Catalyst switching · APC Symmetra UPS · Dell server hardware · shared SAN storage",
                  ],
                  [
                    "Outcome",
                    "400% throughput increase. Complete system redundancy. Offsite backup replication. New servers built from the ground up. A fully documented environment — and a server room no one is ashamed of anymore.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Discovery",
        heading: "A spaghetti monster in the server room",
        blocks: [
          {
            type: "p",
            text: "The COO was at a holiday party when the network went down. An IT staff member reached him and suggested he come take a look at the server room. He had never been in it before.",
          },
          {
            type: "p",
            text: "What he found was a wall of tangled cables, unlabeled patch panels, and equipment arranged in no particular order. A spaghetti monster. He had always trusted that the infrastructure was broadly under control. Standing in that room, he understood that it wasn't — and that it hadn't been for some time.",
          },
          {
            type: "p",
            text: "The network issues that had been blamed on the ISP for years were not ISP issues. VLAN assignments were undocumented. Ports in the patch panels weren't connected to anything. 10G interfaces capable of driving the throughput the business needed sat running at a fraction of their capacity. There was no system redundancy. Any hardware failure would take down services across the company with no clear path to recovery.",
          },
          {
            type: "p",
            text: "He called us.",
          },
          {
            type: "quote",
            text: "Ports in the patch panels weren't connected to the network. Troubleshooting required extensive guesswork about network configurations.",
            attribution: "Senior IT Support Engineer",
          },
        ],
      },
      {
        label: "The Scope",
        heading: "Forty virtual machines and no map",
        blocks: [
          {
            type: "p",
            text: "The scale of the environment was significant — and the absence of documentation made it more daunting than it needed to be.",
          },
          {
            type: "p",
            text: "The infrastructure ran multiple VMware ESXi hosts with shared SAN storage, supporting 40 virtual machines that spanned every function in the business: mechanical engineering, electrical engineering, software development, materials science, marketing, sales, customer support, finance, and legal. The network ran on Cisco Catalyst switches. Power was backed by APC Symmetra UPS units — but with no redundancy designed into the system, a UPS failure was a production failure.",
          },
          {
            type: "p",
            text: "Every department depended on this environment. None of them knew how it actually worked. Neither, in a meaningful sense, did anyone else — because it had never been properly documented.",
          },
          {
            type: "p",
            text: "Before we could fix it, we had to learn it.",
          },
        ],
      },
      {
        label: "The Approach",
        heading: "Embedded for months before touching a thing",
        blocks: [
          {
            type: "p",
            text: "We didn't begin by making changes. We began by becoming the people who understood the environment better than anyone else.",
          },
          {
            type: "p",
            text: "For months, we worked embedded within their team — on-site, inside the business, alongside their IT staff. We traced every cable. We documented every virtual machine, every VLAN, every storage volume, every dependency. We talked to the vendors: VMware, Dell, Cisco, APC — working through configurations and capabilities with the people who built the systems, making sure our understanding was correct before we committed it to paper.",
          },
          {
            type: "p",
            text: "By the time we were done with the assessment, we were the authoritative experts on their infrastructure. We knew it better than it had ever been known. And with that foundation, we could plan a complete overhaul — not one that required guesswork, but one that was designed down to the individual cable run.",
          },
        ],
      },
      {
        label: "The Execution",
        heading: "Taking Humpty Dumpty apart over Christmas",
        blocks: [
          {
            type: "p",
            text: "The execution window was the Christmas holiday closure. With the entire company away, we had a clear runway to do the work without disruption to production operations.",
          },
          {
            type: "p",
            text: "We took the environment completely apart and rebuilt it from scratch.",
          },
          {
            type: "p",
            text: "New servers, procured and configured to spec. The virtual machine estate rebuilt on a properly architected VMware foundation with redundancy designed in from the start. Storage arrays reconfigured. A new backup strategy implemented — with offsite replication to a nearby datacenter, so that a failure on-site was no longer also a data loss event. Network remediation throughout: VLAN assignments rationalized, documented, and consistent. Cisco Catalyst switching properly configured to actually use the 10G capacity the hardware had always been capable of.",
          },
          {
            type: "p",
            text: "And the cables — every run organized, dressed with braided sleeves and velcro, labeled, and traced. Not for aesthetics, but because an environment you can read is an environment you can troubleshoot, and an environment you can troubleshoot is one that never has to be a mystery again.",
          },
          {
            type: "img",
            src: "/assets/mkb-v2.jpg",
            width: 834,
            height: 1600,
            alt: "MakerBot server room after infrastructure overhaul",
          },
        ],
      },
    ],
    results: [
      "400% throughput increase — immediate, on the first morning back after the holiday",
      "Full utilization of 10G switching that had been running at a fraction of its capacity for years",
      "Complete system redundancy — no single points of failure across compute, storage, or power",
      "Offsite backup replication to a nearby datacenter — a site failure is no longer a data loss event",
      "New server hardware, built and configured from the ground up",
      "40 virtual machines rebuilt on a properly architected VMware foundation",
      "Consistent, documented VLAN assignments throughout the environment",
      "Full infrastructure documentation — for the first time in the company's history",
      "A cable plant anyone on the team can read and trace",
      "\"Now set up for the future, and no longer ashamed to show people the server room.\" — COO",
      "\"The immediate thing I noticed was I was a hell of a lot less anxious in there.\" — Senior IT Support Engineer",
    ],
  },

  {
    slug: "ultimaker-conferencing",
    title: "Two Rooms, Built to Meet",
    canonicalPath: "/case-studies/ultimaker-conferencing",
    subtitle:
      "Board room and all-hands conferencing for UltiMaker's new headquarters",
    client: "UltiMaker",
    industry: "Product Design & Manufacturing",
    tags: ["Networking", "Cloud & Infrastructure", "Buildout"],
    topics: [
      "Zoom Rooms",
      "Logitech Rally",
      "Logitech Swytch",
      "AV",
      "conferencing",
      "buildout",
      "cabling",
      "board room",
      "all-hands",
      "55 Water Street",
    ],
    summary:
      "Board room and all-hands Zoom Room conferencing for UltiMaker's new 55 Water Street headquarters — two turnkey systems designed, procured, configured, and installed end to end by Macktez, including the full physical buildout behind the walls.",
    seoTitle: "Two Rooms, Built to Meet — UltiMaker Conferencing | Macktez",
    seoDescription:
      "Board room and all-hands Zoom Room conferencing for UltiMaker's new 55 Water Street headquarters — designed, procured, and installed end to end by Macktez, including cabling, power, and the full physical buildout.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Two Rooms, Built to Meet — UltiMaker Conferencing",
      "description":
        "Board room and all-hands Zoom Room conferencing for UltiMaker's new 55 Water Street headquarters — designed, procured, and installed end to end by Macktez, including the full physical buildout.",
      "url": "https://macktez.com/case-studies/ultimaker-conferencing",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Conference room AV design and buildout with Zoom Rooms",
      },
    },
    featuredImage: {
      src: "/assets/ultimaker-conferencing.jpg",
      alt: "UltiMaker's board room Zoom Room — dual displays, table microphones, and a tabletop controller — at its new 55 Water Street headquarters",
      position: "center 20%",
    },
    sections: [
      {
        label: "Background",
        heading: "A new headquarters, and two rooms that had to just work",
        blocks: [
          {
            type: "p",
            text: "UltiMaker — the desktop 3D-printing company formed by the merger of MakerBot and Ultimaker — was consolidating into a new headquarters at 55 Water Street. A new office meant new spaces to bring online, and two of them, the board room and the all-hands area, carried more weight than the rest. These are the rooms where leadership meets, where the whole company gathers, and where a dropped call or a fumbled screen-share happens in front of the largest possible audience.",
          },
          {
            type: "p",
            text: "Macktez had already been MakerBot's infrastructure partner for years — running server management and higher-tier support alongside the internal IT team. As part of the larger merger and relocation effort, UltiMaker asked Macktez to design, procure, and install turnkey conferencing systems for both rooms.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Two rooms, two very different jobs",
        blocks: [
          {
            type: "p",
            text: "The two spaces had genuinely different requirements, and getting each right meant designing for how the room is actually used rather than dropping the same kit into both.",
          },
          {
            type: "p",
            text: "The board room needed to feel effortless and executive: clean sightlines, reliable audio around a conference table, and a system anyone could walk up to and drive without training. The all-hands space had a harder problem — presenting to a full room from a podium while also carrying remote attendees, which calls for multiple camera angles, a presenter's view, and simple laptop presenting for whoever is running the meeting.",
          },
          {
            type: "p",
            text: "Both had to standardize on the platform the company already lived in — Zoom Rooms — and both had to be built into a raw new office, which meant the work wasn't just configuring equipment. It meant cabling, power, in-wall and in-ceiling runs, and the physical buildout behind the scenes that makes an integrated room look simple once it's done.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Purpose-built systems for each room",
        blocks: [
          {
            type: "p",
            text: "Macktez designed, procured, configured, and installed a complete conferencing system for each space, tuned to how that room would be used:",
          },
          {
            type: "ul",
            items: [
              "Board Room Zoom Room — dual displays with a Logitech Rally Plus system (camera, speakers, and table-mounted microphones), an iPad controller for one-touch meeting control, and a dedicated Zoom Rooms computer, so leadership can start a call or share a screen without a second thought",
              "All-Hands Conferencing — a podium-based Zoom Room with dual displays, two Logitech Rally cameras (one covering the room, one at the podium) for flexible framing during presentations, and a Logitech Swytch so any presenter can plug in a laptop and present locally while remote attendees stay connected",
              "Full physical buildout — in-wall and in-ceiling cable runs, power and network drops behind the displays and at the podium and camera locations, and mounted displays, cameras, and controllers, delivered as finished, integrated rooms rather than a pile of equipment",
            ],
          },
          {
            type: "p",
            text: "Installing into a brand-new office meant working around the realities of new construction — displays delivered on the building's schedule, furniture arriving late, and power that wasn't live until partway through the install. Macktez adapted on site as conditions changed: when the board room table arrived with one fewer microphone cutout than planned, the team reconfigured the audio layout on the spot and relocated the controller to the credenza rather than hold up the room. The result in each case was a working, tested system — a laptop presentation and a live Zoom call verified before handoff.",
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "Two turnkey Zoom Rooms — the board room and the all-hands space — designed, built, and tested for UltiMaker's new 55 Water Street headquarters",
      "Executive-grade simplicity in the board room: one-touch meeting control anyone can use",
      "Flexible multi-camera presentation in the all-hands space, with easy laptop presenting for any presenter",
      "Complete physical buildout — cabling, power, mounting, and configuration — handled end to end as part of the larger merger and relocation",
      "Rooms that hold up where it matters most: leadership meetings and full-company gatherings",
    ],
  },

  {
    slug: "hypebeast-flagship",
    title: "Seven Floors, One System",
    canonicalPath: "/case-studies/hypebeast-flagship",
    subtitle:
      "A ground-up technology buildout for Hypebeast's flagship New York headquarters",
    client: "Hypebeast / HBX",
    industry: "Retail & Hospitality",
    tags: ["Networking", "Buildout", "Security", "Cloud & Infrastructure"],
    topics: [
      "low-voltage cabling",
      "structured cabling",
      "network buildout",
      "PDK access control",
      "video surveillance",
      "Sonos",
      "Google Meet",
      "VoIP",
      "point-to-point wireless",
      "elevator integration",
      "retail",
      "41 Division Street",
    ],
    summary:
      "A ground-up, ~$330,000 technology buildout for Hypebeast's first U.S. flagship — seven floors and ~25,000 square feet at 41 Division Street, unifying cabling, network, audio, 113-camera surveillance, 27-door access control, conferencing, phone, and internet, delivered as one system and opened on time.",
    seoTitle: "Seven Floors, One System — Hypebeast Flagship Buildout | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez designed and delivered the entire low-voltage technology backbone for Hypebeast's seven-floor NYC flagship at 41 Division Street — network, cabling, audio, surveillance, access control, conferencing, phone, and internet — on a live construction schedule.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Seven Floors, One System — Hypebeast Flagship Buildout",
      "description":
        "How Macktez designed and delivered the entire low-voltage technology backbone for Hypebeast's seven-floor NYC flagship at 41 Division Street — network, cabling, audio, surveillance, access control, conferencing, phone, and internet.",
      "url": "https://macktez.com/case-studies/hypebeast-flagship",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Ground-up low-voltage technology buildout for a flagship retail building",
      },
    },
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    sections: [
      {
        label: "Background",
        heading: "A flagship building, wired from the ground up",
        blocks: [
          {
            type: "p",
            text: "In 2022, Hypebeast — the global streetwear and contemporary-culture platform — opened its first U.S. flagship building at 41 Division Street on the edge of Manhattan's Chinatown. Spanning seven floors and roughly 25,000 square feet, the building brought Hypebeast's entire ecosystem under one roof: the largest HBX retail flagship in the world across the lower floors, the first U.S. Hypebeans café, a third-floor gaming lounge and innovation space, event and exhibition areas, a rooftop, a photo studio, and the company's North American headquarters above.",
          },
          {
            type: "p",
            text: "A building doing that many jobs at once needed a technology backbone to match. Macktez designed and delivered that backbone end to end — network, low-voltage cabling, audio, conferencing, video surveillance, access control with elevator integration, phone service, and internet delivery — a roughly $330,000 program of work installed into an exceptionally high-finish space where nothing could be seen that wasn't meant to be.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "One building, many buildings’ worth of systems",
        blocks: [
          {
            type: "p",
            text: "Retail, café, gaming lounge, events, photo studio, and corporate headquarters each carry their own technical demands, and 41 Division Street had to serve all of them at once, across seven floors plus a cellar and roof. A shopper on the ground floor, a gamer on three, an editor at a desk on five, and a rooftop event all drew on the same underlying infrastructure — segmented so each function got what it needed without interfering with the others.",
          },
          {
            type: "p",
            text: "Two constraints raised the difficulty. First, the finish level: this was a design-forward flagship where the architecture was part of the brand, so the technology had to disappear into the walls, ceilings, and millwork — hundreds of cable runs and more than a hundred devices placed precisely and hidden cleanly. Second, the coordination: a buildout on this scale meant working shoulder to shoulder with architects, the general contractor, the elevator vendor, the fire-alarm vendor, and other trades on a live construction schedule with a hard opening date — where low-voltage cabling has to be in the walls before they close and every device location is committed early.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Every system, designed and delivered as one",
        blocks: [
          {
            type: "p",
            text: "Macktez served as the single technology partner across the entire buildout — designing each system, coordinating with the construction and vendor teams, procuring equipment, and installing and configuring it floor by floor. Because every system rode on one network and cabling backbone that Macktez designed, they were built to work together rather than as a stack of disconnected vendors' products. The delivered systems included:",
          },
          {
            type: "ul",
            items: [
              "Structured low-voltage cabling — the Cat6/Cat6A backbone under everything else, growing from 204 to 240 runs as the design evolved, with fiber between the main distribution frame and both intermediate frames, all terminated, tested, labeled, and documented",
              "Building-wide network — a 40G fiber backbone between floors, redundant firewall and core switches with dual-WAN failover, 10G to the desks that needed it, roughly 18 wireless access points for dense retail and office coverage, and full battery-backup protection at every rack",
              "Sonos audio — around 20 zoned speakers across the retail and common spaces on their own dedicated VLAN, centrally controlled, licensed for commercial streaming",
              "Video surveillance — 113 cameras covering retail floors, cellar, stairwells, egress, sidewalk, roof, and elevator, recording to an 80TB NVR, with footage timestamp-matched to door-access events so security review is fast and precise",
              "Access control with elevator integration — PDK-based controlled access on 27 doors with mobile and keypad credentials, an elevator module governing access from retail floors to private floors, and NYC fire-code cutover relays tied in with the fire-alarm vendor",
              "Conferencing — a Google Meet room system (Logitech Tap) for the executive conference room, matched to the tools Hypebeast already ran",
              "Phone — a flexible VoIP system with room phones in retail, reception, the CEO's office, studio, and lounges, plus softphones for retail staff",
              "Internet delivery — with fiber to the building cost-prohibitive, Macktez sourced and coordinated point-to-point wireless service with primary and backup circuits and designed AWS-based DNS failover for inbound services",
            ],
          },
          {
            type: "img",
            src: "/assets/hypebeast-topology-v2.png",
            width: 2000,
            height: 1267,
            alt: "Network topology map for Hypebeast's 41 Division Street flagship — dual-WAN internet feeding a redundant firewall and core switches, a 40G fiber backbone to per-floor switches, and wireless access points across seven floors",
            caption: "The 41 Division Street network topology — every system riding one designed backbone.",
          },
          {
            type: "p",
            text: "Two moments defined how Macktez ran the project. When the gaming lounge's bandwidth needs turned out to approach 900 Mbps at peak, Macktez proactively upgraded the firewall and primary circuit rather than let the floor bottleneck on opening day. And when Cisco marked the specified core switches end-of-life mid-project — with replacement gear carrying a six-month lead time that would have blown past the opening — Macktez loaned Hypebeast a complete temporary network at no additional cost to open on schedule, then swapped in the permanent switching once it arrived. The building opened on time.",
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "A complete, unified technology backbone for a seven-floor, ~25,000-square-foot flagship — cabling, network, audio, surveillance, access control, conferencing, phone, and internet, delivered as roughly a $330,000 program of work",
      "Segmented infrastructure serving retail, café, gaming, event, studio, and headquarters functions simultaneously without interference",
      "113-camera surveillance with 30-day retention, integrated with 27-door access control and elevator floor governance, plus NYC fire-code compliance",
      "Technology installed cleanly into a high-finish, design-forward space, coordinated with architects, the GC, and the elevator and fire-alarm vendors on a live schedule",
      "An on-time opening held even through a mid-project Cisco end-of-life disruption, via a no-cost loaner network",
      "A single accountable partner across every low-voltage system, rather than a patchwork of separate vendors",
    ],
  },

  {
    slug: "directory-modernization",
    title: "Directory Modernization",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    canonicalPath: "/case-studies/directory-modernization",
    subtitle:
      "Scalable identity and device infrastructure for a fast-growing global beauty brand",
    client: "A global beauty and personal care brand",
    industry: "Retail & Hospitality",
    tags: ["Identity", "Cloud & Infrastructure", "MDM"],
    topics: [
      "JumpCloud",
      "NinjaOne",
      "Active Directory",
      "SSO",
      "MFA",
      "device management",
      "identity",
      "hardware refresh",
      "BYOD",
    ],
    summary:
      "Identity and device modernization for a fast-growing global beauty and personal care brand — replacing a single on-premises domain controller with JumpCloud, a co-managed NinjaOne device fleet, a full Windows hardware refresh, and SSO across business-critical applications, spanning more than 750 users and 570 workstations.",
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    sections: [
      {
        label: "The Challenge",
        heading: "Identity sprawl and aging infrastructure ahead of rapid growth",
        blocks: [
          {
            type: "p",
            text: "A fast-growing global beauty and personal care brand had scaled its headcount and international footprint faster than its IT foundation could support. The company ran on a single on-premises Active Directory domain controller with no redundancy, a hybrid-join configuration Microsoft had already begun deprecating, and inconsistent security group hygiene. Devices only received policy updates when connected to VPN, and there was no unified platform for managing them.",
          },
          {
            type: "p",
            text: "With a workforce split across corporate offices, remote staff, and a significant BYOD population, the company needed a clear-eyed assessment of its options — and an execution partner who could carry out a multi-year modernization without slowing down a business moving this fast.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "JumpCloud as the identity backbone, with a phased device and application rollout",
        blocks: [
          {
            type: "p",
            text: "Macktez began with a focused, fixed-scope discovery engagement: reviewing the existing Active Directory and sync architecture, auditing SaaS applications for single sign-on readiness, meeting with stakeholders across the organization, and evaluating the security implications of the company's BYOD culture. That discovery produced a clear recommendation — modernize the identity layer around JumpCloud, a cloud-based, cross-platform directory built for a hybrid Mac and Windows environment.",
          },
          {
            type: "p",
            text: "From there, the engagement expanded in phases over several years:",
          },
          {
            type: "ul",
            items: [
              "Deployed JumpCloud as the organization's central identity provider, federating Microsoft and other business-critical platforms under a single directory",
              "Stood up a co-managed NinjaOne tenant for remote monitoring and device management, deployed via JumpCloud across the Mac and Windows fleet, with the internal IT team trained to run it directly",
              "Executed a full Windows hardware refresh — evaluating the existing fleet, shipping pre-configured standardized replacement workstations directly to employees in phased waves, and refurbishing or retiring machines based on age and condition",
              "Audited 21 business-critical SaaS applications — including Adobe, Dropbox, Greenhouse, NetSuite, SAP Concur, and Zoom — for native single sign-on support, mapping a largely cost-free integration path for most of them",
              "Ran an initial assessment of the internal help desk's ticketing workflows, SLAs, and self-service resources, then reallocated the remaining budget toward higher-impact priorities within the engagement",
            ],
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "A single cloud-based directory now governs identity and device policy, replacing a single point of failure on-prem domain controller",
      "Identity management now spans more than 750 users, with over 570 workstations under active device management",
      "Multi-factor authentication enforced across all JumpCloud-federated access points",
      "A modernized, standardized Windows fleet with a clear replacement and retirement policy going forward",
      "An ongoing, multi-year co-managed relationship, with Macktez available for new integrations, ad hoc support, and periodic account and device audits",
    ],
  },

  {
    slug: "law-firm-essentials-rollout",
    title: "Double the Workforce, Not the IT Team",
    canonicalPath: "/case-studies/law-firm-essentials-rollout",
    subtitle:
      "A co-managed Essentials rollout for a national law firm — unified identity, modern device management, and centralized security",
    client: "A national law firm",
    industry: "Professional Services",
    tags: ["Identity", "Security", "MDM"],
    topics: [
      "JumpCloud",
      "1Password",
      "Huntress",
      "NinjaOne",
      "MDR",
      "EDR",
      "MFA",
      "SSO",
      "device management",
      "Management Essentials",
      "onboarding",
      "co-managed IT",
      "Microsoft 365",
      "SCIM",
    ],
    summary:
      "A national law firm needed a scalable way to manage a growing workforce, modernize IT support, and streamline onboarding. Over a six-month co-managed rollout, Macktez unified identity, device management, and security on our Management Essentials subscription — so the firm could nearly double its workforce without adding IT headcount.",
    seoTitle: "Scaling a Law Firm Without Scaling IT | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez helped a national law firm scale with a co-managed Essentials rollout — JumpCloud identity, NinjaOne device management, Huntress MDR, and 1Password across 141 identities and 117 workstations.",
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Double the Workforce, Not the IT Team",
      "description":
        "How Macktez helped a national law firm scale with a co-managed Essentials rollout — JumpCloud identity, NinjaOne device management, Huntress MDR, and 1Password across 141 identities and 117 workstations.",
      "url": "https://macktez.com/case-studies/law-firm-essentials-rollout",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Co-managed identity, device management, and security rollout",
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "A national law firm"],
                  ["Industry", "Legal — national practice"],
                  [
                    "Problem",
                    "A rapidly growing firm needed to modernize identity, device management, and security — and streamline onboarding — without expanding its IT team.",
                  ],
                  [
                    "Approach",
                    "A six-month, co-managed rollout of Macktez Management Essentials: JumpCloud identity, NinjaOne device management, Huntress MDR, and 1Password, designed and overseen by Macktez while the firm's IT team enrolled devices and users.",
                  ],
                  [
                    "Outcome",
                    "141 managed identities, 117 monitored workstations, 116 protected endpoints, and 88 password vaults — with onboarding reduced to a low-touch, wave-based process and internal IT running the platforms day to day.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Growth outpacing manual IT",
        blocks: [
          {
            type: "p",
            text: "A national law firm needed a robust, scalable way to manage a growing workforce, modernize IT support, and streamline employee onboarding. Like many rapidly expanding organizations, it faced several operational hurdles at once.",
          },
          {
            type: "ul",
            items: [
              "Manual onboarding: setting up new hardware and provisioning software access was a slow, hands-on process for the IT team.",
              "Poor device visibility: The firm needed better oversight and policy enforcement across its fleet of Windows and Mac workstations.",
              "Security vulnerabilities: more frequent, more sophisticated phishing attempts made a unified multi-factor authentication (MFA) strategy and stronger endpoint security urgent.",
              "Too many passwords: because every application required its own login, employees juggled multiple accounts — causing password fatigue and security gaps.",
            ],
          },
        ],
      },
      {
        label: "The Approach",
        heading: "A change of direction after the assessment",
        blocks: [
          {
            type: "p",
            text: "The firm has a skilled internal IT team and recognized the need to update its identity and device management tools ahead of growth. The firm initially reached out to Macktez to plan enrollment in Microsoft's Entra and Intune platforms.",
          },
          {
            type: "p",
            text: "But the work we did together during our Initial Assessment revealed a broader set of IT questions that we believed would be more comprehensively addressed with different tools. The firm's decision-makers were open to a change of direction after we reviewed the options together, and asked Macktez to design and oversee a six-month enrollment process.",
          },
          {
            type: "p",
            text: "Our scope of work included:",
          },
          {
            type: "ul",
            items: [
              "Setting up and configuring co-managed tenants in JumpCloud (identity and device management), 1Password (password management), Huntress (endpoint and managed detection and response — EDR/MDR), and NinjaOne (additional device monitoring).",
              "Federating identities with the firm's existing single sign-on (SSO)-enabled services, working with third-party vendors where needed — including Microsoft 365, Zoom, 1Password, Airtable, Bonusly, Adobe, and Podium.",
              "Setting up and configuring MDR security policies.",
              "Configuring and enforcing JumpCloud MFA.",
              "Documentation, demonstrations, and co-working sessions so the firm's IT team could confidently enroll employees and devices in the new tools.",
              "Weekly progress meetings.",
            ],
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Four pillars of modern IT infrastructure",
        blocks: [
          {
            type: "p",
            text: "Macktez designed a solution centered on our Management Essentials subscription and enrollment in 1Password, resting on four pillars.",
          },
          { type: "h3", text: "1. Unified identity" },
          {
            type: "p",
            text: "Implementing JumpCloud gave the firm centralized user authentication and less login friction. A key federation with Microsoft 365 lets people use one set of credentials for their workstations and productivity suite, with additional SSO integrations across other critical applications.",
          },
          { type: "h3", text: "2. Robust device management" },
          {
            type: "p",
            text: "To manage a hybrid fleet, Macktez implemented NinjaOne for remote monitoring and management (RMM). The rollout standardized security settings — 15-minute lock screens, BitLocker encryption — and automated deployment of common applications and extensions.",
          },
          { type: "h3", text: "3. Proactive security" },
          {
            type: "p",
            text: "To defend against sophisticated threats, Macktez deployed Huntress across all endpoints for managed detection and response (MDR), so suspicious activity is caught and remediated by a 24/7 Security Operations Center (SOC).",
          },
          { type: "h3", text: "4. Centralized password management" },
          {
            type: "p",
            text: "1Password was deployed firm-wide to secure credentials that fall outside SSO. Macktez configured a System for Cross-domain Identity Management (SCIM) bridge to automate user provisioning from JumpCloud to 1Password, and helped the firm set up granular vaults to support least-privilege access based on each user's needs.",
          },
        ],
      },
      {
        label: "By the Numbers",
        heading: "Six months, one platform",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Managed identities", "141"],
                  ["Monitored workstations", "117"],
                  ["Protected endpoints", "116"],
                  ["Password vaults", "88"],
                  ["Timeline", "Six months, all objectives met"],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "Ongoing Consulting",
        heading: "A foundation to keep building on",
        blocks: [
          {
            type: "p",
            text: "Building on this foundation, we are exploring phase 2 initiatives — including HR system integrations to further automate onboarding, and device trust to further secure access to sensitive company data.",
          },
          {
            type: "p",
            text: "Macktez excels at co-management and augmentation for skilled IT teams that need additional expertise and resources. The firm's internal team runs the new systems day to day, with Macktez available remotely for new integrations and features.",
          },
        ],
      },
    ],
    resultsHeading: "What changed.",
    results: [
      "Enhanced security: unified MFA and federated identities across Microsoft and other platforms significantly reduced the firm's attack surface. It has already proven itself — when an employee was lured into downloading malicious code, Huntress detected and isolated the device within minutes, and the firm's IT team completed remediation the same day.",
      "Streamlined operations: new-hire onboarding is now low touch, rolled out in a phased, wave-based process that brought the whole organization online over several months without disrupting casework — and set up for zero-touch configuration in phase 2, initiated directly by HR.",
      "Improved user experience: employees get a consistent login across their desktop and web applications.",
      "High enrollment success: the firm's internal IT team steadily enrolled every company device and migrated users to the new identity platform, with instructions and support from Macktez.",
      "Co-managed IT platforms: internal IT continues to administer the systems day to day, with Macktez assisting remotely on new integrations and features.",
    ],
  },

  {
    slug: "high-impact-small-team",
    title: "High-Impact Support for a High-Stakes Small Team",
    canonicalPath: "/case-studies/high-impact-small-team",
    subtitle:
      "Ten years of executive-level IT partnership with the Jerome L. Greene Foundation",
    client: "Jerome L. Greene Foundation",
    industry: "Nonprofits",
    tags: ["Identity", "Security", "MDM", "Cloud & Infrastructure"],
    topics: [
      "JumpCloud",
      "Addigy",
      "MDM",
      "SSO",
      "1Password",
      "DMARC",
      "Foundation Source",
      "FileMaker",
      "cybersecurity awareness",
      "AI governance",
      "cloud-native",
      "nonprofit",
      "identity",
      "backup",
    ],
    summary:
      "Ten years of executive-level IT partnership with the Jerome L. Greene Foundation — full device management, JumpCloud single sign-on, cloud-native infrastructure, and a complete security suite, run on behalf of a lean, non-technical team stewarding a very large endowment.",
    seoTitle:
      "High-Impact Support for a High-Stakes Small Team — Jerome L. Greene Foundation | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez runs a complete modern IT operation for the Jerome L. Greene Foundation — MDM, JumpCloud SSO, cloud-native infrastructure, and a full security suite for a small team responsible for a very large endowment.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "High-Impact Support for a High-Stakes Small Team — Jerome L. Greene Foundation",
      "description":
        "How Macktez runs a complete modern IT operation for the Jerome L. Greene Foundation — MDM, JumpCloud SSO, cloud-native infrastructure, and a full security suite for a small team responsible for a very large endowment.",
      "url": "https://macktez.com/case-studies/high-impact-small-team",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Managed IT, identity, and security for a small foundation",
      },
    },
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    sections: [
      {
        label: "Background",
        heading: "A small team, a big mission, and no room for error",
        blocks: [
          {
            type: "p",
            text: "The Jerome L. Greene Foundation invests in the arts, education, medicine, and social justice across New York City — funding institutions from Columbia and Juilliard to reproductive healthcare and community organizations. It does that work with a deliberately small staff and a very large endowment.",
          },
          {
            type: "p",
            text: "That combination — a lean, non-technical team responsible for stewarding hundreds of millions of dollars — defines everything about how the Foundation's technology has to work. There is no internal IT department. There is no room for a security lapse. And there is very little tolerance for technology that demands attention rather than quietly enabling the mission.",
          },
          {
            type: "p",
            text: "Macktez has been the Foundation's technology partner for roughly ten years. What began with periodic onsite visits has grown into a complete, modern IT operation — full device management, single sign-on, cloud-native infrastructure, a comprehensive security suite, and a support relationship that reaches all the way up to personal, direct support for the Foundation's leadership.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "High stakes, low tolerance for friction",
        blocks: [
          {
            type: "p",
            text: "Small foundations occupy a genuinely difficult position. They control significant assets, which makes them attractive targets, but they rarely have the in-house technical staff that a bank or a large enterprise would bring to bear on the same risk. Every dollar spent on overhead is a dollar not spent on the mission, so the technology has to be lean. And when the people using it aren't especially technical, the security can't depend on users making sophisticated judgment calls under pressure.",
          },
          {
            type: "p",
            text: "For the Jerome L. Greene Foundation, that meant the infrastructure had to be essentially bulletproof by design — secure by default, simple to use, and backed by a partner who could act as the Foundation's IT department, security team, and technology advisor all at once, without ever asking the staff to become any of those things themselves.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "A complete, modern IT operation — run for them, not by them",
        blocks: [
          {
            type: "p",
            text: "Over ten years, Macktez has built and now runs the Foundation's entire technology stack, evolving it from onsite, hands-on support into a fully modern, cloud-native operation:",
          },
          {
            type: "ul",
            items: [
              "Full device management across the Foundation's Mac and iPad fleet via JumpCloud and Addigy MDM, so every device is enrolled, encrypted, patched, and manageable remotely",
              "Single sign-on and centralized identity through JumpCloud, unifying Google Workspace, Microsoft 365, 1Password, Zoom, DocuSign, Salesforce, and AWS under one governed login",
              "A comprehensive security suite — centralized password management, DMARC email protection, managed backups for cloud data, and ongoing cybersecurity awareness training for staff",
              "Cloud-native file infrastructure replacing on-premises servers, with third-party backup protecting against data loss even when a platform provider fails",
              "Personal, executive-level support — including direct support for Foundation leadership. When an executive has a security concern over a holiday weekend, someone answers the call",
            ],
          },
        ],
      },
      {
        label: "Knowing When Not to Be the Vendor",
        heading: "The best recommendation is sometimes someone else",
        blocks: [
          {
            type: "p",
            text: "Years ago, Macktez built the Foundation a custom FileMaker database to manage grant and grantee information. It served the Foundation well for a long time. But as the Foundation's needs grew more specialized, Macktez recommended something that not every technology partner would: that the Foundation leave the custom system behind and move to Foundation Source, a platform purpose-built for philanthropic grant management. Macktez helped manage that transition and fully decommissioned the old FileMaker system once the move was complete.",
          },
          {
            type: "p",
            text: "That willingness to recommend a better-fit specialist — even when it means handing work to someone else — has become a defining feature of the relationship. When the Foundation needed specialized web development, Macktez advised on selecting an outside developer with the right expertise rather than forcing a fit. The guiding principle is simple: the Foundation should always have the best tool and the best specialist for the job, whether or not that's Macktez.",
          },
        ],
      },
      {
        label: "AI, Adopted With Intention",
        heading: "New capability, on the Foundation’s terms",
        blocks: [
          {
            type: "p",
            text: "Most recently, Macktez guided the Foundation through a structured assessment of how AI could strengthen its work without compromising the trust, privacy, and confidentiality at its core. The approach was governance-first: keeping all data internal and out of external training models, treating every AI output as a first draft requiring human review, and respecting existing permission structures.",
          },
          {
            type: "p",
            text: "Just as important was matching the ambition to the Foundation's real capacity. The team wasn't positioned to take on advanced, autonomous AI agents — and Macktez didn't push them there. Instead, the work focused on what the Foundation could genuinely absorb and benefit from: AI-assisted meeting notes, drafting help for routine correspondence, and querying internal document collections. Practical capability, matched to the organization, rather than capability for its own sake.",
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "A complete modern IT operation — MDM, SSO, cloud-native infrastructure, and a full security suite — run on the Foundation's behalf, with no internal IT staff required",
      "Enterprise-grade security posture protecting a small team responsible for a very large endowment",
      "A decade-long partnership that scaled from onsite visits to executive-level, always-available support",
      "Technology decisions consistently made in the Foundation's interest — including recommending outside specialists when they were the better fit",
      "AI capability adopted deliberately and safely, matched to the organization's real capacity",
    ],
  },

  {
    slug: "ai-with-intention",
    title: "AI, Adopted with Intention",
    canonicalPath: "/case-studies/ai-with-intention",
    subtitle:
      "How a mission-driven foundation brought Generative AI into its workflows without compromising trust",
    client: "Jerome L. Greene Foundation",
    industry: "Nonprofits",
    tags: ["AI", "Security", "Cloud & Infrastructure"],
    topics: [
      "Generative AI",
      "AI governance",
      "Gemini",
      "Google Workspace",
      "Microsoft 365",
      "Zoom AI",
      "human-in-the-loop",
      "data privacy",
      "nonprofit",
      "AI readiness",
    ],
    summary:
      "How the Jerome L. Greene Foundation brought Generative AI into its workflows the way it approaches philanthropy — governance first, real problems targeted, and impact measured before anything scaled. A structured assessment, three guardrails, and four measured pilots for a lean team stewarding sensitive information.",
    seoTitle: "AI, Adopted with Intention — Jerome L. Greene Foundation | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez helped the Jerome L. Greene Foundation adopt Generative AI without compromising trust — a governance-first framework, data kept internal, human-in-the-loop review, and four measured pilots delivering real time savings.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "AI, Adopted with Intention — Jerome L. Greene Foundation",
      "description":
        "How Macktez helped the Jerome L. Greene Foundation adopt Generative AI without compromising trust — a governance-first framework, data kept internal, human-in-the-loop review, and four measured pilots delivering real time savings.",
      "url": "https://macktez.com/case-studies/ai-with-intention",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Thing",
        "name": "Governance-first Generative AI adoption for a nonprofit foundation",
      },
    },
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    sections: [
      {
        label: "Background",
        heading: "A foundation that treats technology the way it treats philanthropy",
        blocks: [
          {
            type: "p",
            text: "The Jerome L. Greene Foundation invests in the arts, education, medicine, and social justice across New York City. It does that work with a lean team, a large endowment, and a great deal of sensitive information about grantees, donors, and community partners.",
          },
          {
            type: "p",
            text: "By 2025, AI was everywhere the Foundation looked — in the tools it already used, in the headlines, and in every sector it touched. Staff had begun experimenting on their own. But for an organization built on trust, the question was never simply whether to use AI. It was how to use it in a way that honored the confidence placed in the Foundation. Working with Macktez, its longtime technology partner, JLGF set out to answer that question deliberately.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Scattered experimentation, and no guardrails",
        blocks: [
          {
            type: "p",
            text: "The Foundation's day-to-day work runs on meetings, email, and document-heavy report review — exactly the kind of work Generative AI can accelerate. But the team's early experimentation was scattershot: unsupervised, inconsistent, and without any way to tell what was actually helping.",
          },
          {
            type: "p",
            text: "What was missing wasn't enthusiasm. It was structure. The Foundation needed a clear set of \"safe to use\" guidelines, repeatable workflows for its highest-value tasks, a way to measure time actually saved, and confidence that its tools were configured correctly for privacy and internal governance. Above all, it needed an approach that fit a small, non-technical team stewarding significant assets — one that added capability without adding risk.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Governance first, tools second",
        blocks: [
          {
            type: "p",
            text: "Macktez began with a structured two-week assessment, mapping the Foundation's existing ecosystem — Google Workspace, Microsoft 365, Salesforce, DocuSign — to find where AI capability already existed, where the gaps were, and where the highest-impact, lowest-risk opportunities sat. Before any tool was piloted, we established the guardrails that would govern all AI use:",
          },
          {
            type: "ul",
            items: [
              "Data stays internal. Tools including Gemini in Google Workspace were configured so that organizational inputs are never used to train external models. Grantee reports, board materials, and donor information remain entirely within the Foundation's control.",
              "Humans remain the authors. Every AI output is treated as a first draft requiring human review. Nothing is finalized or sent externally without deliberate staff approval. AI assists; it does not decide.",
              "Access is earned, not assumed. AI features respect existing permission structures — meeting summaries, for example, are stored in secure internal locations available only to those who would ordinarily have access.",
            ],
          },
          {
            type: "p",
            text: "With the guardrails in place, we launched four pilots — each chosen because it solved a documented, daily friction, not because it made an impressive demo:",
          },
          {
            type: "ul",
            items: [
              "Meeting intelligence — Zoom AI generating consistent meeting summaries and action items, cutting the time staff spend reconstructing who committed to what",
              "Drafting assistance — Gemini producing first drafts of routine correspondence like grant acknowledgments and reminders, giving staff a clean starting point to refine in their own voice",
              "Knowledge retrieval — Gemini's \"Ask this folder\" letting staff query curated internal document collections and get answers sourced directly from the Foundation's own materials",
              "Desk research and synthesis — AI-assisted research across the Foundation's core funding areas, supporting richer thematic analysis without hours of manual work",
            ],
          },
          {
            type: "p",
            text: "Each pilot was measured against clear benchmarks over a 30-day window, with a simple expectation: demonstrate concrete value before expanding. To support adoption, Macktez provided a plain-language \"Do/Don't\" guide, per-workflow cheat sheets, and a lightweight tracker for time saved — then built a 90-day roadmap for measured, intentional growth rather than a sprint toward adoption.",
          },
        ],
      },
    ],
    resultsHeading: "What we delivered.",
    results: [
      "Meaningful time savings across recurring work — roughly 4–6 hours per week each on meeting follow-ups, routine email drafting, and multi-document review",
      "Greater consistency — standardized notes, drafts, and summaries reduced rework and improved internal alignment",
      "Strong governance — usage stayed internal and controlled through folder permissions, internal sharing rules, and human-in-the-loop review",
      "A repeatable framework — the Foundation finished the engagement with workflows, templates, and guides it could keep using immediately",
      "A roadmap matched to real capacity — advanced autonomous agents were deliberately left off the table in favor of what the team could genuinely absorb and benefit from",
    ],
  },

  {
    slug: "met-gala",
    title: "The Night $4.6 Million Moved Over Wi-Fi",
    subtitle:
      "Event network infrastructure for a high-profile non-profit's gala at the Metropolitan Museum of Art",
    client: "a high-profile non-profit",
    industry: "Nonprofits",
    tags: ["Networking"],
    summary:
      "Event network for a high-profile non-profit's gala at the Metropolitan Museum of Art — Ruckus high-density wireless, FortiGate firewall, and on-site radio coordination supporting 500 devices across two spaces. $4.59 million raised in a single evening; zero network issues.",
    seoTitle: "The Night $4.6 Million Moved Over Wi-Fi | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez engineered the event network for a high-profile non-profit's gala at the Metropolitan Museum of Art — Ruckus high-density wireless, FortiGate firewall, and on-site radio coordination supporting 500 devices and $4.59 million raised in a single evening.",
    canonicalPath: "/case-studies/met-gala",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline":
        "The Night $4.6 Million Moved Over Wi-Fi — Event Network for a high-profile non-profit's gala at the Metropolitan Museum of Art",
      "description":
        "How Macktez engineered the event network for a high-profile non-profit's gala at the Metropolitan Museum of Art — Ruckus high-density wireless, FortiGate firewall, and on-site radio coordination supporting 500 devices and $4.59 million raised in a single evening.",
      "url": "https://macktez.com/case-studies/met-gala",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Event",
        "name": "A High-Profile Non-Profit Gala",
        "organizer": { "@type": "Organization", "name": "a high-profile non-profit" },
        "location": {
          "@type": "Place",
          "name": "The Metropolitan Museum of Art, Temple of Dendur",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "New York City",
            "addressRegion": "NY",
            "addressCountry": "US",
          },
        },
      },
    },
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "a high-profile non-profit bringing clean water to communities in developing countries",
                  ],
                  [
                    "Venue",
                    "The Metropolitan Museum of Art, Temple of Dendur in the Sackler Wing, New York City",
                  ],
                  [
                    "Problem",
                    "Support hundreds of simultaneous high-stakes devices — guest iPads used for donations and real-time projection systems — through a dense urban RF environment, during a single high-pressure evening where wireless failure meant fundraising failure.",
                  ],
                  [
                    "Services",
                    "RF environment assessment · equipment specification and procurement · on-premises wireless controller deployment · firewall and traffic management · event production coordination · on-site network operations",
                  ],
                  [
                    "Platforms",
                    "Ruckus wireless APs and ZoneDirector controller · FortiGate firewall · Metropolitan Museum fiber WAN uplink",
                  ],
                  [
                    "Outcome",
                    "500-device-capacity network, zero issues on the night. 400 guests raised $4.59 million for clean water in Cambodia and Ethiopia — the network was invisible, as intended.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Event",
        heading: "A fundraising gala where the network was the mechanism",
        blocks: [
          {
            type: "p",
            text: "Inside the Metropolitan Museum of Art, in the glass-ceilinged Sackler Wing, sits the Temple of Dendur — a 2,000-year-old Egyptian temple surrounded by a reflecting pool, framing the New York night sky above. It is one of the most extraordinary rooms available for an event in the world.",
          },
          {
            type: "p",
            text: "A high-profile non-profit chose it as the backdrop for their annual black-tie gala: 400 guests, Christie's auctioneers on stage, real-time donation totals projected onto the museum walls, the room ringed with neon-yellow jerry cans — the organization's signature symbol of the water crisis. An iPad sat at every table, loaded with a personal story about a specific family in Ethiopia, waiting to unlock at the right moment in the program.",
          },
          {
            type: "p",
            text: "At the center of all of it was a wireless network. The iPads guests used to make donations. The live-projection system tracking cumulative giving in real time. The simultaneous unlock that asked every table to give at once. Every one of those systems needed reliable, high-capacity wireless to function — and in a room where an iPad freezing mid-program would have interrupted a fundraising mechanism raising millions in minutes, \"probably fine\" was not an acceptable standard.",
          },
          {
            type: "p",
            text: "The evening raised $4.59 million — enough to bring clean water to 153,000 people.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Dense city, dense room, no margin for error",
        blocks: [
          {
            type: "p",
            text: "Event wireless in New York City is harder than event wireless almost anywhere else. The RF environment is saturated — neighboring networks, adjacent events, and the interference profile of a dense urban building all compete for the same spectrum. Add several hundred devices associating simultaneously in a single space, with traffic that spikes sharply at specific program moments, and the failure modes become very specific and very predictable. A network sized for average load falls over at peak. Access points that work during setup behave differently when the room fills. Traffic that looks fine during testing looks completely different when five hundred devices are contending at once.",
          },
          {
            type: "p",
            text: "Planning for those moments — not average conditions — was the whole exercise.",
          },
        ],
      },
      {
        label: "The Approach",
        heading: "Spec for the spike, test before the night",
        blocks: [
          {
            type: "p",
            text: "The first step was traffic modeling: estimating the number of concurrent devices, the traffic profile at peak moments in the program, and the density requirements for the two primary spaces — the Temple of Dendur and the Great Hall. With that baseline, we specified equipment sized for the peak, not the average, with a spare access point on standby.",
          },
          {
            type: "p",
            text: "The wireless layer was built on top-of-the-line Ruckus access points, chosen specifically for their performance in high-density environments, managed by an on-premises Ruckus controller. Running a local controller rather than relying on cloud management meant the network had no external dependencies during the event — no cloud path, no external service that could go wrong at the wrong moment. A FortiGate firewall sat at the perimeter, handling traffic shaping and security. The WAN uplink ran over fiber from the Metropolitan Museum's own network infrastructure — a clean, high-capacity connection already in place at the venue.",
          },
          {
            type: "p",
            text: "Two access points covered the Temple of Dendur. Two covered the Great Hall. The fifth was on the shelf, ready to swap in if anything failed. Everything was tested before the guests arrived. By the time doors opened, every failure mode we could anticipate had already been found and resolved.",
          },
        ],
      },
      {
        label: "On the Night",
        heading: "Radios on, ready for anything",
        blocks: [
          {
            type: "p",
            text: "For the duration of the event, one team member was on-site and on radio with the event production team — aware of every moment in the schedule that would drive a traffic spike, coordinating in real time with the people running the program. A second team member was standing by off-site in case of emergency.",
          },
          {
            type: "p",
            text: "Nothing required them. The issues had been found during testing, not during the gala. The network ran the evening without intervention, absorbed the spikes, and stayed invisible — which is exactly what a good event network is supposed to do.",
          },
        ],
      },
      {
        label: "The Partnership",
        heading: "Equipment donated as an in-kind contribution",
        blocks: [
          {
            type: "p",
            text: "Macktez loaned the full equipment stack — FortiGate firewall, Ruckus wireless controller, access points, and switching — to the non-profit at no cost, as an in-kind donation valued at over $11,000. The professional fees covered the design, coordination, installation, and on-site operations. The hardware was our contribution to the mission.",
          },
          {
            type: "p",
            text: "The work we do at events like this — making sure a wireless network behaves for a few hours inside a museum — is in direct service of something much larger. The funds raised that evening will bring clean water to communities in Cambodia and Ethiopia for generations. We were glad to play a small part in making that night work.",
          },
        ],
      },
    ],
    results: [
      "500-device-capacity wireless network across two spaces in one of the most RF-dense cities in the world",
      "Enterprise-grade Ruckus access points and on-premises controller — no cloud dependencies during the event",
      "FortiGate firewall with traffic shaping at the perimeter",
      "Fiber WAN uplink via the Metropolitan Museum's own infrastructure",
      "Full traffic modeling and peak-load planning before equipment was specified",
      "On-site network operations and radio coordination with event production throughout the evening",
      "Zero issues on the night — all failure modes resolved during pre-event testing",
      "Full equipment stack donated as an in-kind contribution at no cost to the non-profit",
    ],
  },

  {
    slug: "windows-tools-for-a-mac-design-firm",
    title: "A GPU in Azure, a Mac on Every Desk",
    subtitle:
      "GPU-accelerated cloud desktops for Bluebeam and Revit, without a single Windows machine on premises",
    client: "Architecture and design professional corporation, Mac-first, distributed workforce",
    industry: "Architecture & Design",
    tags: ["Cloud & Infrastructure", "Microsoft"],
    summary:
      "GPU-accelerated Azure Virtual Desktop for a Mac-only architecture firm — Bluebeam Revu and Autodesk Revit at full resolution and full speed, with Entra ID-native identity, Terraform IaC, and no Windows hardware on premises.",
    seoTitle: "A GPU in Azure, a Mac on Every Desk | Macktez",
    seoDescription:
      "How Macktez deployed GPU-accelerated Azure Virtual Desktop for a Mac-only architecture firm — enabling Bluebeam and Revit over H.264/AVC hardware-encoded RDP, with Entra ID-native identity and full Terraform IaC, serving 20+ users with no Windows hardware on premises.",
    canonicalPath: "/case-studies/windows-tools-for-a-mac-design-firm",
    featuredImage: {
      src: "/assets/MN_image.png",
      alt: "Mac workstation with Azure Virtual Desktop open in window",
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline":
        "A GPU in Azure, a Mac on Every Desk — GPU-Accelerated Azure Virtual Desktop for Bluebeam and Revit",
      "description":
        "How Macktez deployed GPU-accelerated Azure Virtual Desktop for a Mac-only architecture firm — enabling Bluebeam and Revit over H.264/AVC hardware-encoded RDP, with Entra ID-native identity and full Terraform IaC, serving 20+ users with no Windows hardware on premises.",
      "url": "https://macktez.com/case-studies/windows-tools-for-a-mac-design-firm",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Service",
        "name": "Azure Virtual Desktop Deployment",
        "provider": {
          "@type": "Organization",
          "name": "Macktez",
          "url": "https://macktez.com",
        },
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "Architecture and design professional corporation, Mac-first, distributed workforce",
                  ],
                  [
                    "Problem",
                    "A design practice running entirely on Macs that depends on Windows-only CAD and BIM tools — Bluebeam Revu and Autodesk Revit — with no Windows hardware and no appetite to buy or manage it.",
                  ],
                  [
                    "Services",
                    "Azure Virtual Desktop design and deployment · GPU VM selection and configuration · RDP codec optimization for hardware-accelerated remote graphics · Entra ID-native identity and RBAC · infrastructure as code (Terraform) · user training and documentation · ongoing platform management",
                  ],
                  [
                    "Platforms",
                    "Azure Virtual Desktop · Azure GPU VMs · Microsoft Entra ID · Terraform · Bluebeam Revu · Autodesk Revit",
                  ],
                  [
                    "Outcome",
                    "A production environment serving 20+ users across a distributed team, with up to five concurrent sessions running Bluebeam and Revit at full resolution and full speed — no Windows hardware, no VPN, no on-premises footprint.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Brief",
        heading: "Immersive design tools on a Mac-only team",
        blocks: [
          {
            type: "p",
            text: "MN Design Professional Corporation runs on Macs. That's a deliberate choice — the hardware is excellent, the workflow fits — but it creates a hard wall when the practice's most important software doesn't run on macOS. Bluebeam Revu and Autodesk Revit are Windows-only. The firm needed both, reliably, across a distributed team that had no Windows machines and no plans to acquire any.",
          },
          {
            type: "p",
            text: "The ask was straightforward: give Mac users access to Windows CAD tools. The engineering to make that work well — especially at the resolution and performance these applications demand — was more involved.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "The wrong answer is just a VM",
        blocks: [
          {
            type: "p",
            text: "Cloud desktops for CAD workflows aren't a standard compute problem. Bluebeam and Revit are GPU-accelerated applications. They render large drawings, navigate complex 3D models, and push significant pixel counts. A general-purpose cloud VM — the kind optimized for CPU and memory — will run these applications, but slowly and at degraded quality. Users notice immediately when a remote session feels like a remote session, and that experience gap is usually the reason a VDI deployment fails.",
          },
          {
            type: "p",
            text: "The right answer was a GPU-equipped virtual machine, sized specifically for visualization workloads with dedicated GPU memory. But specifying the right hardware is only half the problem. The other half is getting the GPU's output to the user over a Remote Desktop connection at full fidelity.",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "Making the GPU work over RDP",
        blocks: [
          {
            type: "p",
            text: "By default, Remote Desktop Protocol renders the display on the CPU and streams a compressed result. Even on a GPU-equipped VM, standard RDP configuration doesn't use the GPU for session rendering — to actually leverage the hardware, you have to explicitly configure the RDP stack to use it.",
          },
          {
            type: "p",
            text: "This meant enabling hardware-accelerated video encoding in the host pool's RDP properties and configuring the session to use H.264/AVC encoding with the GPU encoder engaged. Once in place, the session host used the GPU not just to run the application but to encode the display stream — producing a sharper, higher-framerate result with significantly less network overhead than a CPU-encoded session. For CAD work, the difference is tangible: panning a Revit model or marking up a large PDF in Bluebeam became smooth rather than stuttered. Users on Mac clients, connecting through the Windows App, were getting a GPU-rendered Windows desktop at full quality.",
          },
          {
            type: "p",
            text: "The host pool was also configured for Entra ID SSO directly in the RDP properties, so users authenticate once and land in the desktop without a second credential prompt.",
          },
        ],
      },
      {
        label: "The Build",
        heading: "Built as code",
        blocks: [
          {
            type: "p",
            text: "The entire environment was deployed from a Terraform codebase, with remote state managed in Terraform Cloud. The hub network, subnets, security groups, session hosts, RBAC assignments, backup configuration, and AVD host pool are all in source control and reproducible from scratch — nothing was clicked together in the Azure portal.",
          },
          {
            type: "p",
            text: "The network is segmented across five subnets including a fully isolated zone with outbound internet access blocked at the security group level. Infrastructure access is whitelisted to known IPs only. Session hosts are placed on the private subnet, configured automatically on first boot, and registered to the host pool without manual intervention. VM backups run on a regular schedule with both daily and weekly retention.",
          },
          {
            type: "p",
            text: "Everything is tagged Managed-By: Terraform. Changes go through the codebase, not the console.",
          },
        ],
      },
      {
        label: "Identity & Access",
        heading: "Entra ID-native, no domain required",
        blocks: [
          {
            type: "p",
            text: "There are no domain controllers in this design. No hybrid join, no on-premises Active Directory dependency. Session hosts are joined directly to Entra ID, and access is managed through two security groups — one for users and one for administrators — with role-based access scoped appropriately to each.",
          },
          {
            type: "p",
            text: "Users connect from the Windows App on their Mac, authenticate via their Microsoft account, and land on a Windows 11 desktop. From the user's side: no domain, no VPN, no Windows hardware — just a fast Windows desktop in a window on their Mac.",
          },
        ],
      },
      {
        label: "In Production",
        heading: "From proof of concept to 20+ users",
        blocks: [
          {
            type: "p",
            text: "The deployment started as a deliberate minimum: a single GPU VM shared by a small group of users, running at pay-as-you-go rates while the firm evaluated whether cloud desktops could actually replace local Windows workstations for this kind of work. The goal was to validate the workflow before committing to it.",
          },
          {
            type: "p",
            text: "It validated. The combination of GPU-accelerated session encoding and a properly sized host pool delivered an experience the team found genuinely workable for daily use. The environment was expanded to support the full distributed team — now 20+ users, with the pool sized for up to five concurrent sessions and depth-first load balancing to keep resource usage efficient. Azure reservation pricing replaced pay-as-you-go rates, reducing VM costs substantially from the evaluation period.",
          },
          {
            type: "p",
            text: "The Terraform codebase made the expansion straightforward: adding session hosts is a variable change and a terraform apply, not a manual provisioning process.",
          },
        ],
      },
    ],
    results: [
      "Production environment for 20+ users across a distributed, Mac-only team",
      "GPU-accelerated Windows 11 cloud desktops running Bluebeam Revu and Revit at full resolution",
      "H.264/AVC hardware-encoded RDP — GPU used for both application rendering and display stream encoding",
      "Entra ID-native identity — no domain controller, no VPN, no Windows hardware on premises",
      "Full Terraform codebase with remote state — the entire environment in source control, reproducible from scratch",
      "Segmented network with isolated security zone and IP-whitelisted infrastructure access",
      "Automated backup and recovery configuration",
      "User and admin documentation; training for all users",
    ],
  },

  {
    slug: "fost-fest",
    title: "A Festival Campus, Fully Connected",
    subtitle: "Festival-grade network engineering for FoST Fest at Snug Harbor",
    client: "Future of Storytelling (FoST)",
    industry: "Arts & Culture",
    tags: ["Networking"],
    summary:
      "A complete temporary event network for FoST Fest at Snug Harbor — spanning twelve landmarked buildings and a Great Lawn tent across an 83-acre campus, thousands of concurrent high-bandwidth VR/AR devices, self-healing redundant topology, and zero dead air across the full run.",
    seoTitle: "FoST Fest — Festival-Grade Event Network | Macktez",
    seoDescription:
      "How Macktez engineered a temporary, resilient festival network for FoST Fest at Snug Harbor Cultural Center — FortiGate firewall, Cisco switching, Ruckus wireless, a 600-foot outdoor Ethernet relay to the Great Lawn tent, and a self-healing topology that absorbed live failures invisibly.",
    canonicalPath: "/case-studies/fost-fest",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "A Festival Campus, Fully Connected — Network Engineering for FoST Fest at Snug Harbor",
      "description":
        "A temporary multi-day festival network for FoST Fest at Snug Harbor Cultural Center — spanning a dozen landmarked buildings and open-field tents, thousands of concurrent VR/AR devices, and a self-healing redundant topology that absorbed live failures invisibly.",
      "url": "https://macktez.com/case-studies/fost-fest",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Event",
        "name": "FoST Fest",
        "organizer": { "@type": "Organization", "name": "Future of Storytelling" },
        "location": {
          "@type": "Place",
          "name": "Snug Harbor Cultural Center & Botanical Garden",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Staten Island",
            "addressRegion": "NY",
            "addressCountry": "US",
          },
        },
      },
    },
    featuredImage: {
      src: "/assets/Fost_Pelican.JPG",
      alt: "Sealed Pelican case PoE relay deployed at the edge of the Great Lawn at Snug Harbor",
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  ["Client", "Future of Storytelling (FoST), an immersive media festival"],
                  ["Venue", "Snug Harbor Cultural Center & Botanical Garden, Staten Island — 83 acres"],
                  [
                    "Problem",
                    "Knit a dozen historic, landmarked buildings and open-field tents, spread across an 83-acre campus, into one resilient network capable of carrying thousands of high-bandwidth VR/AR devices, for a temporary, multi-day public event with zero tolerance for downtime.",
                  ],
                  [
                    "Services",
                    "Temporary event network design · fiber and structured cabling · outdoor wireless · wireless point-to-point backhaul · self-healing redundant topology · on-site NOC and production coordination",
                  ],
                  [
                    "Platforms",
                    "FortiGate firewall · Cisco switching · Ruckus wireless APs and controller · UniFi point-to-point · dedicated fiber WAN with cellular LTE failover",
                  ],
                  [
                    "Outcome",
                    "Thousands of concurrent high-data devices supported across the campus. Live link failures absorbed invisibly by the self-healing design. The festival ran without a hitch.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Brief",
        heading: "Immersive storytelling on a campus that was never wired for it",
        bg: "bg-[#F4F2EF]",
        blocks: [
          {
            type: "p",
            text: "FoST Fest is the public face of the Future of Storytelling — a festival built around the newest VR, AR, AI, mixed-reality, haptics, and interactive-film work, with more than 100 exhibits and dozens of world and US premieres. After its New York debut, it moved to Snug Harbor on Staten Island: a sprawling cultural campus of nineteenth-century landmark buildings, gardens, and open lawns.",
          },
          {
            type: "p",
            text: "That setting is the entire problem. Immersive storytelling is bandwidth-hungry — headsets, sensors, live-streamed installations, and packed auditoriums — and it was scattered across a dozen buildings and tents that were never wired to talk to each other. Everything had to be temporary, everything had to come down cleanly afterward, and for the run of the festival it could not go down. A frozen headset or a dead access point in front of a thousand people isn't a support ticket; it's the show breaking.",
          },
          {
            type: "p",
            text: "Every building on that map needed a network drop. Every tent, every outdoor installation, every auditorium filling with people running bandwidth-hungry headsets. The topology below is what we built to cover it.",
          },
          {
            type: "img",
            src: "/assets/FoST_Network_Topology_generic.svg",
            width: 1900,
            height: 2620,
            alt: "FoST Fest campus network topology diagram showing all buildings, backhaul links, fiber runs, wireless point-to-point shots, and the Pelican case relay to the Great Lawn tent",
          },
        ],
      },
      {
        label: "The Build",
        heading: "Weeks in the basements and attics",
        blocks: [
          {
            type: "img",
            src: "/assets/FoST_Buildign_G_Cellar.JPG",
            width: 1600,
            height: 1200,
            alt: "Basement of Building G at Snug Harbor during the FoST Fest network installation",
            size: "small",
          },
          {
            type: "p",
            text: "The festival ran for days. The network took weeks.",
          },
          {
            type: "p",
            text: "Long before the first attendee arrived, we were in the basements and attics of the Snug Harbor buildings pulling fiber and Ethernet, setting switches and access points, and landing data drops at every installation across the campus. A dedicated fiber gigabit circuit was brought in as the clean uplink — no contention with house systems, no surprises — with a cellular LTE connection standing by as an automatic backup if the primary ever dropped. From a central MDF in the main building, fiber runs reached out to the outlying halls, and each major building was given its own PoE distribution switch and its own cluster of access points sized to what that space actually needed.",
          },
          {
            type: "p",
            text: "None of that placement was guesswork. Every exhibit had different data demands — some just needed Wi-Fi for a handful of headsets, others were pulling live high-bandwidth feeds — so the build was planned drop by drop, in close coordination with each installation and with event production. By show time everyone on the team carried a radio.",
          },
        ],
      },
      {
        label: "Field Engineering",
        heading: "Reaching the tent",
        blocks: [
          {
            type: "p",
            text: "The hardest single drop wasn't a building. It was the middle of a field.",
          },
          {
            type: "p",
            text: "The main stage and a cluster of demos lived under a large tent on the Great Lawn — hundreds of feet from the nearest building, across open grass, with no infrastructure of any kind. We reached it the way you reach anything that far over copper: in stages. A roughly 300-foot outdoor Ethernet run left the nearest building and terminated at a sealed Pelican case tucked into the brush at the edge of the lawn. Inside that case sat a PoE-powered switch acting as a midspan relay. From there, a second 300-foot run carried the link the rest of the way out to the tent.",
          },
          {
            type: "p",
            text: "Two hops, six hundred feet, one waterproof case in the bushes — and a fully live network node in the center of a field.",
          },
          {
            type: "img",
            src: "/assets/Fost_Pelican.JPG",
            width: 1600,
            height: 1200,
            alt: "Sealed Pelican case PoE relay deployed at the edge of the Great Lawn at Snug Harbor",
          },
        ],
      },
      {
        label: "The Architecture",
        heading: "Built to fail gracefully",
        blocks: [
          {
            type: "p",
            text: "A temporary outdoor network has a lot of ways to break. A buried run gets nicked, a media converter overheats in the sun, someone trips a cable behind a booth. The design assumed all of it.",
          },
          {
            type: "p",
            text: "The backbone was deliberately built with more than one path between key points. Spanning Tree Protocol ran across the switching fabric with the core pinned as the root bridge, so the topology had a single, predictable brain deciding which links were active and which were standing by. Where a building was reached by fiber, it was also reachable by a wireless point-to-point shot — directional antennas aimed building-to-building as a hot backup. The wired path was always preferred; if it dropped, the network reconverged onto the wireless link and traffic kept moving. The internet uplink had the same logic: fiber first, cellular LTE as the fallback.",
          },
          {
            type: "p",
            text: "That's the whole philosophy: hardwired first, wireless as the safety net, and a loop-aware fabric that's allowed to have multiple paths precisely so it can lose one and keep running. Redundancy that nobody in the audience would ever see working.",
          },
        ],
      },
      {
        label: "Under Load",
        heading: "The stress test",
        blocks: [
          {
            type: "p",
            text: "The moment every event network secretly dreads came in one of the auditoriums.",
          },
          {
            type: "p",
            text: "A speaker, mid-presentation, turned to a thousand people and said some version of \"okay, now everyone take out your phones and connect to the Wi-Fi.\" A thousand devices associating at once is, functionally, a friendly denial-of-service attack on your own wireless. One access point in the room hit its ceiling and seized. The others — same SSID, same controller, overlapping coverage — picked up the load and kept the room online. The talk continued. Nobody in the audience knew anything had happened.",
          },
          {
            type: "p",
            text: "That outcome wasn't luck. It came from over-provisioning the wireless with enterprise-grade access points, designing for overlapping coverage rather than minimum coverage, and running a local controller so the APs behaved as one coordinated system instead of a dozen islands fighting each other.",
          },
        ],
      },
      {
        label: "Equipment",
        heading: "The kit",
        blocks: [
          {
            type: "p",
            text: "Everything was enterprise-grade and everything lived in flight cases, built to be deployed fast, run hard for days, and strike clean:",
          },
          {
            type: "ul",
            items: [
              "Firewall — FortiGate at the perimeter, behind a dedicated fiber gigabit circuit with cellular LTE failover",
              "Switching — Cisco PoE switches at the core and in each building, running Spanning Tree with a pinned root bridge",
              "Wireless — Ruckus access points, indoor and outdoor, managed by a local Ruckus controller",
              "Backhaul — fiber between core buildings, with UniFi point-to-point antennas as wireless failover",
              "Field reach — a sealed Pelican-case PoE relay extending two 300-foot outdoor runs to the Great Lawn tent",
              "Power — protected, conditioned power throughout",
            ],
          },
        ],
      },
      {
        label: "Production",
        heading: "Production as one team",
        blocks: [
          {
            type: "p",
            text: "A festival network isn't really an IT project — it's a production department that happens to run on packets. The build only worked because it was planned alongside the people mounting the exhibits and the people running the show: which booth needed what, where the crowds would be, when the auditoriums would fill, who to raise on the radio when something needed to move.",
          },
          {
            type: "p",
            text: "It came down as cleanly as it went up. Across the run of the festival and thousands of devices, the network did the one thing a live event demands of it: it disappeared. No dead air.",
          },
        ],
      },
    ],
    results: [
      "Thousands of concurrent high-data devices supported across an 83-acre campus",
      "Live link failures absorbed invisibly by the self-healing redundant topology",
      "Wireless controller coordination prevented auditorium overload from reaching the audience",
      "600-foot outdoor Ethernet relay reached the Great Lawn tent with a single sealed Pelican case midspan",
      "Network struck cleanly after the festival — no permanent infrastructure, no trace",
      "The festival ran without a hitch",
    ],
  },

  {
    slug: "duality-group",
    title: "Built for Scale",
    subtitle:
      "Redesigning a cloud environment for a quantitative trading firm — from inherited complexity to infrastructure as code",
    client: "Duality Group",
    industry: "Financial Services",
    tags: ["Cloud & Infrastructure", "Networking", "Security"],
    summary:
      "A complete rebuild of an overbuilt, incentive-misaligned AWS environment for quantitative trading firm Duality Group — four isolated accounts, every subnet and routing rule defined in Terraform, active/passive FortiGate HA at the network edge, encrypted VPN connectivity to market data and execution partners, and a lower monthly bill than the environment it replaced.",
    seoTitle: "Built for Scale — Cloud Infrastructure for Duality Group | Macktez",
    seoDescription:
      "How Macktez replaced an overbuilt, incentive-misaligned AWS environment for quantitative trading firm Duality Group — rebuilding from first principles in Terraform across four accounts, with FortiGate HA, encrypted market data connectivity, and a lower monthly bill than the environment it replaced.",
    canonicalPath: "/case-studies/duality-group",
    featuredImage: {
      src: "/assets/dual.jpg",
      alt: "AWS cloud infrastructure architecture — Duality Group",
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Built for Scale — Cloud Infrastructure Redesign for Duality Group",
      "description":
        "A complete rebuild of an overbuilt, incentive-misaligned AWS environment for quantitative trading firm Duality Group — four isolated accounts, every resource defined in Terraform, active/passive FortiGate HA, encrypted market data connectivity, and a lower monthly bill than the environment it replaced.",
      "url": "https://macktez.com/case-studies/duality-group",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Organization",
        "name": "Duality Group",
        "description": "A quantitative trading firm with business-critical infrastructure reliability requirements",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "Duality Group — a quantitative trading firm operating in an environment where infrastructure reliability is a business-critical concern, not a quality-of-life one.",
                  ],
                  [
                    "Problem",
                    "An AWS environment inherited from a previous managed service provider whose contract was structured as a percentage of Duality's cloud spend — a billing model that made complexity profitable, and left behind an overbuilt, poorly rationalized, and expensive environment.",
                  ],
                  [
                    "Services",
                    "AWS architecture · multi-account design · Terraform infrastructure as code · network design · FortiGate virtual firewall deployment · encrypted VPN connectivity · security monitoring and observability",
                  ],
                  [
                    "Platforms",
                    "AWS (VPC, Transit Gateway, Fargate, Route 53, VPC Endpoints) · Terraform · Fortinet FortiGate (virtual) · Terraform Cloud",
                  ],
                  [
                    "Outcome",
                    "A fully code-defined, multi-account AWS environment that is reproducible from scratch, costs less than what it replaced, and is significantly more capable.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "An environment built for someone else’s incentives",
        blocks: [
          {
            type: "p",
            text: "Duality Group is a quantitative trading firm operating in a space where infrastructure reliability is not a quality-of-life concern — it is a business-critical one. Latency matters. Uptime matters. The integrity of data connections to market data providers and downstream execution partners matters in ways that are immediate and financial.",
          },
          {
            type: "p",
            text: "The environment they inherited had been built by another managed service provider — one whose contract was structured as a percentage of Duality's AWS spend. That billing model created a perverse incentive: complexity wasn't a problem to be solved, it was a revenue mechanism. The result was an overbuilt, poorly rationalized environment that was expensive, fragile, and difficult to reason about.",
          },
          {
            type: "p",
            text: "The goal wasn't to patch it. It was to replace it entirely — with an environment designed from first principles, built entirely in code, and capable of scaling with the demands of a growing trading operation.",
          },
        ],
      },
      {
        label: "Architecture",
        heading: "Multi-account, multi-environment from the ground up",
        blocks: [
          {
            type: "p",
            text: "The new environment is organized across discrete AWS accounts — infrastructure, development, UAT, and production — each isolated, consistently structured, and managed entirely in Terraform. Every subnet, routing rule, firewall policy, and access control is version-controlled and reproducible. Configuration drift is impossible by design.",
          },
          {
            type: "p",
            text: "A Transit Gateway in the infrastructure account serves as the backbone, with all inter-environment routing flowing through a single, centrally managed hub. Shared services — DNS resolution, firewall, identity — live in the infrastructure account and are consumed by all other environments through controlled, auditable paths.",
          },
        ],
      },
      {
        label: "Network Design",
        heading: "Subnets purpose-built for containerized workloads",
        blocks: [
          {
            type: "p",
            text: "Each environment is subdivided into distinct subnet tiers: public-facing, application, data, and a dedicated container tier for Fargate workloads. The container subnets are intentionally oversized — providing thousands of available addresses per availability zone — because Duality's trading workloads need room to scale horizontally without re-addressing infrastructure.",
          },
          {
            type: "p",
            text: "The data tier, where databases live, is kept fully isolated with its own routing and access controls. Nothing reaches it that isn't explicitly permitted. AWS service traffic is routed through private VPC endpoints, keeping it off the public internet entirely.",
          },
        ],
      },
      {
        label: "Firewall",
        heading: "Active/passive FortiGate high availability",
        blocks: [
          {
            type: "p",
            text: "All traffic into and out of AWS flows through a pair of Fortinet FortiGate virtual appliances deployed in the infrastructure account across separate availability zones. They run in active/passive high availability — if the active unit fails, the passive takes over automatically, with no manual intervention and no traffic disruption.",
          },
          {
            type: "p",
            text: "The FortiGates were procured with full security licensing and support contracts — a predictable, fixed annual cost, rather than a variable expense that grows with cloud spend.",
          },
        ],
      },
      {
        label: "External Connectivity",
        heading: "Encrypted tunnels to market data and execution partners",
        blocks: [
          {
            type: "p",
            text: "Duality's trading operation depends on reliable, low-latency connectivity to a major market data provider for trade data, and to downstream partners for trade execution. Both connections are handled as encrypted VPN tunnels terminated at the FortiGate layer — traffic that never traverses the public internet unprotected. The firewall manages all policy enforcement for these connections: what is permitted in, what is permitted out, and how it routes once inside.",
          },
          {
            type: "p",
            text: "Internal DNS resolution is centrally managed in the infrastructure account and shared across all environments, so every account resolves internal hostnames consistently without duplicating infrastructure.",
          },
        ],
      },
      {
        label: "Security & Observability",
        heading: "Defense in depth, managed as code",
        blocks: [
          {
            type: "p",
            text: "Threat detection, API audit logging, resource configuration history, and encryption key management are all enabled across every account from day one — centrally aggregated, consistently configured, and defined in code alongside everything else. Every VPC generates flow logs. Nothing in the environment is undocumented or unobserved.",
          },
        ],
      },
    ],
    results: [
      "A fully code-defined AWS environment across four accounts — reproducible, version-controlled, and auditable",
      "Container subnets built for horizontal scale, with capacity to grow without architectural changes",
      "Active/passive FortiGate high availability with automatic failover and no single point of failure at the network edge",
      "Encrypted connectivity to market data and execution partners, terminated at the firewall and never exposed to the internet",
      "All environments connected through a centralized routing hub, with shared DNS and security services flowing from a single infrastructure account",
      "Security monitoring, audit logging, and configuration tracking running across all environments from day one",
      "A lower monthly AWS bill than the environment it replaced — despite being significantly more capable",
    ],
  },

  {
    slug: "easterseals-nj-intune",
    title: "Automating Device Management with Intune",
    subtitle:
      "Building a zero-touch provisioning pipeline for a 45-location healthcare nonprofit — from fully manual to Autopilot at scale",
    client: "Easterseals New Jersey",
    industry: "Healthcare & Medical",
    tags: ["MDM", "Identity", "Security", "Microsoft"],
    summary:
      "A complete zero-touch provisioning pipeline for new devices and an automated re-enrollment path for existing ones — across 45 locations, 60+ Wi-Fi networks, and dozens of deployed applications — for a HIPAA-regulated nonprofit with 775 endpoints working toward HITRUST certification.",
    featuredImage: {
      src: "/assets/esnj.png",
      alt: "Easterseals New Jersey — device enrollment and IT provisioning",
    },
    seoTitle: "Automating Device Management with Intune — Easterseals NJ | Macktez",
    seoDescription:
      "How Macktez built a zero-touch Windows Autopilot and Intune provisioning pipeline for Easterseals New Jersey — a 45-location healthcare nonprofit with 775 endpoints — deploying security baselines, application packages, and Wi-Fi credentials across the full fleet through iterative development with the internal IT team.",
    canonicalPath: "/case-studies/easterseals-nj-intune",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Automating Device Management with Intune — Zero-Touch Provisioning for Easterseals New Jersey",
      "description":
        "How Macktez built a zero-touch Windows Autopilot and Intune provisioning pipeline for Easterseals New Jersey — a 45-location healthcare nonprofit with 775 endpoints in a HIPAA-regulated environment working toward HITRUST certification.",
      "url": "https://macktez.com/case-studies/easterseals-nj-intune",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "State", "name": "New Jersey" },
      },
      "about": {
        "@type": "Organization",
        "name": "Easterseals New Jersey",
        "description": "A large nonprofit providing services to persons with disabilities across 45 locations in New Jersey",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "NJ",
          "addressCountry": "US",
        },
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "Easterseals New Jersey — a large nonprofit providing services to persons with disabilities across 45 locations in New Jersey, with 775 endpoints in a HIPAA-regulated environment working toward HITRUST certification.",
                  ],
                  [
                    "Problem",
                    "A growing organization running entirely on on-premises Active Directory with no device management pipeline — where every workstation was provisioned by hand, every user was a local administrator, and the IT team's time was consumed by manual configuration work that should have been automatic.",
                  ],
                  [
                    "Services",
                    "Windows Autopilot and Intune design and deployment · security baseline development · PowerShell scripting and remediation · application deployment pipeline · vendor and reseller coordination (CDW) · Wi-Fi credential deployment · iterative development with internal IT team",
                  ],
                  [
                    "Platforms",
                    "Microsoft Intune · Windows Autopilot · Microsoft Entra ID · Microsoft Defender · LAPS · CDW",
                  ],
                  [
                    "Outcome",
                    "A complete zero-touch provisioning pipeline for new devices and an automated re-enrollment path for existing ones — across 45 locations, 60+ Wi-Fi networks, and dozens of deployed applications — with an IT team that spends significantly less time manually configuring workstations.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Organization",
        heading: "A statewide nonprofit with a small IT team and a large footprint",
        bg: "bg-[#F4F2EF]",
        blocks: [
          {
            type: "p",
            text: "Easterseals New Jersey provides a wide range of services to people with disabilities across the state — therapy, employment, early intervention, residential programs, and more. They operate out of 45 locations, support roughly 775 endpoints, and handle protected health information across multiple clinical systems. Their IT team is small relative to the footprint they cover, which makes every hour spent on manual provisioning an hour not spent on something more valuable.",
          },
          {
            type: "p",
            text: "Their environment at the start of the engagement was built on on-premises Active Directory, with no modern device management pipeline. Windows workstations were domain-joined by hand, configured by hand, and deployed by hand. There was no LAPS — every local admin account used the same credentials. Security baselines were applied inconsistently, if at all. Everyone was a local administrator on their own machine.",
          },
          {
            type: "p",
            text: "For a healthcare organization working toward HITRUST certification and handling HIPAA-regulated data, the gap between their current posture and where they needed to be was significant.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Scale, complexity, and a live environment",
        blocks: [
          {
            type: "p",
            text: "This was not a greenfield deployment. Easterseals NJ was operating a full clinical and administrative environment — line-of-business applications serving every department, remote access for dozens of locations, and multiple EHR systems. The work had to happen around the organization’s operations, not instead of them.",
          },
          {
            type: "p",
            text: "The scale added its own complexity. Forty-five locations meant 60-plus distinct Wi-Fi networks, each with their own SSID and credentials, all of which needed to be pushed to endpoints automatically. Application requirements spanned a wide range — from standard productivity tools to specialty clinical software used at the point of care. Each one was a custom package to build, test, deploy, and maintain.",
          },
          {
            type: "p",
            text: "And the device estate itself was split: new machines going forward could be enrolled automatically, but roughly 275 existing domain-joined PCs also needed a path into the new system without requiring someone to physically touch each one.",
          },
        ],
      },
      {
        label: "The Approach",
        heading: "Iterative, in partnership with the internal team",
        bg: "bg-[#F4F2EF]",
        blocks: [
          {
            type: "p",
            text: "The engagement was structured as a series of weekly working sessions with Easterseals NJ’s IT team — not a one-time deployment, but a collaborative development process. Policies were built, tested on a small group of devices, refined, and expanded incrementally. Each week added capability; each cycle caught issues before they reached the broader fleet.",
          },
          {
            type: "p",
            text: "That cadence mattered. A policy conflict in BitLocker configuration, a hostname rename script that ran faster than a remote support tool could sync its device name, a screensaver setting that needed a registry workaround — these are the kinds of issues that only surface in a real environment with real machines. Working iteratively meant they were resolved with a handful of test devices, not with the full fleet.",
          },
          {
            type: "p",
            text: "The internal IT team was a genuine partner throughout. They knew the environment, the applications, the edge cases. The work was built with them, not handed off to them.",
          },
        ],
      },
      {
        label: "The Build",
        heading: "What was built",
        blocks: [
          {
            type: "p",
            text: "The Autopilot pipeline runs in two directions. New Windows devices purchased through CDW are automatically registered in Autopilot at the time of purchase — zero-touch enrollment, out of the box. For existing machines, a PowerShell script ingests the hardware hash and registers the device in Autopilot, so it can be factory reset and re-enrolled through the same automated flow. The IT team can now wipe and redeploy a workstation without manually configuring anything.",
          },
          {
            type: "p",
            text: "A full security baseline was deployed across the fleet: LAPS for automated local admin password rotation, BitLocker encryption with recovery keys escrowed in Intune, strong MFA enforced via Microsoft Authenticator, Microsoft Defender replacing the legacy endpoint protection, URL blocking configured in Defender for restricted domains, and a full transition of local administrator rights away from end users.",
          },
          {
            type: "p",
            text: "An application pipeline delivers dozens of packages to every enrolled device — productivity software, clinical tools, remote support, browsers, and printer configurations — all deployed and maintained through Intune without manual installation.",
          },
          {
            type: "p",
            text: "Configuration policies complete the picture: Wi-Fi credentials pushed to all 60-plus SSID locations using dynamic group targeting, power and sleep settings standardized across the fleet, screensaver and lock screen enforced with Easterseals NJ branding, interactive logon message deployed with policy acknowledgment language, and every device renamed automatically to its serial number on enrollment.",
          },
          {
            type: "p",
            text: "A staged Windows 11 upgrade pipeline was coordinated alongside the Autopilot work — upgrade rings rolled out incrementally, with user communications and IT team training to minimize disruption.",
          },
        ],
      },
    ],
    results: [
      "Full Windows Autopilot enrollment pipeline — zero-touch for new devices, hash-ingestion re-enrollment for roughly 275 existing domain-joined PCs",
      "CDW vendor coordination for automatic Autopilot registration on all new hardware purchases",
      "Intune security baseline: LAPS, BitLocker with escrowed recovery keys, Microsoft Defender, strong MFA, local admin removal",
      "Dozens of application packages deployed and maintained via Intune without manual installation",
      "Wi-Fi credential deployment across 60+ locations using dynamic group targeting",
      "Windows 11 upgrade pipeline with staged rollout rings",
      "Iterative development in weekly partnership with the internal IT team",
    ],
  },

  {
    slug: "nature-preserve-wireless",
    title: "Campus-Wide Wireless for a Remote Nature Preserve",
    subtitle:
      "A point-to-multipoint wireless network for a multi-national non-profit's remote nature preserve — designed in New York, built in Florida, delivering 200 Mbps where major carriers declined to serve.",
    client: "A multi-national non-profit",
    industry: "Nonprofits",
    tags: ["Networking"],
    summary:
      "Campus-wide wireless connectivity for a multi-national non-profit's remote nature preserve — remote deployment, zero carriers willing to serve, 200 Mbps delivered.",
    featuredImage: {
      src: "/assets/preserve-antennas.jpg",
      alt: "Unifi Bridge Pro antennas mounted on a monopole at the preserve",
      position: "center 40%",
    },
    seoTitle: "Campus-Wide Wireless for a Remote Nature Preserve — Macktez",
    seoDescription:
      "Macktez designed and deployed a point-to-multipoint wireless campus network for a multi-national non-profit — a remote Florida nature preserve that major carriers wouldn't serve. Delivered remotely, on time, and at a fraction of the cost of a traditional enterprise build.",
    canonicalPath: "/case-studies/nature-preserve-wireless",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The Network Behind the Boardwalk",
      "description":
        "Macktez designed and deployed campus-wide wireless connectivity for a remote nature preserve in rural Florida, operated by a multi-national non-profit — using a point-to-multipoint architecture, SonicWall, Cisco, and Unifi to deliver 200 Mbps campus-wide where major carriers wouldn't go.",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "about": { "@type": "Organization", "name": "A multi-national non-profit" },
      "keywords":
        "nature preserve, wireless network, point-to-multipoint, SonicWall, Cisco, Unifi, remote network deployment, nonprofit technology, Florida",
    },
    sections: [
      {
        label: "Background",
        heading: "A sanctuary that couldn’t get online",
        blocks: [
          {
            type: "p",
            text: "The preserve is one of the most significant conservation sites in North America — a remote site in rural Florida operated by a multi-national non-profit, home to old-growth forest and a premier destination for visitors and researchers. The preserve depends on reliable connectivity for conservation work, education, visitor operations, and staff communications. For years, it had none.",
          },
          {
            type: "p",
            text: "Major carriers declined to serve the site. Earlier efforts to establish connectivity stalled repeatedly: permitting disputes with local authorities, vendor failures, and a project intermediary that went bankrupt mid-build — leaving an ISP that had invested in infrastructure unpaid and forced to withdraw. When a local provider eventually installed a 100-foot monopole on site, the sanctuary's buildings were still not wired. The physical assets were there. A functioning network was not.",
          },
          {
            type: "p",
            text: "Macktez was brought in to finish what others couldn't.",
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Years of stalled infrastructure, solved in months",
        blocks: [
          {
            type: "p",
            text: "The task was to turn fragmented, abandoned physical assets into a reliable, scalable campus network — distributed across multiple buildings separated by wetlands, dense tree cover, and roughly a kilometer of open preserve.",
          },
          {
            type: "p",
            text: "The technical constraints were significant. The 5 GHz radio spectrum in the area included DFS channels shared with FAA and weather radar systems, which can force radios to vacate channels without warning. One building was partially obstructed by tree canopy. The entire project had to be designed, configured, and managed remotely — from over 1,000 miles away in New York City — while a Florida-based vendor handled crane work, mounting, and physical cabling.",
          },
          {
            type: "p",
            text: "On hardware: Macktez selected Unifi for the RF and access point layer, not because it carries no risk — Unifi's software QA is inconsistent, firmware updates have introduced bugs, and rollback options are limited — but because the price-to-performance ratio was the right call for this application, with that risk explicitly understood and managed. Core routing and switching used SonicWall and Cisco, where reliability is non-negotiable.",
          },
          {
            type: "img",
            src: "/assets/preserve-site-map.jpg",
            width: 1600,
            height: 1170,
            alt: "Site map of the preserve showing campus buildings and wireless coverage",
          },
        ],
      },
      {
        label: "The Solution",
        heading: "The monopole becomes the hub",
        blocks: [
          {
            type: "p",
            text: "Macktez designed a point-to-multipoint wireless architecture using the existing monopole as the central distribution point for the entire campus.",
          },
          {
            type: "p",
            text: "Comcast fiber was brought to the site and terminated into a SonicWall firewall at the base of the tower. Behind the firewall, a 48-port Cisco PoE switch and UPS served as the distribution core. All of this equipment was installed inside a NEMA-rated outdoor rack with HVAC at the monopole base — weatherproofed and thermally protected for Florida's heat, humidity, and outdoor conditions.",
          },
          {
            type: "p",
            text: "On the monopole itself, four Unifi Bridge Pro 90° antennas were mounted high on the tower. Their overlapping coverage sectors created approximately 270° of usable RF coverage across the preserve — operating in the 5 GHz band at effective ranges well within the antennas' 5 km capacity, giving ample signal margin for every building.",
          },
          {
            type: "p",
            text: "Each building on the property received a matching Unifi point-to-point radio aimed back at the appropriate sector on the monopole. Inside each structure, that radio fed a PoE switch, which powered indoor Unifi access points for local Wi-Fi coverage. Additional PoE capacity was reserved at every site for future systems — cameras, environmental sensors, or additional coverage — without requiring any changes to the core network.",
          },
          {
            type: "img",
            src: "/assets/preserve-antennas.jpg",
            width: 1600,
            height: 2125,
            alt: "Unifi Bridge Pro antennas mounted on a monopole at the preserve",
          },
        ],
      },
      {
        label: "Remote Execution",
        heading: "Designed in New York, built in Florida",
        blocks: [
          {
            type: "p",
            text: "Macktez pre-configured all Unifi hardware before shipping equipment to the site. The Cloud Controller was used for ongoing management — not a physical Cloud Key, which would have introduced another point of failure in an already remote environment.",
          },
          {
            type: "p",
            text: "For the physical installation, Macktez provided the local Florida vendor with detailed documentation including precise compass bearings and mounting locations for each antenna — designed to be executed with a crane, by a crew that had never seen the network design. The antenna placement worked.",
          },
          {
            type: "p",
            text: "Channel and power optimization came next. Macktez tuned the 5 GHz spectrum carefully, navigating DFS channel constraints and local interference sources, and working through the adoption and reboot cycles typical of Unifi commissioning. The Visitor Center — initially delivering 5–10 Mbps due to signal overlap and interference — reached 200 Mbps after optimization.",
          },
          {
            type: "img",
            src: "/assets/preserve-ptp-radio.jpg",
            width: 1200,
            height: 904,
            alt: "Point-to-point radio installed at the Conservation Office",
          },
          {
            type: "img",
            src: "/assets/preserve-topology.png",
            width: 1600,
            height: 2044,
            alt: "Network topology — point-to-multipoint wireless architecture for the preserve",
          },
        ],
      },
    ],
    results: [
      "Point-to-multipoint wireless campus network serving all buildings across the preserve",
      "SonicWall firewall + 48-port Cisco PoE switch in NEMA outdoor rack with HVAC at monopole base",
      "Four Unifi Bridge Pro 90° sectors providing ~270° coverage across the 5 GHz band",
      "Per-building point-to-point backhaul + PoE switch + Unifi WAPs for local coverage",
      "200 Mbps real-world Wi-Fi throughput campus-wide (1 Gbps fiber delivered to monopole)",
      "Visitor Center improved from 5–10 Mbps to 200 Mbps after RF optimization",
      "Unifi Cloud Controller for centralized remote management",
      "Domotz for ongoing monitoring and alerting",
      "Future-ready: extra PoE capacity at every building for cameras and sensors; Starlink failover planned",
      "Full execution managed remotely from NYC with local Florida vendor for crane and cabling work",
    ],
  },
  {
    slug: "remote-datacenter-azure",
    title: "Replacing a 10-Year-Old Data Center — Without Replacing the Hardware",
    subtitle:
      "Multi-region Azure infrastructure, disaster recovery, and infrastructure-as-code for a nationally distributed conservation organization with a global growth roadmap.",
    client: "A multi-national non-profit",
    industry: "Nonprofits",
    tags: ["Cloud & Infrastructure"],
    summary:
      "Multi-region Azure infrastructure, disaster recovery, and Terraform automation for a multi-national non-profit — DR requirements met at a fraction of a hardware refresh.",
    featuredImage: {
      src: "/assets/azure-datacenter.jpg",
      alt: "Inside a Microsoft Azure datacenter",
    },
    seoTitle:
      "Replacing a 10-Year-Old Data Center Without Replacing the Hardware — Macktez",
    seoDescription:
      "Macktez designed and built a multi-region Azure infrastructure for a multi-national non-profit — disaster recovery, Virtual WAN, Azure Site Recovery, and Terraform automation — replacing an aging Cologix VMware stack without a hardware refresh.",
    canonicalPath: "/case-studies/remote-datacenter-azure",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Replacing a 10-Year-Old Data Center Without Replacing the Hardware",
      description:
        "Macktez designed and built a multi-region Azure infrastructure for a multi-national non-profit — including Azure Virtual WAN, Azure Site Recovery, Terraform infrastructure-as-code, and multi-region disaster recovery — replacing an aging Cologix VMware stack at a fraction of the cost of a hardware refresh.",
      author: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      about: {
        "@type": "Organization",
        name: "A multi-national non-profit",
      },
      keywords:
        "Azure, Terraform, disaster recovery, Azure Site Recovery, Virtual WAN, infrastructure-as-code, cloud migration, nonprofit technology",
    },
    sections: [
      {
        label: "Background",
        heading: "A colocation stack past its expiration date",
        blocks: [
          {
            type: "p",
            text: "The organization's core IT infrastructure had run for over a decade out of the Cologix colocation facility in New Jersey — VMware-based, aging, and overdue for replacement. The organization also had a substantial AWS footprint, a growing remote workforce, and plans for international expansion.",
          },
          {
            type: "p",
            text: "The existing backup solution covered data protection but not disaster recovery. In practical terms, that gap was showing up in insurance premiums. The organization needed a DR-capable infrastructure that could protect their most critical systems, support an eventual migration away from Cologix, and scale to serve a globally distributed organization — without the cost and ceiling of another hardware refresh.",
          },
        ],
      },
      {
        label: "The Decision",
        heading: "Cloud as infrastructure, not just storage",
        blocks: [
          {
            type: "p",
            text: "A direct hardware replacement at Cologix would have been expensive, extended the life of systems already scheduled for retirement, and still left the DR gap unresolved. Azure offered a different path: build the disaster recovery infrastructure first, establish the migration pathway second, and design the whole thing to scale globally from day one.",
          },
          {
            type: "p",
            text: "Macktez recommended a phased, iterative engagement — a fixed monthly scope with collaborative prioritization rather than a rigid fixed-price contract — given the number of architectural unknowns at the start of the project. Everything would be built and validated in an isolated development subscription before touching production.",
          },
        ],
      },
      {
        label: "The Approach",
        heading: "Infrastructure-as-code from day one",
        blocks: [
          {
            type: "p",
            text: "Rather than configuring Azure through the portal, Macktez built the entire infrastructure in Terraform — the industry-standard platform for infrastructure-as-code. That decision had compounding benefits throughout the engagement.",
          },
          {
            type: "p",
            text: "During development, Terraform let the team tear down the entire build between sessions, eliminating idle compute costs. When architectural decisions changed, a single script edit and a rebuild replaced what would otherwise have been hours of manual reconfiguration. And when it comes time to extend the infrastructure into a new region, the same modules that built the primary environment can be reused with minimal modification.",
          },
        ],
      },
      {
        label: "What Was Built",
        heading: "Phase by phase, over six months",
        blocks: [
          {
            type: "p",
            text: "The project ran across five phases:",
          },
          {
            type: "h3",
            text: "Network Connectivity",
          },
          {
            type: "p",
            text: "Azure Virtual WAN (vWAN) was deployed as the networking foundation. Rather than manually maintaining point-to-point peering rules between Azure regions, Cologix, and AWS, vWAN creates a central hub that handles any-any routing automatically. Traffic between Azure and Cologix, between Azure and AWS, and between Azure regions all routes through vWAN — over Microsoft's private fiber backbone, not the public internet. The architecture was designed to accommodate additional regional hubs without redesigning the core.",
          },
          {
            type: "h3",
            text: "Azure Site Recovery",
          },
          {
            type: "p",
            text: "ASR was configured for three scenarios: VMware-to-Azure failover (protecting the Cologix stack), Azure-to-VMware failback, and Azure-to-Azure replication across regions. This is the \"pilot light\" model — protected VMs replicate continuously, but Azure runs no active compute in the secondary region until a failover is triggered. The cost is storage only — approximately $25 per VM per month — compared to running a full parallel environment. For an organization with dozens of protected systems, the economics are compelling.",
          },
          {
            type: "h3",
            text: "Virtual Machines",
          },
          {
            type: "p",
            text: "A pair of domain controllers and a pair of Okta sync agents were deployed natively in Azure, establishing the identity foundation for cloud-hosted workloads. The Terraform modules built for this phase are reusable for every future VM deployment.",
          },
          {
            type: "h3",
            text: "Production",
          },
          {
            type: "p",
            text: "Protected VMs in Cologix began replicating to Azure, meeting the disaster recovery and insurance requirements that initiated the project. The lift-and-shift pathway — moving workloads fully into Azure — was established and ready for execution.",
          },
        ],
      },
    ],
    results: [
      "Multi-region Azure infrastructure built entirely in Terraform, from scratch",
      "Azure Virtual WAN connecting two Azure regions, Cologix VMware, and AWS in a single routing mesh",
      "Azure Site Recovery configured for VMware-to-Azure, Azure-to-VMware, and Azure-to-Azure disaster recovery",
      "DR and insurance requirements met at ~$25/VM/month",
      "Domain controllers and Okta sync agents deployed natively in Azure",
      "Infrastructure designed for future regional expansion without core redesign",
      "Reusable Terraform modules for all future VM, network, and DR deployments",
      "Phased delivery over six months, on time and on budget",
    ],
  },
  {
    slug: "hq-wireless-tuning",
    title: "Upgrade, Survey, Tune",
    subtitle:
      "Replacing eight aging access points at the organization's NYC headquarters — and using heat mapping to confirm the new ones were in the right places.",
    client: "A multi-national non-profit",
    industry: "Nonprofits",
    tags: ["Networking"],
    summary:
      "Replaced eight aging access points, surveyed the result, and tuned placement and channel configuration based on what the data showed.",
    featuredImage: {
      src: "/assets/07-hq-key-photo.jpg",
      alt: "NetSpot 5GHz heat map of the organization's NYC headquarters showing wireless signal coverage",
    },
    seoTitle:
      "Wireless Infrastructure Upgrade & Heat Mapping — Macktez",
    seoDescription:
      "We replaced eight aging access points at the organization's NYC headquarters, surveyed the result with a heat map, and tuned placement and channel configuration based on what the data showed.",
    canonicalPath: "/case-studies/hq-wireless-tuning",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Upgrade, Survey, Tune — Wireless Infrastructure for a Multi-National Non-Profit",
      description:
        "We replaced eight aging access points at the organization's NYC headquarters, surveyed the result with a heat map, and tuned placement and channel configuration based on what the data showed.",
      author: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Macktez",
        url: "https://macktez.com",
      },
      about: { "@type": "Organization", name: "A multi-national non-profit" },
      keywords:
        "wireless infrastructure, UniFi, heat mapping, NetSpot, Wi-Fi survey, 6GHz, nonprofit technology, channel planning, access point placement",
    },
    sections: [
      {
        label: "The Upgrade",
        heading: "The upgrade",
        blocks: [
          {
            type: "p",
            text: "The organization's New York headquarters runs on a UniFi wireless infrastructure managed by Macktez. The existing fleet of eight access points had reached end of useful life — newer hardware offered meaningfully better performance across 2.4, 5, and 6GHz, with improved multi-gigabit backhaul support and stronger performance in dense RF environments like midtown Manhattan.",
          },
          {
            type: "p",
            text: "We replaced all eight APs with new UniFi hardware, re-cabling where needed to ensure each access point retained its multi-gigabit wired uplink. Once the new hardware was online and enrolled in the controller, we walked the floor with a spectrum analyzer to see what we'd actually built.",
          },
          {
            type: "img",
            src: "/assets/01-hq-floor-plan.jpg",
            width: 1600,
            height: 1052,
            alt: "Annotated floor plan of the organization's NYC headquarters showing access point locations and patch panel port assignments",
            caption:
              "Annotated floor plan with access point locations and port assignments — the reference document for the survey and all subsequent placement decisions.",
          },
        ],
      },
      {
        label: "The Survey",
        heading: "The survey",
        blocks: [
          {
            type: "p",
            text: "We conducted a full wireless heat map of the floor using NetSpot, walking a calibrated zigzag pattern across the entire space with a 6GHz-capable laptop. The methodology matters here — zigzag movement with overlapping sample areas produces real measured data at every point rather than the interpolated guesswork that straight-line walking creates. We ran the survey during working hours to capture the RF environment as it actually exists, with neighboring networks active and devices connected.",
          },
          {
            type: "p",
            text: "The output was heat maps for each frequency band — raw signal strength (RSSI) and signal-to-noise ratio (SNR) — overlaid on a dimensionally accurate floor plan with each AP labeled and located.",
          },
        ],
      },
      {
        label: "What the Data Showed",
        heading: "What the data showed",
        blocks: [
          {
            type: "p",
            text: "5GHz coverage had a gap in the southwest corner of the floor. The new APs were providing good coverage across most of the space, but weaker signal in that area — a function of how the initial placement distributed coverage. All APs were already broadcasting at high power. The answer was better positioning, not more power.",
          },
          {
            type: "img",
            src: "/assets/02-hq-5ghz-coverage.png",
            width: 1600,
            height: 1030,
            alt: "NetSpot 5GHz heat map with adjusted gradient showing weaker coverage in the southwest corner of the floor",
            caption:
              "5GHz signal coverage with tightened gradient scale — the southwest corner reads cooler than the rest of the floor, identifying the area most in need of repositioning.",
          },
          {
            type: "p",
            text: "2.4GHz signal strength looked reasonable at first glance — adequate coverage across most of the floor. Raw signal alone is an incomplete picture.",
          },
          {
            type: "img",
            src: "/assets/03-hq-2ghz-signal.jpg",
            width: 1600,
            height: 1029,
            alt: "NetSpot 2.4GHz raw signal strength heat map showing broadly adequate coverage across the floor",
            caption:
              "2.4GHz raw signal strength — coverage looks acceptable across most of the floor.",
          },
          {
            type: "p",
            text: "When we examined signal-to-noise ratio — often the more meaningful metric for real-world performance under load — the picture was poor throughout. The scan found 70+ named Wi-Fi networks visible from this floor, and several hundred more too weak to identify. This is the reality of 2.4GHz in dense Manhattan: a shared, congested medium regardless of how well your own equipment is configured. The finding confirmed that steering staff toward 5GHz and 6GHz wherever possible is the right direction.",
          },
          {
            type: "img",
            src: "/assets/04-hq-2ghz-snr.jpg",
            width: 1600,
            height: 1029,
            alt: "NetSpot 2.4GHz signal-to-noise ratio heat map showing poor SNR across the entire floor despite adequate raw signal",
            caption:
              "2.4GHz signal-to-noise ratio — poor across the entire floor. Over 70 named networks and hundreds more unnamed ones are visible from this space. Raw signal means little when the noise floor is this high.",
          },
          {
            type: "p",
            text: "The background RF scan of 5GHz revealed a more specific problem: a neighboring tenant's access points on the floor above were broadcasting strong signals across channels we were using. At their loudest they registered at -61 dBm — extremely loud for a neighboring network — running 80MHz channel widths that consumed a meaningful slice of the available 5GHz spectrum. Our APs nearest that interference needed to be on channels as far from theirs as possible.",
          },
          {
            type: "img",
            src: "/assets/05-hq-5ghz-neighbor-interference.png",
            width: 1600,
            height: 996,
            alt: "UniFi AirView 5GHz spectrum scan showing neighboring access points with strong signal levels on 80MHz wide channels",
            caption:
              "UniFi AirView 5GHz scan — neighboring access points visible at -61 dBm, running 80MHz channels that consume a significant portion of the available 5GHz spectrum. Manual channel assignment was needed to steer our APs into quieter frequencies.",
          },
          {
            type: "p",
            text: "6GHz was clean. Near-zero external interference across the entire band. The staff SSID is 6GHz enabled, and staff on 6GHz-capable hardware are operating in clear spectrum with no competition from neighboring networks.",
          },
          {
            type: "img",
            src: "/assets/06-hq-6ghz-coverage.png",
            width: 1600,
            height: 1030,
            alt: "NetSpot 6GHz heat map showing strong, uniform coverage across the entire floor with near-zero external interference",
            caption:
              "6GHz signal coverage — strong and consistent across the entire floor, with near-zero interference from neighboring networks. The cleanest band on this floor by a significant margin.",
          },
        ],
      },
      {
        label: "The Adjustments",
        heading: "The adjustments",
        blocks: [
          {
            type: "p",
            text: "The survey data drove a focused set of changes. Four of the eight new APs were relocated to improve coverage distribution and address the southwest gap. Two additional APs were added at positions the heat map identified as coverage gaps that repositioning alone would not fully resolve — bringing the total to ten. The organization's IT team handled the physical moves and cabling, with Macktez on a call for coordination and documentation.",
          },
          {
            type: "p",
            text: "With the hardware in its final positions, Macktez configured manual channel assignments across all ten access points — hardcoding 2.4, 5, and 6GHz channels on each AP so that the units nearest the neighboring tenant's interference operate on non-overlapping frequencies. Leaving channel selection to the controller's automatic logic does not account for a specific neighbor's specific interference profile. Manual assignment does.",
          },
          {
            type: "p",
            text: "A follow-up review confirmed improved 5GHz coverage with the southwest gap addressed, and validated excellent 6GHz coverage throughout — the cleanest band on the floor and the most headroom for future growth as 6GHz-capable hardware becomes standard.",
          },
        ],
      },
      {
        label: "The Result",
        heading: "The result",
        blocks: [
          {
            type: "p",
            text: "Ten access points, properly placed, properly channeled, with documented topology and a validated heat map on file. When the next round of hardware questions comes up, or a neighboring tenant changes their configuration, there is a baseline to compare against.",
          },
          {
            type: "p",
            text: "The heat map survey is not an optional step in a wireless deployment — it is how you confirm that what you installed actually does what you intended. Moving hardware is significantly cheaper before cables are run and APs are ceiling-mounted than after.",
          },
        ],
      },
    ],
    results: [
      "Eight aging access points replaced with new UniFi hardware with multi-gigabit wired uplinks",
      "Full wireless heat map conducted across 2.4, 5, and 6GHz bands using calibrated zigzag methodology",
      "Southwest 5GHz coverage gap identified and resolved — four APs repositioned, two added, fleet brought to ten",
      "Manual channel assignments hardcoded on all ten APs to avoid neighboring tenant interference on 5GHz",
      "6GHz confirmed near-zero interference across the entire floor — cleanest band by a significant margin",
      "Documented floor plan with final AP locations and port assignments on file as a baseline for future changes",
    ],
  },
  {
    slug: "dmarc-email-deliverability",
    title: "DMARC and email deliverability",
    subtitle:
      "How Macktez used SPF, DKIM, and DMARC to deliver 3-5 million emails a month from multiple platforms without getting tagged as spam",
    client: "Fair Harbor Clothing",
    industry: "Retail & Hospitality",
    tags: ["Security"],
    summary:
      "How Macktez used SPF, DKIM, and DMARC to help Fair Harbor Clothing reliably deliver 3-5 million monthly emails while blocking spoofing of its domain.",
    featuredImage: {
      src: "/assets/placeholder/case-study-macktez.svg",
      alt: "",
    },
    seoTitle: "DMARC and Email Deliverability — Fair Harbor Clothing | Macktez",
    ogImage: "https://macktez.com/assets/placeholder/case-study-macktez-og.png",
    seoDescription:
      "How Macktez built SPF, DKIM, and DMARC policy for Fair Harbor Clothing — delivering 3-5 million emails a month to verified subscribers from multiple marketing and retail platforms, while blocking spoofing attempts against the domain.",
    canonicalPath: "/case-studies/dmarc-email-deliverability",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "DMARC and Email Deliverability — High-Volume Email Authentication for Fair Harbor Clothing",
      "description":
        "How Macktez established SPF and DKIM records across every sending service and incrementally tightened a DMARC policy for Fair Harbor Clothing, raising deliverability on 3-5 million monthly emails while flagging spoofing attempts from non-authorized servers.",
      "url": "https://macktez.com/case-studies/dmarc-email-deliverability",
      "author": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Macktez",
        "url": "https://macktez.com",
        "areaServed": { "@type": "City", "name": "New York City" },
      },
      "about": {
        "@type": "Organization",
        "name": "Fair Harbor Clothing",
        "description": "A retail clothing brand sending 3-5 million marketing and transactional emails a month across multiple platforms",
      },
    },
    sections: [
      {
        label: "At a Glance",
        blocks: [
          {
            type: "specs",
            groups: [
              {
                title: "",
                items: [
                  [
                    "Client",
                    "Fair Harbor Clothing — a retail brand sending 3-5 million emails a month to verified subscribers from several marketing and retail platforms.",
                  ],
                  [
                    "Problem",
                    "High-volume email sent on the company's behalf by multiple third-party services, with no unified authentication policy — leaving legitimate campaigns at risk of being tagged as spam and the domain open to spoofing.",
                  ],
                  [
                    "Services",
                    "Macktez Domain Management · SPF and DKIM record establishment across every sending service · DMARC policy authoring · deliverability report monitoring · incremental policy tightening in partnership with client staff",
                  ],
                  [
                    "Platforms",
                    "SPF · DKIM · DMARC · multiple third-party marketing and retail email services",
                  ],
                  [
                    "Outcome",
                    "Deliverability of authentic messages rose over the first two months and continued climbing through two more months of monitoring and adjustment, while spoofing attempts sent from non-authorized servers were consistently flagged.",
                  ],
                ],
              },
            ],
          },
        ],
      },
      {
        label: "The Challenge",
        heading: "Four-decade-old protocols and a very large send volume",
        bg: "bg-[#F4F2EF]",
        blocks: [
          {
            type: "p",
            text: "Fair Harbor Clothing recently asked us a hard question: can Macktez help them deliver 3-5 million emails a month to verified subscribers, from multiple platforms, without getting tagged as spam?",
          },
          {
            type: "p",
            text: "Why is that so hard? A lot has changed in technology over the past four decades, but the core protocols for sending and receiving email were solidified in the 1980s and have not. That leaves plenty of room for mess, misuse, and misunderstanding, both for people trying to separate signal from noise in their own inboxes and for organizations that rely on large email campaigns to reach their customers and drive business.",
          },
          {
            type: "p",
            text: "Google and Microsoft in particular have been attacking the spam problem aggressively, flagging email they recognize as spam or phishing and encouraging users to take cybersecurity seriously.",
          },
          {
            type: "p",
            text: "But what about organizations like Fair Harbor that legitimately need to send a high volume of email, want that email delivered, and want to make sure no one is sabotaging their online identity with spoofs and malicious content?",
          },
          {
            type: "p",
            text: "The answer involves a number of acronyms and backend services, plus continued human attention to manage and monitor configuration changes. The tools are useful for any organization concerned with its online reputation, and especially impactful for any with high-volume email requirements.",
          },
        ],
      },
      {
        label: "The Tools",
        heading: "SPF, DKIM, and DMARC working together",
        blocks: [
          { type: "p", text: "First, the acronyms:" },
          {
            type: "ul",
            items: [
              "SPF (Sender Policy Framework) is a protocol for letting the world know which outgoing mail servers are allowed to send email from your domain.",
              "DKIM (Domain Keys Identified Mail) is a protocol that proves an email claiming to come from your outgoing mail server really did.",
              "DMARC (Domain-based Message Authentication Reporting and Conformance) is a protocol that leverages SPF and DKIM to produce detailed reporting on all email associated with your domain name, and then lets you send specific instructions to recipients' mail servers about what to do with invalid email.",
            ],
          },
          {
            type: "p",
            text: "Configured properly, used together, and then monitored and adjusted over time, these tools can greatly reduce cybersecurity risks and increase deliverability for legitimate email. (We include these tools and services in Macktez Domain Management as a monthly subscription.)",
          },
          {
            type: "p",
            text: "When a company receives all its email through Google, for example, but sends email using Mailchimp, SPF, DKIM, and DMARC all need to work together to ensure the Mailchimp email isn't flagged as spam.",
          },
          {
            type: "p",
            text: "Fair Harbor uses several marketing and retail services to send 3-5 million emails per month on the company's behalf. The sheer volume requires careful policy to make sure the email reaches its intended audience, and each service used to send email needs to be incorporated into the DMARC policy and monitored.",
          },
        ],
      },
      {
        label: "The Approach",
        heading: "Collect first, then tighten incrementally",
        bg: "bg-[#F4F2EF]",
        blocks: [
          {
            type: "p",
            text: "We spearheaded the Domain Management project with our client. First, we confirmed or established correct SPF and DKIM records for every service the client uses to send email. Then we wrote a DMARC policy that at first only collects information on all email alleging to come from the client's domain.",
          },
          {
            type: "p",
            text: "Once we had enough data, we worked closely with several key members of the client's staff to make adjustments and additions to the policy, slowly raising the percentage of invalid emails that DMARC would instruct recipient mail servers to quarantine or reject outright. This part of the process needs to be done incrementally and with ongoing attention to make sure the policy is applied correctly. It's important not to rush it, or you run the risk of legitimate emails getting blocked.",
          },
        ],
      },
      {
        label: "The Results",
        heading: "Deliverability up, spoofing flagged",
        blocks: [
          {
            type: "p",
            text: "Within the first two months, DMARC reporting confirmed that the number and percentage of sent emails delivered properly had gone up. Two more months of monitoring and policy adjustments showed that percentage continuing to increase: authentic messages were delivered as intended, while illegitimate messages (spoofing attempts sent from non-authorized servers) were consistently flagged.",
          },
          {
            type: "p",
            text: "This let our client feel confident that its marketing and sales emails were reaching customers, and that any malicious attempts to subvert its domain reputation were being blocked.",
          },
          {
            type: "p",
            text: "The client's marketing, sales, and customer experience teams benefit from knowing their email is sent with proper authentication and received without issue, while their customers get the added security of knowing that spoofing attempts appearing to come from the company won't even make it to their inbox.",
          },
        ],
      },
    ],
    results: [
      "SPF and DKIM records confirmed or established for every service sending email on the company's behalf",
      "DMARC policy authored in collect-only mode first, then tightened incrementally toward quarantine and reject",
      "Deliverability of authentic messages up within the first two months, and still climbing after two more months of monitoring",
      "Spoofing attempts sent from non-authorized servers consistently flagged",
      "Ongoing monitoring and policy adjustment through the Macktez Domain Management subscription",
    ],
  },
];
